import { convertKgToViewerUnit, convertLength, convertMmToViewerUnit, convertUnitToDefault, convertWeightUnitToDefault, LengthUnit } from "Utils/MeasurementUtils";
import { useViewerStore, ViewerTabsMode } from "context/ViewerContext";
import { useDesignStore } from "context/DesignContext";
import { useSelectedDesignStore } from "context/SelectedDesignContext";
import { Engraving } from "models/Engraving";
import { AdditionNewStatus } from "models/Addition";
import { Hardness, LOCAL_STORAGE_KEYS, MeasurementSystem } from "components/Viewer3d/consts";
import { Insole, ShoeSize, ConditionSide, InsoleType, backendInsoleTypeMap, resolveProfileCurveName } from "models/Insole";
import { PatientConsditions } from "models/PatientConditions";
import { addNewAddition, fetchUserDesigns, getNewDesign, getPressureCalib, loadPrivateTemplate, markDesignLoadPendingClose, removeDesign, SavePatientOnServer } from "services/designService";
import { PaginatedSearchRequestDto } from "models/PaginatedSearchRequestDto";
import { Patient } from "models/Patient";
import { restoreDefaultPrinters, restorePrintHelpers } from "services/Printerservice";
import { scheduleAutosave } from "components/Viewer3d/SaveManager";
import { localDesignStack, undoLastAutosave } from "components/Viewer3d/UndoStack";
import { mirrorToLeft, mirrorToRight } from "components/Viewer3d/Mirror";
import {
  estimateSpeechMs,
  VOICE_PARAM_APPLY_MS,
  VOICE_PARAM_GAP_MS,
  VOICE_PARAM_HOLD_MS,
  VOICE_PARAM_STEP_END,
  waitMs,
} from "Voice/voiceSpeechSync";
import { buildParamNarration, buildShoeSizeNarration } from "Voice/voiceParamNarration";
import { voiceText, spokenNumber, currentVoiceLang } from "Voice/voiceLanguage";
import { buildPressureReport } from "Voice/voicePressureReport";
import { requestTutorial, useTutorialStore } from "Tutorial/tutorialStore";
import { isScanHardwareUnsupportedDevice } from "Utils/MediaQueryUtils";

export const VOICE_CAMERA_EVENT = "voice:camera";
export const VOICE_ZOOM_EVENT = "voice:zoom";
export const VOICE_PAN_EVENT = "voice:pan";
export const VOICE_GET_STATE_EVENT = "voice:getInsoleState";
export const VOICE_GET_PARAM_RANGE_EVENT = "voice:getParamRange";
export const VOICE_OPEN_PANEL_EVENT = "voice:openPanel";
export const VOICE_CLOSE_PANELS_EVENT = "voice:closePanels";
export const VOICE_CLEAR_PARAM_LABELS_EVENT = "voice:clearParamLabels";
export const VOICE_PARAM_FOCUS_EVENT = "voice:focusParam";
/** Re-frame the camera on whatever is currently visible. */
export const VOICE_FIT_CAMERA_EVENT = "voice:fitCamera";
export const VOICE_PARAM_PLAN_EVENT = "voice:planParamCamera";
/** Frames one addition, located from its label, and opens that label. */
export const VOICE_FOCUS_ADDITION_EVENT = "voice:focusAddition";
/** Circles the camera around whatever it is looking at. */
export const VOICE_ORBIT_EVENT = "voice:orbit";
/** Ends the running circle wherever it has got to. */
export const VOICE_ORBIT_STOP_EVENT = "voice:orbitStop";
/** Claims the camera for the narration, across a model rebuild. */
export const VOICE_CAMERA_HOLD_EVENT = "voice:cameraHold";

/** Closes the focused label and takes the camera back where it came from. */
export const VOICE_RELEASE_FOCUS_EVENT = "voice:releaseFocus";
/** Asks the live session to make the assistant speak one line as its own turn. */
export const VOICE_SPEAK_LINE_EVENT = "voice:speakLine";
/** Marks a message the agent must read back word for word and nothing else. */
export const VOICE_SPEAK_PREFIX = "SPEAK_EXACTLY:";
export const VOICE_OPEN_DATABASE_EVENT = "voice:openDatabase";
export const VOICE_DATABASE_QUERY_EVENT = "voice:databaseQuery";
export const VOICE_DATABASE_OPEN_EVENT = "voice:databaseOpen";
export const VOICE_DESIGN_DELETED_EVENT = "voice:designDeleted";
export const VOICE_OPEN_PATIENT_EVENT = "voice:openPatient";
export const VOICE_PATIENT_UPDATED_EVENT = "voice:patientUpdated";
export const VOICE_SAVE_PATIENT_EVENT = "voice:savePatient";
export const VOICE_AUTOMATIC_CONDITION_EVENT = "voice:automaticCondition";
export const VOICE_AUTOMATIC_CONDITION_DONE_EVENT = "voice:automaticConditionDone";
export const VOICE_OPEN_SCAN_EVENT = "voice:openScan";
export const VOICE_PRESSURE_SCAN_EVENT = "voice:pressureScan";
export const VOICE_PRESSURE_SCAN_READY_EVENT = "voice:pressureScanReady";
export const VOICE_PRESSURE_SCAN_DONE_EVENT = "voice:pressureScanDone";
export const VOICE_WBCT_SCAN_EVENT = "voice:wbctScan";
export const VOICE_WBCT_SCAN_DONE_EVENT = "voice:wbctScanDone";
export const VOICE_OPEN_TEMPLATE_EVENT = "voice:openLoadTemplate";
export const VOICE_OPEN_SAVE_MENU_EVENT = "voice:openSaveMenu";
export const VOICE_EXPORT_EVENT = "voice:export";
export const VOICE_OPEN_SAVE_TEMPLATE_EVENT = "voice:openSaveTemplate";
export const VOICE_GENERATE_GCODE_EVENT = "voice:generateGcode";
export const VOICE_GCODE_RESULT_EVENT = "voice:gcodeResult";
export const VOICE_OPEN_SETTINGS_EVENT = "voice:openSettings";
export const VOICE_GET_SETTINGS_EVENT = "voice:getSettingsState";
export const VOICE_SETTINGS_STATE_EVENT = "voice:settingsState";
export const VOICE_SET_SETTINGS_EVENT = "voice:setSettings";
export const VOICE_SAVE_SETTINGS_EVENT = "voice:saveSettings";
export const VOICE_SETTINGS_READY_EVENT = "voice:settingsReady";
export const VOICE_SETTINGS_RESULT_EVENT = "voice:settingsResult";
export const VOICE_PING_SETTINGS_EVENT = "voice:pingSettings";
export const VOICE_SETTINGS_STATUS_EVENT = "voice:settingsStatus";
export const VOICE_OPEN_TICKETS_EVENT = "voice:openTickets";
export const VOICE_TICKET_UPDATED_EVENT = "voice:ticketUpdated";
export const VOICE_SEND_TICKET_EVENT = "voice:sendTicket";
export const VOICE_SET_THEME_EVENT = "voice:setTheme";
export const VOICE_SET_CATEGORY_LABEL_EVENT = "voice:setCategoryLabel";

export const VOICE_PRINT_CONFIRM_EVENT = "voice:printConfirm";

function closeOpenDialogs(keep?: "database" | "loadTemplate" | "saveTemplate" | "patient" | "tickets" | "settings" | "print" | "scan" | "wbct") {
  document.dispatchEvent(new CustomEvent(VOICE_OPEN_PANEL_EVENT, { detail: { close: true } }));
  document.dispatchEvent(new Event("toolbox:close"));
  document.dispatchEvent(new CustomEvent(VOICE_CLOSE_PANELS_EVENT, { detail: { keep } }));
}

let suppressTemplateUntil = 0;

function blockTemplateAfterPatientSave(): string | null {
  if (Date.now() >= suppressTemplateUntil) return null;
  return "Patient was just saved and the patient panel closed. Do not open a template panel. Stop. Do not call any other tool.";
}

function openAfterClose(open: () => void) {
  closeOpenDialogs();
  window.setTimeout(open, 50);
}

const ENGRAVING_PANEL_ALIASES = new Set(["engraving", "engrave", "text", "حکاکی", "graveren", "graveer", "雕刻", "刻字"]);

export function exitEngravingIfNeeded() {
  if (useViewerStore.getState().mode !== ViewerTabsMode.Engraving) return;
  document.dispatchEvent(new CustomEvent(VOICE_OPEN_PANEL_EVENT, { detail: { close: true } }));
  document.dispatchEvent(new Event("toolbox:close"));
  useViewerStore.getState().setMode(ViewerTabsMode.View);
}

function staysInEngraving(toolName: string, args?: { panel?: string }) {
  if (toolName === "setEngravingParam" || toolName === "getEngravingState") return true;
  if (toolName === "openPanel") {
    const compact = String(args?.panel || "").trim().toLowerCase().replace(/[\s_-]/g, "");
    return ENGRAVING_PANEL_ALIASES.has(compact);
  }
  return false;
}

export function withEngravingExit<T extends Record<string, (args?: any) => any>>(tools: T): T {
  const wrapped = {} as T;
  for (const name of Object.keys(tools) as (keyof T)[]) {
    const fn = tools[name];
    wrapped[name] = ((args?: any) => {
      if (!staysInEngraving(String(name), args)) exitEngravingIfNeeded();
      return fn(args);
    }) as T[keyof T];
  }
  return wrapped;
}

export const VOICE_CAMERA_VIEWS = ["top", "bottom", "medial", "lateral", "heel", "toes", "reset"] as const;
export type VoiceCameraView = (typeof VOICE_CAMERA_VIEWS)[number];

const CAMERA_VIEW_ALIASES: Record<string, VoiceCameraView> = {
  top: "top",
  bottom: "bottom",
  medial: "medial",
  lateral: "lateral",
  heel: "heel",
  toes: "toes",
  reset: "reset",
  posterior: "heel",
  anterior: "toes",
  superior: "top",
  inferior: "bottom",
  پاشنه: "heel",
  پشت: "heel",
  پنجه: "toes",
  انگشت: "toes",
  نوک: "toes",
  جلو: "toes",
  بالا: "top",
  روی: "top",
  رو: "top",
  پایین: "bottom",
  زیر: "bottom",
  داخل: "medial",
  داخلی: "medial",
  مدیال: "medial",
  خارج: "lateral",
  خارجی: "lateral",
  لترال: "lateral",
  ریست: "reset",
  پشتپا: "heel",
  ازبالا: "top",
  ازپایین: "bottom",
  hiel: "heel",
  hak: "heel",
  achterkant: "heel",
  tenen: "toes",
  voorvoet: "toes",
  neus: "toes",
  boven: "top",
  bovenkant: "top",
  onder: "bottom",
  onderkant: "bottom",
  mediaal: "medial",
  binnen: "medial",
  binnenkant: "medial",
  lateraal: "lateral",
  buiten: "lateral",
  buitenkant: "lateral",
  terugzetten: "reset",
  beginstand: "reset",
  足跟: "heel",
  后跟: "heel",
  脚跟: "heel",
  足趾: "toes",
  脚趾: "toes",
  前足: "toes",
  俯视: "top",
  从上: "top",
  从上往下: "top",
  从下: "bottom",
  从下往上: "bottom",
  仰视: "bottom",
  内侧: "medial",
  外侧: "lateral",
  复位: "reset",
  重置: "reset",
  初始视角: "reset",
};

export type VoiceFoot = "left" | "right" | "both";

export const INSOLE_PARAM_FIELD_KEYS: Record<string, string> = {
  heelHeight: "Heel Height",
  heelDepth: "Heel Depth",
  thickness: "Thickness",
  archMedialHeight: "Arch Medial Height",
  archLateralHeight: "Arch Lateral Height",
  archCenterHeight: "Arch Center Height",
  heelLateralWedge: "Heel Lateral Wedge",
  heelMedialWedge: "Heel Medial Wedge",
  forefootLateralWedge: "Forefoot Lateral Wedge",
  forefootMedialWedge: "Forefoot Medial Wedge",
  length: "Length",
  width: "Width",
  heelFill: "Heel Fill",
  archFill: "Arch Fill",
  archSagitalPosition: "Arch Sagital Position",
  // Sandal only. The viewer hides them in the other models, and getInsoleState
  // leaves them out there, which is what tells the assistant to say so rather
  // than set a value nothing is listening to.
  outsoleThickness: "Outsole Thickness",
  bandHeight: "Band Height",
};

/**
 * Parameters the app computes and shows but nobody can set.
 *
 * Read out of getInsoleState like any other value; a request to change one is
 * answered with what it is instead of "unknown parameter", which is what the
 * assistant would otherwise say about a field it can plainly see in the state.
 */
export const INSOLE_READ_ONLY_PARAMS: Record<string, string> = {
  partialLength: "Partial Length",
  instepArea: "Instep Area",
};

/** Camera framing for voice param changes — look direction hints only; distance comes from sphere positions + screen aspect. */
export type VoiceParamCameraView = "heel" | "toes" | "top" | "medial" | "lateral";
export type VoiceParamCameraPreset = {
  /** Anatomical look direction when framing one foot's sphere. */
  single: VoiceParamCameraView;
  /** How much of the viewport the spheres should fill (0.4–0.85). Higher = closer. */
  fill: number;
};

export const VOICE_PARAM_CAMERA_PRESETS: Record<string, VoiceParamCameraPreset> = {
  "Heel Height": { single: "heel", fill: 0.62 },
  "Heel Depth": { single: "heel", fill: 0.62 },
  "Heel Fill": { single: "heel", fill: 0.6 },
  "Heel Lateral Wedge": { single: "lateral", fill: 0.58 },
  "Heel Medial Wedge": { single: "medial", fill: 0.58 },
  "Forefoot Lateral Wedge": { single: "lateral", fill: 0.58 },
  "Forefoot Medial Wedge": { single: "medial", fill: 0.58 },
  "Arch Medial Height": { single: "medial", fill: 0.58 },
  "Arch Lateral Height": { single: "lateral", fill: 0.58 },
  "Arch Center Height": { single: "medial", fill: 0.58 },
  "Arch Fill": { single: "medial", fill: 0.58 },
  "Arch Sagital Position": { single: "medial", fill: 0.58 },
  "Thickness": { single: "lateral", fill: 0.6 },
  "Length": { single: "top", fill: 0.7 },
  "Width": { single: "top", fill: 0.7 },
  // The outsole is the slab under the sandal, read from the side like Thickness.
  "Outsole Thickness": { single: "lateral", fill: 0.6 },
  // The band crosses the instep, so the medial view is the one that shows it.
  "Band Height": { single: "medial", fill: 0.58 },
};

const PARAM_ALIASES: Record<string, keyof typeof INSOLE_PARAM_FIELD_KEYS> = {
  heelheight: "heelHeight",
  heeldepth: "heelDepth",
  thickness: "thickness",
  archmedialheight: "archMedialHeight",
  archlateralheight: "archLateralHeight",
  archcenterheight: "archCenterHeight",
  heellateralwedge: "heelLateralWedge",
  heelmedialwedge: "heelMedialWedge",
  forefootlateralwedge: "forefootLateralWedge",
  forefootmedialwedge: "forefootMedialWedge",
  length: "length",
  width: "width",
  heelfill: "heelFill",
  archfill: "archFill",
  archsagitalposition: "archSagitalPosition",
  sagitalposition: "archSagitalPosition",
  sagittalposition: "archSagitalPosition",
  sagital: "archSagitalPosition",
  sagittal: "archSagitalPosition",
  archsagital: "archSagitalPosition",
  archsagittal: "archSagitalPosition",
  ارتفاعپاشنه: "heelHeight",
  پاشنه: "heelHeight",
  عمقپاشنه: "heelDepth",
  ضخامت: "thickness",
  قوسداخلی: "archMedialHeight",
  قوسمدیال: "archMedialHeight",
  قوس: "archMedialHeight",
  قوسخارجی: "archLateralHeight",
  قوسلترال: "archLateralHeight",
  قوسوسط: "archCenterHeight",
  قوسمرکزی: "archCenterHeight",
  گوهخارجیپاشنه: "heelLateralWedge",
  گوهداخلیپاشنه: "heelMedialWedge",
  گوهخارجیپنجه: "forefootLateralWedge",
  گوهداخلیپنجه: "forefootMedialWedge",
  طول: "length",
  عرض: "width",
  فیلپاشنه: "heelFill",
  پرشدگیپاشنه: "heelFill",
  فیلقوس: "archFill",
  پرشدگیقوس: "archFill",
  پرشدگيقوس: "archFill",
  موقعیتساجیتال: "archSagitalPosition",
  موقعیتساژیتال: "archSagitalPosition",
  ساجیتال: "archSagitalPosition",
  ساژیتال: "archSagitalPosition",
  موقعیتساجیتالقوس: "archSagitalPosition",
  گوهپاشنه: "heelMedialWedge",
  گوهپنجه: "forefootMedialWedge",
  hielhoogte: "heelHeight",
  hieldiepte: "heelDepth",
  dikte: "thickness",
  mediaalvoetboog: "archMedialHeight",
  voetboog: "archMedialHeight",
  lateralevoetboog: "archLateralHeight",
  middenvoetboog: "archCenterHeight",
  lateralehielwig: "heelLateralWedge",
  medialehielwig: "heelMedialWedge",
  lateralevoorvoetwig: "forefootLateralWedge",
  medialevoorvoetwig: "forefootMedialWedge",
  lengte: "length",
  breedte: "width",
  hielvulling: "heelFill",
  voetboogvulling: "archFill",
  sagitaal: "archSagitalPosition",
  sagittalepositie: "archSagitalPosition",
  sagitaalpositie: "archSagitalPosition",
  hielwig: "heelLateralWedge",
  voorvoetwig: "forefootLateralWedge",
  wig: "heelLateralWedge",
  足跟高度: "heelHeight",
  后跟高度: "heelHeight",
  脚跟高度: "heelHeight",
  足跟深度: "heelDepth",
  后跟深度: "heelDepth",
  厚度: "thickness",
  内侧足弓高度: "archMedialHeight",
  内侧足弓: "archMedialHeight",
  足弓: "archMedialHeight",
  外侧足弓高度: "archLateralHeight",
  外侧足弓: "archLateralHeight",
  足弓中央高度: "archCenterHeight",
  中央足弓: "archCenterHeight",
  足弓中央: "archCenterHeight",
  长度: "length",
  宽度: "width",
  足跟填充: "heelFill",
  后跟填充: "heelFill",
  足弓填充: "archFill",
  矢状位: "archSagitalPosition",
  矢状位置: "archSagitalPosition",
  足弓矢状位: "archSagitalPosition",
  足跟内侧楔: "heelMedialWedge",
  足跟外侧楔: "heelLateralWedge",
  前足内侧楔: "forefootMedialWedge",
  前足外侧楔: "forefootLateralWedge",
  内侧楔: "heelMedialWedge",
  外侧楔: "heelLateralWedge",
  // Sandal parameters.
  outsolethickness: "outsoleThickness",
  outsole: "outsoleThickness",
  solethickness: "outsoleThickness",
  bandheight: "bandHeight",
  band: "bandHeight",
  strapheight: "bandHeight",
  buitenzoolhoogte: "outsoleThickness",
  buitenzooldikte: "outsoleThickness",
  buitenzool: "outsoleThickness",
  bandhoogte: "bandHeight",
  bandjeshoogte: "bandHeight",
  ضخامتزیره: "outsoleThickness",
  زیره: "outsoleThickness",
  ضخامتکفزیرین: "outsoleThickness",
  ارتفاعبند: "bandHeight",
  بند: "bandHeight",
  ارتفاعتسمه: "bandHeight",
  外底厚度: "outsoleThickness",
  外底: "outsoleThickness",
  鞋底厚度: "outsoleThickness",
  鞋带高度: "bandHeight",
  绑带高度: "bandHeight",
  鞋带: "bandHeight",
};

const FOOT_ALIASES: Record<string, VoiceFoot> = {
  left: "left",
  right: "right",
  both: "both",
  چپ: "left",
  راست: "right",
  هردو: "both",
  "هر دو": "both",
  links: "left",
  rechts: "right",
  beide: "both",
  allebei: "both",
  左: "left",
  左脚: "left",
  左侧: "left",
  右: "right",
  右脚: "right",
  右侧: "right",
  双侧: "both",
  双脚: "both",
  两只: "both",
  两边: "both",
  两脚: "both",
};

/**
 * Centre and frame the camera on whatever is visible right now.
 *
 * Needed when visibility changes rather than the angle: showing one foot
 * instead of two, or hiding the shell to reveal the bones, leaves the subject
 * off to one side because the framing still belongs to what was there before.
 * None of the named views fix that — they change direction, not framing.
 */
/**
 * Re-frame the camera on the meshes currently on screen.
 *
 * With a duration it is an animated move, which is what a narration needs: the
 * instant fit is correct after a visibility toggle the visitor made themselves,
 * but mid-explanation it reads as the page jumping.
 */
export function fitCameraToVisible(
  options: { durationMs?: number; fill?: number; view?: string } = {}
): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(finish, (options.durationMs ?? 0) + 5000);
    document.dispatchEvent(
      new CustomEvent(VOICE_FIT_CAMERA_EVENT, { detail: { ...options, done: finish } })
    );
  });
}

/**
 * Zoom onto an addition and open its label.
 *
 * The viewer finds the pad's 3D position from its own label rather than being
 * pointed at a named view, so the pad is actually in shot whatever part of the
 * insole it sits on. Resolves once the camera has been put back, so a narration
 * step can hold its place until the move is finished.
 */
/**
 * Swing the camera once around the subject.
 *
 * Used where a still view is ambiguous — bones sitting above an insole read as
 * a flat picture until the view moves. Resolves when the circle is complete, so
 * a narration step can hold its place for the whole turn.
 */
export function orbitVoiceCamera(options: {
  degrees?: number;
  durationMs?: number;
  /** How much of the frame the subject fills; lower sits further back. */
  fill?: number;
  /** How long the move onto the subject takes before the circle begins. */
  enterMs?: number;
  /** False turns the camera from exactly where it is, with no entry move. */
  reframe?: boolean;
  /**
   * Degrees to drop the camera towards the horizon over the whole turn.
   * Spread across the circle so the change in angle is not seen happening.
   */
  tiltDegrees?: number;
  /** Run as the move begins, for changes that belong to this shot. */
  onStart?: () => void;
  /** Grace for onStart to reach the scene before what is visible is measured. */
  settleMs?: number;
} = {}): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve();
    };
    // Nothing answers when there is no viewer mounted; the narration must not
    // stall waiting for a camera that does not exist.
    const timer = window.setTimeout(finish, (options.durationMs ?? 6000) + 5000);
    document.dispatchEvent(
      new CustomEvent(VOICE_ORBIT_EVENT, { detail: { ...options, done: finish } })
    );
  });
}

export function focusVoiceAddition(options: {
  foot?: number;
  index?: number;
  fill?: number;
  viewHint?: string;
  holdMs?: number;
  /** Open the pad's own controls once the camera arrives. Default true. */
  openLabel?: boolean;
  /** Stay on the pad afterwards; the camera goes home on releaseVoiceFocus. */
  keepCamera?: boolean;
  /** Run once the camera has arrived and the label is open. */
  onArrived?: () => void;
} = {}): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve();
    };
    // The viewer may have no such addition, or no scene at all, in which case
    // nothing answers; the narration must not stall on that.
    const timer = window.setTimeout(finish, (options.holdMs ?? 3500) + 12000);
    document.dispatchEvent(
      new CustomEvent(VOICE_FOCUS_ADDITION_EVENT, { detail: { ...options, done: finish } })
    );
  });
}

/**
 * Give up a held close-up: collapse the label and return the camera.
 *
 * Paired with focusVoiceAddition's keepCamera, so a narration can stay on one
 * pad across several sentences and only leave when the subject changes.
 */
export function releaseVoiceFocus(
  options: { keepCamera?: boolean } = {}
): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(finish, 6000);
    document.dispatchEvent(
      new CustomEvent(VOICE_RELEASE_FOCUS_EVENT, { detail: { ...options, done: finish } })
    );
  });
}

/** Stop the circle now; the caller has something else for the camera to do. */
export function stopVoiceOrbit(): void {
  document.dispatchEvent(new CustomEvent(VOICE_ORBIT_STOP_EVENT));
}

/**
 * Hold the camera for the narration, or hand it back.
 *
 * The viewer re-frames itself when the model is rebuilt, with no animation, so
 * a model change mid-explanation threw the picture to a new angle. Holding keeps
 * whatever angle the narration has set on both sides of the switch.
 */
export function holdVoiceCamera(hold: boolean): void {
  document.dispatchEvent(new CustomEvent(VOICE_CAMERA_HOLD_EVENT, { detail: { hold } }));
}

export function dispatchCameraView(view: string): string {
  const normalized = view.trim().toLowerCase();
  const mapped = CAMERA_VIEW_ALIASES[normalized] ?? CAMERA_VIEW_ALIASES[view.trim()];
  if (!mapped) {
    return voiceText({
      en: `Unknown camera view "${view}". Use one of: ${VOICE_CAMERA_VIEWS.join(", ")}.`,
      nl: `Onbekend camerabeeld "${view}". Gebruik: ${VOICE_CAMERA_VIEWS.join(", ")}.`,
      fa: `نمای دوربین «${view}» ناشناخته است. از این‌ها استفاده کن: ${VOICE_CAMERA_VIEWS.join(", ")}.`,
      zh: `未知摄像头视角「${view}」。请使用：${VOICE_CAMERA_VIEWS.join(", ")}。`,
    });
  }
  document.dispatchEvent(new CustomEvent(VOICE_CAMERA_EVENT, { detail: { view: mapped } }));
  const named = {
    top: voiceText({ en: "top", nl: "boven", fa: "از بالا", zh: "俯视"}),
    bottom: voiceText({ en: "bottom", nl: "onder", fa: "از پایین", zh: "从下看"}),
    medial: voiceText({ en: "medial", nl: "mediaal", fa: "مدیال", zh: "内侧"}),
    lateral: voiceText({ en: "lateral", nl: "lateraal", fa: "لترال", zh: "外侧"}),
    heel: voiceText({ en: "heel", nl: "hiel", fa: "پاشنه", zh: "足跟"}),
    toes: voiceText({ en: "toes", nl: "tenen", fa: "پنجه", zh: "足趾"}),
    reset: voiceText({ en: "the initial view", nl: "de beginstand", fa: "نمای پیش‌فرض", zh: "初始视角"}),
  }[mapped];
  return voiceText({
    en: `Showing the ${named}.`,
    nl: `Ik toon ${named === "de beginstand" ? named : `de ${named}`}.`,
    fa: `${mapped === "reset" ? "نمای پیش‌فرض را نشان می‌دهم." : `نمای ${named} را نشان می‌دهم.`}`,
    zh: mapped === "reset" ? "正在显示初始视角。" : `正在显示${named}。`,
  });
}

const ZOOM_ACTIONS = ["in", "out", "reset"] as const;
const ZOOM_ALIASES: Record<string, (typeof ZOOM_ACTIONS)[number]> = {
  in: "in",
  out: "out",
  reset: "reset",
  closer: "in",
  nearer: "in",
  farther: "out",
  away: "out",
  نزدیک: "in",
  نزدیکتر: "in",
  بزرگ: "in",
  زوم: "in",
  دور: "out",
  دورتر: "out",
  کوچک: "out",
  ریست: "reset",
  dichterbij: "in",
  inzoomen: "in",
  zoominn: "in",
  verder: "out",
  verderweg: "out",
  uitzoomen: "out",
  zoomuit: "out",
  terugzetten: "reset",
  放大: "in",
  拉近: "in",
  缩小: "out",
  拉远: "out",
  复位: "reset",
  重置: "reset",
};

export function dispatchCameraZoom(action: string, amount?: number | string): string {
  const mapped = ZOOM_ALIASES[action.trim().toLowerCase()] ?? ZOOM_ALIASES[action.trim()];
  if (!mapped) {
    return voiceText({
      en: `Unknown zoom action "${action}". Use in, out, or reset.`,
      nl: `Onbekende zoomactie "${action}". Gebruik in, out of reset.`,
      fa: `عمل زوم «${action}» ناشناخته است. از in، out یا reset استفاده کن.`,
      zh: `未知缩放操作「${action}」。请使用 in、out 或 reset。`,
    });
  }
  const numeric = amount === undefined || amount === "" ? undefined : Number(amount);
  document.dispatchEvent(new CustomEvent(VOICE_ZOOM_EVENT, { detail: { action: mapped, amount: numeric } }));
  if (mapped === "reset") {
    return voiceText({
      en: "Zoom is back to the initial view.",
      nl: "De zoom is terug naar de beginstand.",
      fa: "زوم به نمای اول برگشت.",
      zh: "缩放已回到初始视角。",
    });
  }
  const by = Number.isFinite(numeric)
    ? voiceText({
        en: ` by ${spokenNumber(numeric as number, 0)} percent`,
        nl: ` met ${spokenNumber(numeric as number, 0)} procent`,
        fa: ` به اندازه ${spokenNumber(numeric as number, 0)} درصد`,
        zh: `百分之${spokenNumber(numeric as number, 0)}`,
      })
    : "";
  if (mapped === "in") {
    return voiceText({ en: `Zoomed in${by}.`, nl: `Ingezoomd${by}.`, fa: `زوم نزدیک شد${by}.`, zh: `已放大${by}。`});
  }
  return voiceText({ en: `Zoomed out${by}.`, nl: `Uitgezoomd${by}.`, fa: `زوم دور شد${by}.`, zh: `已缩小${by}。`});
}

const PAN_ACTIONS = ["left", "right", "up", "down", "reset"] as const;
const PAN_ALIASES: Record<string, (typeof PAN_ACTIONS)[number]> = {
  left: "left",
  right: "right",
  up: "up",
  down: "down",
  reset: "reset",
  panleft: "left",
  panright: "right",
  panup: "up",
  pandown: "down",
  shiftleft: "left",
  shiftright: "right",
  shiftup: "up",
  shiftdown: "down",
  moveleft: "left",
  moveright: "right",
  moveup: "up",
  movedown: "down",
  چپ: "left",
  راست: "right",
  بالا: "up",
  پایین: "down",
  ریست: "reset",
  پنچپ: "left",
  پنراست: "right",
  ببرچپ: "left",
  ببرراست: "right",
  ببربالا: "up",
  ببرپایین: "down",
  links: "left",
  rechts: "right",
  omhoog: "up",
  omlaag: "down",
  terugzetten: "reset",
  verschuiflinks: "left",
  verschuifrechts: "right",
  verschuifomhoog: "up",
  verschuifomlaag: "down",
  panlinks: "left",
  panrechts: "right",
  左移: "left",
  右移: "right",
  上移: "up",
  下移: "down",
  向左: "left",
  向右: "right",
  向上: "up",
  向下: "down",
  复位: "reset",
  重置: "reset",
};

export function dispatchCameraPan(action: string, amount?: number | string): string {
  const mapped = PAN_ALIASES[action.trim().toLowerCase().replace(/[\s_-]/g, "")]
    ?? PAN_ALIASES[action.trim().toLowerCase()]
    ?? PAN_ALIASES[action.trim()];
  if (!mapped) {
    return voiceText({
      en: `Unknown pan action "${action}". Use left, right, up, down, or reset.`,
      nl: `Onbekende panactie "${action}". Gebruik left, right, up, down of reset.`,
      fa: `عمل پن «${action}» ناشناخته است. از left، right، up، down یا reset استفاده کن.`,
      zh: `未知平移操作「${action}」。请使用 left、right、up、down 或 reset。`,
    });
  }
  const numeric = amount === undefined || amount === "" ? undefined : Number(amount);
  document.dispatchEvent(new CustomEvent(VOICE_PAN_EVENT, { detail: { action: mapped, amount: numeric } }));
  if (mapped === "reset") {
    return voiceText({
      en: "Pan is back to the initial position.",
      nl: "De pan is terug naar de beginpositie.",
      fa: "پن به جای اول برگشت.",
      zh: "平移已回到初始位置。",
    });
  }
  const dir = {
    left: voiceText({ en: "left", nl: "links", fa: "چپ", zh: "左"}),
    right: voiceText({ en: "right", nl: "rechts", fa: "راست", zh: "右"}),
    up: voiceText({ en: "up", nl: "omhoog", fa: "بالا", zh: "上"}),
    down: voiceText({ en: "down", nl: "omlaag", fa: "پایین", zh: "下"}),
  }[mapped];
  const by = Number.isFinite(numeric)
    ? voiceText({
        en: ` by ${spokenNumber(numeric as number, 0)} percent`,
        nl: ` met ${spokenNumber(numeric as number, 0)} procent`,
        fa: ` به اندازه ${spokenNumber(numeric as number, 0)} درصد`,
        zh: `百分之${spokenNumber(numeric as number, 0)}`,
      })
    : "";
  return voiceText({
    en: `Panned ${dir}${by}.`,
    nl: `Verschoven naar ${dir}${by}.`,
    fa: `پن ${dir} شد${by}.`,
    zh: `已向${dir}平移${by}。`,
  });
}

type ParamUpdate = { fieldKey: string; foot: number; value: number; single: boolean };

type ParamCameraPlan = { steps: { focusMs: number }[]; returnMs: number };

/** Camera framing for a step, resolved from the parameter's sphere preset. */
function cameraForField(fieldKey: string) {
  return VOICE_PARAM_CAMERA_PRESETS[fieldKey] ?? { single: "heel", fill: 0.6 };
}

/**
 * Ask the viewer for the exact camera durations of a run of steps, without
 * moving. Each step is measured from where the previous one lands, and the same
 * numbers are handed back as animation overrides, so plan and reality match.
 */
function planParamCamera(steps: { fieldKey: string; foot: number }[]): Promise<ParamCameraPlan> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (plan: ParamCameraPlan | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve(plan ?? { steps: steps.map(() => ({ focusMs: 1700 })), returnMs: 1700 });
    };
    const timer = window.setTimeout(() => finish(null), 900);
    document.dispatchEvent(
      new CustomEvent(VOICE_PARAM_PLAN_EVENT, {
        detail: {
          steps: steps.map((step) => ({ ...step, camera: cameraForField(step.fieldKey) })),
          resolve: finish,
        },
      })
    );
  });
}

/** How long an announced follow-up step is still expected to arrive. */
const PARAM_SEQUENCE_WINDOW_MS = 35000;
/** Set only when the previous step announced a successor, so a fresh request never waits. */
let followUpDueUntil = 0;

/** When the last camera step finished, which is when the assistant went quiet. */
let lastStepEndedAt = 0;
document.addEventListener(VOICE_PARAM_STEP_END, () => {
  lastStepEndedAt = Date.now();
});

function isTruthyFlag(value: unknown): boolean {
  return value === true || value === "on" || value === "true" || value === "yes" || value === 1;
}

type ParamStepSpec = {
  fieldKey: string;
  foot: number;
  updates: ParamUpdate[];
  /** Build the line once this step's camera window is known. */
  narrate: (windowMs: number, isLast: boolean) => string;
};

/** Show both insoles before a param tour so camera and narration are not framed on one foot. */
async function ensureBothFeetVisibleForParamChange(): Promise<void> {
  const store = useViewerStore.getState();
  if (store.feetView === "both") return;
  store.setFeetView("both");
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
  await waitMs(80);
}

/**
 * Plan and queue a run of camera steps, one per parameter per foot. Returns
 * every step's sentence as one line, so the whole run is a single agent turn:
 * asking for one sentence per turn cost a full round trip of dead air, about
 * four seconds, before each parameter after the first. The camera now walks the
 * steps against that one continuous utterance, one step per sentence.
 */
async function runParamSteps(options: {
  steps: ParamStepSpec[];
  silent: boolean;
  /** Another tool call follows, so the camera stays out instead of going home. */
  expectFollowUp: boolean;
}): Promise<string> {
  const { steps, silent, expectFollowUp } = options;
  await ensureBothFeetVisibleForParamChange();
  const plan = await planParamCamera(steps.map(({ fieldKey, foot }) => ({ fieldKey, foot })));

  // A narrated follow-up call holds its response for the gap, which is what
  // keeps the assistant quiet. A silent step has no response to hold, so the
  // viewer owns the gap instead.
  const isFollowUp = Date.now() < followUpDueUntil;
  const holdResponse = isFollowUp && !silent;
  if (holdResponse) {
    // The round trip that carried this call was already silence. Top the gap up
    // to VOICE_PARAM_GAP_MS instead of adding a whole gap on top of a slow network.
    await waitMs(VOICE_PARAM_GAP_MS - (Date.now() - lastStepEndedAt));
  }

  const lines: string[] = [];
  steps.forEach((step, index) => {
    const isLastStep = index === steps.length - 1;
    const goesHome = isLastStep && !expectFollowUp;
    const windowMs =
      plan.steps[index].focusMs +
      VOICE_PARAM_APPLY_MS +
      VOICE_PARAM_HOLD_MS +
      (goesHome ? plan.returnMs : 0);
    const line = silent ? "" : step.narrate(windowMs, goesHome);
    if (line) lines.push(line);

    document.dispatchEvent(
      new CustomEvent(VOICE_PARAM_FOCUS_EVENT, {
        detail: {
          fieldKey: step.fieldKey,
          feet: [step.foot],
          updates: step.updates,
          line,
          silent,
          expectFollowUp: isLastStep ? expectFollowUp : true,
          focusMs: plan.steps[index].focusMs,
          returnMs: plan.returnMs,
          // The window sized the line; the line now sizes the step back, so the
          // camera leaves this parameter exactly when the sentence about it ends.
          speechMs: estimateSpeechMs(line),
          // Only the first step waits for the turn to become audible. The rest
          // are already inside it, so they follow their predecessor directly.
          startsTurn: index === 0,
          camera: cameraForField(step.fieldKey),
        },
      })
    );
  });

  followUpDueUntil = expectFollowUp ? Date.now() + PARAM_SEQUENCE_WINDOW_MS : 0;
  return lines.join(" ");
}

/**
 * Extra changes ride along in one string, e.g. "left archMedialHeight=8; both thickness=4".
 * The foot prefix is optional and falls back to the call's own foot.
 */
function parseExtraChanges(raw: string): { foot: string; param: string; value: number }[] {
  const pattern = /(?:(left|right|both)\s+)?([a-zA-Z]+)\s*[=:]\s*(-?\d+(?:\.\d+)?)/gi;
  const found: { foot: string; param: string; value: number }[] = [];
  let match = pattern.exec(raw);
  while (match) {
    found.push({ foot: match[1] ?? "", param: match[2], value: Number(match[3]) });
    match = pattern.exec(raw);
  }
  return found;
}

/** One requested change, already validated and converted to viewer units. */
type ResolvedChange = {
  fieldKey: string;
  sides: ("right" | "left")[];
  viewerValue: number;
  spoken: string;
};

async function resolveChange(
  param: string,
  value: number,
  foot: string
): Promise<ResolvedChange | string> {
  const paramKey = resolveParam(param);
  const fieldKey = paramKey ? INSOLE_PARAM_FIELD_KEYS[paramKey] : undefined;
  // Answered before the unknown-param message below, which would otherwise be
  // said about a field the assistant can read in getInsoleState — and it would
  // then list the settable names as if the user had misremembered one.
  const readOnly = resolveReadOnlyParam(param);
  if (!fieldKey && readOnly) {
    return voiceText({
      en: `${readOnly} is measured by the app and cannot be changed. Read the current value and tell the user that number.`,
      nl: `${readOnly} wordt door de app gemeten en kan niet worden gewijzigd. Lees de huidige waarde en noem dat getal.`,
      fa: `${readOnly} را برنامه محاسبه می‌کند و قابل تغییر نیست. مقدار فعلی را بخوان و همان عدد را بگو.`,
      zh: `${readOnly} 由应用测量得出，无法更改。读取当前数值并告知用户。`,
    });
  }
  if (!fieldKey || !paramKey) {
    const names = Object.keys(INSOLE_PARAM_FIELD_KEYS).join(", ");
    return voiceText({
      en: `Unknown param "${param}". Use one of: ${names}.`,
      nl: `Onbekende parameter "${param}". Gebruik een van: ${names}.`,
      fa: `پارامتر «${param}» ناشناخته است. از این‌ها استفاده کن: ${names}.`,
      zh: `未知参数「${param}」。请使用：${names}。`,
    });
  }
  if (!Number.isFinite(value)) {
    return voiceText({
      en: `Value must be a number. Received: ${value}`,
      nl: `Waarde moet een getal zijn. Ontvangen: ${value}`,
      fa: `مقدار باید عدد باشد. دریافت شد: ${value}`,
      zh: `数值必须是数字。收到：${value}`,
    });
  }

  const viewerValue = isNonLengthParam(fieldKey) ? value : toViewerLength(value);
  const range = await requestParamRange(fieldKey);
  if (range && (viewerValue < range.min - 1e-6 || viewerValue > range.max + 1e-6)) {
    const asked = formatParamValue(fieldKey, viewerValue);
    const min = formatParamValue(fieldKey, range.min);
    const max = formatParamValue(fieldKey, range.max);
    return voiceText({
      en: `Value ${asked} is out of range for ${fieldKey}. Allowed range is ${min} to ${max}. Do not apply this change. Ask the user for a value inside that range.`,
      nl: `Waarde ${asked} valt buiten het bereik voor ${fieldKey}. Toegestaan is ${min} tot ${max}. Pas dit niet toe. Vraag een waarde in dat bereik.`,
      fa: `مقدار ${asked} برای ${fieldKey} خارج از محدوده است. محدوده مجاز ${min} تا ${max} است. این تغییر را اعمال نکن. از کاربر مقدار داخل محدوده بخواه.`,
      zh: `数值 ${asked} 超出 ${fieldKey} 的范围。允许范围是 ${min} 到 ${max}。不要应用这次更改。请向用户要一个范围内的数值。`,
    });
  }

  const resolvedFoot = resolveFoot(foot);
  return {
    fieldKey,
    sides: resolvedFoot === "both" ? ["right", "left"] : [resolvedFoot === "left" ? "left" : "right"],
    viewerValue,
    spoken: isNonLengthParam(fieldKey)
      ? voiceText({
          en: `${spokenNumber(value, 1)}${fieldKey.includes("Wedge") ? " degrees" : " percent"}`,
          nl: `${spokenNumber(value, 1)}${fieldKey.includes("Wedge") ? " graden" : " procent"}`,
          fa: `${spokenNumber(value, 1)}${fieldKey.includes("Wedge") ? " درجه" : " درصد"}`,
          zh: fieldKey.includes("Wedge")
            ? `${spokenNumber(value, 1)}度`
            : `百分之${spokenNumber(value, 1)}`,
        })
      : formatLength(viewerValue),
  };
}

export async function dispatchInsoleParam(
  foot: string,
  param: string,
  value: number,
  silent?: string | boolean,
  more?: string | boolean,
  changes?: string
): Promise<string> {
  const silentOn = isTruthyFlag(silent);
  const moreComing = isTruthyFlag(more);

  // The whole request travels in one call: the first change in foot/param/value,
  // any others in changes. The assistant does not reliably chain tool calls, so
  // nothing here may depend on it calling back.
  const requested = [
    { foot, param, value },
    ...(typeof changes === "string" ? parseExtraChanges(changes) : []).map((extra) => ({
      foot: extra.foot || foot,
      param: extra.param,
      value: extra.value,
    })),
  ];

  const resolved: ResolvedChange[] = [];
  for (const entry of requested) {
    const outcome = await resolveChange(entry.param, entry.value, entry.foot);
    // Reject the whole batch on a bad value: nothing is queued until now.
    if (typeof outcome === "string") return outcome;
    resolved.push(outcome);
  }

  // One step per parameter per foot, right foot before left.
  const steps: ParamStepSpec[] = [];
  for (const change of resolved) {
    for (const side of change.sides) {
      const footIndex = side === "left" ? 1 : 0;
      steps.push({
        fieldKey: change.fieldKey,
        foot: footIndex,
        updates: [{ fieldKey: change.fieldKey, foot: footIndex, value: change.viewerValue, single: true }],
        narrate: (windowMs: number, isLast: boolean) =>
          buildParamNarration({
            fieldKey: change.fieldKey,
            side,
            spoken: change.spoken,
            windowMs,
            isLast,
          }),
      });
    }
  }

  const spokenLines = await runParamSteps({ steps, silent: silentOn, expectFollowUp: moreComing });

  // Silent mode produces no narration, so report the changes as plain fact.
  return (
    spokenLines ||
    resolved
      .map((change) =>
        voiceText({
          en: `${change.fieldKey} set to ${change.spoken}.`,
          nl: `${change.fieldKey} gezet op ${change.spoken}.`,
          fa: `${change.fieldKey} روی ${change.spoken} گذاشته شد.`,
          zh: `${change.fieldKey} 已设为 ${change.spoken}。`,
        })
      )
      .join(" ")
  );
}

/**
 * The exhibition demonstration's own parameter change, in its own words.
 *
 * The hub assistant's narration is assembled from a four-language clause bank
 * and padded with filler until it fills each camera window. The demonstration
 * says something more deliberate — the value the parameter holds, the value it
 * is being set to, and then the same confirmed on the other foot — so the
 * wording is supplied here. Everything else is the shared machinery: the camera
 * still walks one step per foot, and each step is still timed to its own
 * sentence, because runParamSteps measures the line it is given.
 */
export async function dispatchDemoInsoleParam(
  param: string,
  value: number,
  label: string,
  previousValue: number
): Promise<string> {
  const outcome = await resolveChange(param, value, "both");
  if (typeof outcome === "string") return outcome;

  const was = formatLength(previousValue);
  const now = outcome.spoken;

  const steps: ParamStepSpec[] = outcome.sides.map((side) => {
    const footIndex = side === "left" ? 1 : 0;
    return {
      fieldKey: outcome.fieldKey,
      foot: footIndex,
      updates: [
        { fieldKey: outcome.fieldKey, foot: footIndex, value: outcome.viewerValue, single: true },
      ],
      // The window is deliberately ignored. These sentences are written, and
      // padding them out to fill the camera move would undo the point of
      // writing them; a step that outlasts its sentence simply holds.
      narrate: () =>
        side === "right"
          ? `${label} is now ${was}. I will set it to ${now}.`
          : `You can now also see this for the left insole. It's set to ${now}.`,
    };
  });

  return runParamSteps({ steps, silent: false, expectFollowUp: false });
}

export async function dispatchShoeSize(
  size: string | number,
  foot: string = "both",
  more?: string | boolean
): Promise<string> {
  const shoeSize = resolveShoeSize(size);
  if (!shoeSize) {
    return voiceText({
      en: `Unknown shoe size "${size}". Use EU 35 to 47, or set length and width in millimeters.`,
      nl: `Onbekende schoenmaat "${size}". Gebruik EU 35 tot 47, of zet lengte en breedte in millimeter.`,
      fa: `سایز کفش «${size}» ناشناخته است. از سایز اروپایی ۳۵ تا ۴۷ استفاده کن، یا طول و عرض را به میلی‌متر بگذار.`,
      zh: `未知鞋码「${size}」。请使用欧码三十五到四十七，或把长度和宽度设为毫米。`,
    });
  }
  const dims = Insole.GetLenWidthByShoeSize(shoeSize);
  const resolvedFoot = resolveFoot(foot);
  const lengthValue = convertMmToViewerUnit(dims.length);
  const widthValue = convertMmToViewerUnit(dims.width);

  const lengthRange = await requestParamRange("Length");
  const widthRange = await requestParamRange("Width");
  if (lengthRange && (lengthValue < lengthRange.min - 1e-6 || lengthValue > lengthRange.max + 1e-6)) {
    return `EU ${shoeSize} length ${formatLength(lengthValue)} is out of range. Allowed length is ${formatLength(lengthRange.min)} to ${formatLength(lengthRange.max)}. Do not apply this change.`;
  }
  if (widthRange && (widthValue < widthRange.min - 1e-6 || widthValue > widthRange.max + 1e-6)) {
    return `EU ${shoeSize} width ${formatLength(widthValue)} is out of range. Allowed width is ${formatLength(widthRange.min)} to ${formatLength(widthRange.max)}. Do not apply this change.`;
  }

  const lengthSpoken = formatLength(lengthValue);
  const widthSpoken = formatLength(widthValue);
  const moreComing = isTruthyFlag(more);
  const sides: ("right" | "left")[] = resolvedFoot === "both"
    ? ["right", "left"]
    : [resolvedFoot === "left" ? "left" : "right"];

  return runParamSteps({
    silent: false,
    expectFollowUp: moreComing,
    steps: sides.map((side) => {
      const footIndex = side === "left" ? 1 : 0;
      return {
        fieldKey: "Length",
        foot: footIndex,
        updates: [
          { fieldKey: "Length", foot: footIndex, value: lengthValue, single: true },
          { fieldKey: "Width", foot: footIndex, value: widthValue, single: true },
        ],
        narrate: (windowMs: number, isLast: boolean) =>
          buildShoeSizeNarration({
            side,
            shoeSize: String(shoeSize),
            lengthSpoken,
            widthSpoken,
            windowMs,
            isLast,
          }),
      };
    }),
  });
}

async function requestParamRange(fieldKey: string, foot: number = 0): Promise<{ min: number; max: number } | null> {
  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => resolve(null), 800);
    document.dispatchEvent(
      new CustomEvent(VOICE_GET_PARAM_RANGE_EVENT, {
        detail: {
          fieldKey,
          foot,
          resolve: (range: { min: number; max: number } | null) => {
            window.clearTimeout(timeout);
            resolve(range);
          },
        },
      })
    );
  });
}

function formatParamValue(fieldKey: string, viewerValue: number): string {
  if (fieldKey.includes("Fill")) {
    return voiceText({
      en: `${spokenNumber(viewerValue, 1)} percent`,
      nl: `${spokenNumber(viewerValue, 1)} procent`,
      fa: `${spokenNumber(viewerValue, 1)} درصد`,
      zh: `百分之${spokenNumber(viewerValue, 1)}`,
    });
  }
  if (fieldKey.includes("Wedge")) {
    return voiceText({
      en: `${spokenNumber(viewerValue, 1)} degrees`,
      nl: `${spokenNumber(viewerValue, 1)} graden`,
      fa: `${spokenNumber(viewerValue, 1)} درجه`,
      zh: `${spokenNumber(viewerValue, 1)}度`,
    });
  }
  return formatLength(viewerValue);
}

function resolveShoeSize(size: string | number): ShoeSize | undefined {
  const raw = String(size).trim();
  const digits = raw.replace(/[^\d]/g, "");
  if (!digits) return undefined;
  const value = digits as ShoeSize;
  return (Object.values(ShoeSize) as string[]).includes(value) ? value : undefined;
}

export async function requestInsoleState(): Promise<string> {
  const state = await new Promise<unknown>((resolve) => {
    const timeout = window.setTimeout(() => {
      resolve({ error: "Viewer is not ready. Open a design first." });
    }, 800);
    document.dispatchEvent(
      new CustomEvent(VOICE_GET_STATE_EVENT, {
        detail: {
          resolve: (next: unknown) => {
            window.clearTimeout(timeout);
            resolve(next);
          },
        },
      })
    );
  });
  const annotated = typeof state === "string" ? state : annotateInsoleState(state);
  return typeof annotated === "string" ? annotated : JSON.stringify(annotated);
}

export const VIEWER_MODE_SETTINGS = [
  "labels",
  "coloring",
  "projection",
  "display",
  "clipping",
  "grid",
  "feet",
] as const;
export type ViewerModeSetting = (typeof VIEWER_MODE_SETTINGS)[number];

const SETTING_ALIASES: Record<string, ViewerModeSetting> = {
  labels: "labels",
  label: "labels",
  labelmode: "labels",
  livemode: "labels",
  coloring: "coloring",
  color: "coloring",
  pressure: "coloring",
  projection: "projection",
  camera: "projection",
  cameramode: "projection",
  perspective: "projection",
  orthographic: "projection",
  display: "display",
  displaymode: "display",
  wireframe: "display",
  clipping: "clipping",
  clip: "clipping",
  infill: "clipping",
  fill: "clipping",
  section: "clipping",
  crosssection: "clipping",
  grid: "grid",
  grids: "grid",
  gridlines: "grid",
  feet: "feet",
  feetview: "feet",
  foot: "feet",
  برچسب: "labels",
  لیبل: "labels",
  برچسبها: "labels",
  رنگ: "coloring",
  رنگآمیزی: "coloring",
  رنگکفی: "coloring",
  فشار: "coloring",
  اطلاعاتفشار: "coloring",
  نقشهفشار: "coloring",
  پرسپکتیو: "projection",
  ارتوگرافیک: "projection",
  نمایش: "display",
  برش: "clipping",
  اینفیل: "clipping",
  داخل: "clipping",
  گرید: "grid",
  شبکه: "grid",
  پا: "feet",
  پاها: "feet",
  etiketten: "labels",
  kleur: "coloring",
  kleuring: "coloring",
  perspectief: "projection",
  projectie: "projection",
  orthografisch: "projection",
  weergave: "display",
  raster: "grid",
  rasterlijnen: "grid",
  knippen: "clipping",
  doorsnede: "clipping",
  draadmodel: "display",
  voeten: "feet",
  voet: "feet",
  标签: "labels",
  着色: "coloring",
  上色: "coloring",
  透视: "projection",
  正交: "projection",
  显示: "display",
  线框: "display",
  剖切: "clipping",
  剖面: "clipping",
  填充: "clipping",
  网格: "grid",
  脚: "feet",
  双脚显示: "feet",
};

const ON_VALUES = new Set(["on", "true", "show", "enable", "enabled", "active", "visible", "yes", "روشن", "فعال", "نمایش", "aan", "toon", "开", "打开", "开启"]);
const OFF_VALUES = new Set(["off", "false", "hide", "disable", "disabled", "inactive", "hidden", "no", "خاموش", "غیرفعال", "پنهان", "uit", "verberg", "关", "关闭", "关掉"]);

const DISPLAY_ALIASES: Record<string, string> = {
  insole: "insole",
  solid: "insole",
  mesh: "insole",
  wireframe: "wireframe",
  wire: "wireframe",
  both: "both",
  all: "both",
  توپر: "insole",
  کفی: "insole",
  وایرفریم: "wireframe",
  هردو: "both",
  zool: "insole",
  draadmodel: "wireframe",
  beide: "both",
  实体: "insole",
  鞋垫: "insole",
  线框: "wireframe",
  两者: "both",
};

const PROJECTION_ALIASES: Record<string, boolean> = {
  perspective: true,
  persp: true,
  ortho: false,
  orthographic: false,
  پرسپکتیو: true,
  ارتوگرافیک: false,
  perspectief: true,
  orthografisch: false,
  透视: true,
  正交: false,
};

export function dispatchViewerMode(setting: string, value: string): string {
  const resolvedSetting = resolveViewerSetting(setting);
  if (!resolvedSetting) {
    return `Unknown viewer setting "${setting}". Use one of: ${VIEWER_MODE_SETTINGS.join(", ")}.`;
  }

  const store = useViewerStore.getState();
  const raw = value.trim();
  const normalized = raw.toLowerCase();

  switch (resolvedSetting) {
    case "labels": {
      const on = parseOnOff(normalized, raw);
      if (on === undefined) return `labels value must be on or off. Received: "${value}"`;
      store.setOldLiveMode(store.liveMode);
      store.setLiveMode(on);
      return on
        ? voiceText({ en: "Labels are on.", nl: "Labels staan aan.", fa: "برچسب‌ها روشن شد.", zh: "标签已打开。"})
        : voiceText({ en: "Labels are off.", nl: "Labels staan uit.", fa: "برچسب‌ها خاموش شد.", zh: "标签已关闭。"});
    }
    case "coloring": {
      const on = parseColoringValue(normalized, raw);
      if (on === undefined) {
        return `coloring value must be pressure, color, on, or off. Received: "${value}"`;
      }
      if (!on && !isColoringAvailable()) {
        return voiceText({
          en: "There is no pressure data to show.",
          nl: "Er is geen drukdata om te tonen.",
          fa: "داده فشاری برای نمایش نیست.",
          zh: "没有可显示的压力数据。",
        });
      }
      store.setColoring(on);
      return on
        ? voiceText({
            en: "Insole color is on.",
            nl: "Zoolkleur staat aan.",
            fa: "رنگ کفی روشن شد.",
            zh: "已显示鞋垫颜色。",
          })
        : voiceText({
            en: "Pressure is shown.",
            nl: "Druk wordt getoond.",
            fa: "اطلاعات فشار نمایش داده شد.",
            zh: "已显示压力数据。",
          });
    }
    case "projection": {
      if (ON_VALUES.has(normalized) || OFF_VALUES.has(normalized)) {
        return 'projection value must be "perspective" or "orthographic".';
      }
      const perspective = PROJECTION_ALIASES[normalized] ?? PROJECTION_ALIASES[raw];
      if (perspective === undefined) {
        return `Unknown projection "${value}". Use perspective or orthographic.`;
      }
      store.setPerspectiveCamera(perspective);
      return perspective
        ? voiceText({ en: "Camera is perspective.", nl: "Camera is perspectief.", fa: "دوربین پرسپکتیو شد.", zh: "相机已设为透视。"})
        : voiceText({ en: "Camera is orthographic.", nl: "Camera is orthografisch.", fa: "دوربین ارتوگرافیک شد.", zh: "相机已设为正交。"});
    }
    case "display": {
      const mode = DISPLAY_ALIASES[normalized] ?? DISPLAY_ALIASES[raw];
      if (!mode) return `display value must be insole, wireframe, or both. Received: "${value}"`;
      store.setWireframe(mode);
      return voiceText({
        en: `Display mode is ${mode}.`,
        nl: mode === "insole"
          ? "Weergave is de zool."
          : mode === "wireframe"
            ? "Weergave is draadmodel."
            : "Weergave is zool en draadmodel.",
        fa: mode === "insole"
          ? "نمایش کفی توپر است."
          : mode === "wireframe"
            ? "نمایش وایرفریم است."
            : "نمایش کفی و وایرفریم با هم است.",
        zh: mode === "insole"
          ? "显示为实体鞋垫。"
          : mode === "wireframe"
            ? "显示为线框。"
            : "同时显示鞋垫和线框。",
      });
    }
    case "clipping": {
      const on = parseOnOff(normalized, raw);
      if (on === false) return applyClippingView({ enabled: "off" });
      if (on === true || CLIPPING_ON_ALIASES.has(normalized) || CLIPPING_ON_ALIASES.has(raw)) {
        return applyClippingView({ enabled: "on" });
      }
      const percent = parseClippingPercent(raw);
      if (percent !== undefined) return applyClippingView({ percent });
      return `clipping value must be on, off, infill, or a percent. Received: "${value}"`;
    }
    case "grid": {
      const on = parseOnOff(normalized, raw);
      if (on === undefined) return `grid value must be on or off. Received: "${value}"`;
      store.setGrids(on);
      return on
        ? voiceText({ en: "Grid lines are on.", nl: "Rasterlijnen staan aan.", fa: "خطوط شبکه روشن شد.", zh: "网格线已打开。"})
        : voiceText({ en: "Grid lines are off.", nl: "Rasterlijnen staan uit.", fa: "خطوط شبکه خاموش شد.", zh: "网格线已关闭。"});
    }
    case "feet": {
      const mapped = FOOT_ALIASES[normalized] ?? FOOT_ALIASES[raw];
      if (!mapped) return `feet value must be left, right, or both. Received: "${value}"`;
      store.setFeetView(mapped);
      const footName = {
        left: voiceText({ en: "left", nl: "linker", fa: "چپ", zh: "左"}),
        right: voiceText({ en: "right", nl: "rechter", fa: "راست", zh: "右"}),
        both: voiceText({ en: "both", nl: "beide", fa: "هر دو", zh: "双侧"}),
      }[mapped];
      return voiceText({
        en: mapped === "both" ? "Showing both feet." : `Showing the ${footName} foot.`,
        nl: mapped === "both" ? "Ik toon beide voeten." : `Ik toon de ${footName} voet.`,
        fa: mapped === "both" ? "هر دو پا را نشان می‌دهم." : `کفی ${footName} را نشان می‌دهم.`,
        zh: mapped === "both" ? "正在显示双脚。" : `正在显示${footName}脚。`,
      });
    }
  }
}

export function requestViewerModeState(): string {
  const state = useViewerStore.getState();
  return JSON.stringify({
    labels: state.liveMode ? "on" : "off",
    coloring: state.coloring ? "on" : "off",
    surface: state.coloring ? "color" : "pressure",
    coloringAvailable: isColoringAvailable(),
    projection: state.perspectiveCamera ? "perspective" : "orthographic",
    display: state.wireframe,
    clipping: state.clipping ? "on" : "off",
    clippingVerticalPercent: state.clippingVerticalPercent,
    clippingLateralPercent: state.clippingLateralPercent,
    grid: state.grids ? "on" : "off",
    feet: state.feetView,
    project: {
      topColor: state.topInsoleDefaultColor,
      bottomColor: state.bottomInsoleDefaultColor,
      liftedColor: state.liftedInsoleDefaultColor,
      shoeColor: state.shoeDataDefaultColor,
      insoleTransparency: state.pressureTransparency,
      liftedTransparency: state.liftedInsoleTransparency,
      bonesTransparency: state.bonesTransparency,
      shoeTransparency: state.shoeDataTransparency,
      animate: state.animatePressure ? "on" : "off",
      vertexMarker: state.showMarkerPosition ? "on" : "off",
      dynamicPressure: useDesignStore.getState().insoleDesign?.pressureData?.ExamType === "dynamic",
    },
  });
}

function isColoringAvailable(): boolean {
  const design = useDesignStore.getState();
  return Boolean(design.insoleDesign?.pressureData) || !design.RondomImageData.some((img) => img === undefined);
}

const CLIPPING_ON_ALIASES = new Set([
  "infill",
  "fill",
  "inside",
  "section",
  "crosssection",
  "اینفیل",
  "داخل",
  "برش",
  "doorsnede",
  "剖切",
  "剖面",
  "填充",
]);

function parseOnOff(normalized: string, raw: string): boolean | undefined {
  if (ON_VALUES.has(normalized) || ON_VALUES.has(raw)) return true;
  if (OFF_VALUES.has(normalized) || OFF_VALUES.has(raw)) return false;
  return undefined;
}

/** Left-bar coloring: true = insole fill color, false = pressure heatmap. */
const COLORING_COLOR_VALUES = new Set([
  "color",
  "colour",
  "insole",
  "fill",
  "insolecolor",
  "insolecolour",
  "kleur",
  "zoolkleur",
  "رنگ",
  "رنگکفی",
  "颜色",
  "鞋垫颜色",
]);
const COLORING_PRESSURE_VALUES = new Set([
  "pressure",
  "heatmap",
  "scan",
  "druk",
  "drukdata",
  "فشار",
  "اطلاعاتفشار",
  "نقشهفشار",
  "压力",
  "压力图",
]);

function parseColoringValue(normalized: string, raw: string): boolean | undefined {
  const compact = normalized.replace(/[\s_-]/g, "");
  if (COLORING_COLOR_VALUES.has(compact) || COLORING_COLOR_VALUES.has(raw)) return true;
  if (COLORING_PRESSURE_VALUES.has(compact) || COLORING_PRESSURE_VALUES.has(raw)) return false;
  return parseOnOff(normalized, raw);
}

function parseClippingPercent(value: unknown): number | undefined {
  if (value == null || value === "") return undefined;
  const text = String(value).trim().replace(/%/g, "").replace(",", ".");
  const match = text.match(/-?\d+(\.\d+)?/);
  if (!match) return undefined;
  const amount = Number(match[0]);
  if (!Number.isFinite(amount)) return undefined;
  return Math.max(0, Math.min(100, Math.round(amount)));
}

export function applyClippingView(args: {
  percent?: number | string;
  verticalPercent?: number | string;
  lateralPercent?: number | string;
  enabled?: string | boolean;
} = {}): string {
  const store = useViewerStore.getState();
  const enabledRaw = args.enabled == null ? "" : String(args.enabled).trim();
  if (enabledRaw) {
    const on = parseOnOff(enabledRaw.toLowerCase(), enabledRaw);
    if (on === false) {
      store.setClipping(false);
      return voiceText({
        en: "Clipping is off.",
        nl: "Knippen staat uit.",
        fa: "برش خاموش شد.",
        zh: "剖切已关闭。",
      });
    }
  }

  const both = parseClippingPercent(args.percent);
  const vertical = parseClippingPercent(args.verticalPercent);
  const lateral = parseClippingPercent(args.lateralPercent);
  const nextVertical = vertical ?? both ?? (store.clipping ? store.clippingVerticalPercent : 50);
  const nextLateral = lateral ?? both ?? (store.clipping ? store.clippingLateralPercent : 100);

  store.setClippingPercents(nextVertical, nextLateral);
  store.setClipping(true);
  return voiceText({
    en: `Clipping is on. Vertical ${spokenNumber(nextVertical, 0)} percent, lateral ${spokenNumber(nextLateral, 0)} percent.`,
    nl: `Knippen staat aan. Verticaal ${spokenNumber(nextVertical, 0)} procent, lateraal ${spokenNumber(nextLateral, 0)} procent.`,
    fa: `برش روشن شد. عمودی ${spokenNumber(nextVertical, 0)} درصد، عرضی ${spokenNumber(nextLateral, 0)} درصد.`,
    zh: `剖切已打开。纵向百分之${spokenNumber(nextVertical, 0)}，横向百分之${spokenNumber(nextLateral, 0)}。`,
  });
}

function resolveViewerSetting(setting: string): ViewerModeSetting | undefined {
  const trimmed = setting.trim();
  const compact = trimmed.toLowerCase().replace(/[\s_-]/g, "");
  if ((VIEWER_MODE_SETTINGS as readonly string[]).includes(compact)) {
    return compact as ViewerModeSetting;
  }
  return SETTING_ALIASES[compact] ?? SETTING_ALIASES[trimmed];
}

function isNonLengthParam(fieldKey: string): boolean {
  return fieldKey.includes("Wedge") || fieldKey.includes("Fill") || fieldKey.includes("Sagital");
}

function currentLengthUnit(): LengthUnit {
  return useViewerStore.getState().measurementSystem === MeasurementSystem.imperial ? "inch" : "mm";
}

function lengthUnitLabel(): string {
  if (currentLengthUnit() === "inch") {
    return voiceText({ en: "inches", nl: "inch", fa: "اینچ", zh: "英寸"});
  }
  return voiceText({ en: "millimeters", nl: "millimeter", fa: "میلی‌متر", zh: "毫米"});
}

function toViewerLength(value: number): number {
  return convertLength(value, currentLengthUnit());
}

function formatLength(value: number): string {
  const n = spokenNumber(value, 2);
  const unit = lengthUnitLabel();
  return currentVoiceLang() === "zh" ? `${n}${unit}` : `${n} ${unit}`;
}

const INSOLE_LENGTH_KEYS = [
  "heelHeight",
  "heelDepth",
  "thickness",
  "archMedialHeight",
  "archLateralHeight",
  "archCenterHeight",
  "length",
  "width",
  "outsoleThickness",
  "bandHeight",
  "partialLength",
  // Named "area" but it is the length of the instep curve, in millimetres.
  "instepArea",
] as const;

function annotateInsoleState(state: unknown): unknown {
  const system = useViewerStore.getState().measurementSystem;
  const lengthUnit = currentLengthUnit();
  const roundFoot = (foot: Record<string, number> | null | undefined) => {
    if (!foot) return null;
    const next = { ...foot };
    for (const key of INSOLE_LENGTH_KEYS) {
      if (typeof next[key] === "number") next[key] = Number(convertMmToViewerUnit(next[key]).toFixed(2));
    }
    return next;
  };
  const body = state && typeof state === "object"
    ? state as { left?: Record<string, number>; right?: Record<string, number>; limits?: Record<string, { min: number; max: number }>; error?: string }
    : {};
  if (body.error) return body;

  const limits: Record<string, { min: number; max: number; unit: string }> = {};
  if (body.limits) {
    for (const [fieldKey, range] of Object.entries(body.limits)) {
      const unit = fieldKey.includes("Fill") || fieldKey.includes("Sagital")
        ? "percent"
        : fieldKey.includes("Wedge")
          ? "degrees"
          : lengthUnit;
      limits[fieldKey] = {
        min: Number(Number(range.min).toFixed(2)),
        max: Number(Number(range.max).toFixed(2)),
        unit,
      };
    }
  }

  return {
    measurementSystem: system,
    units: { length: lengthUnit, wedges: "degrees", fill: "0-100" },
    speakLengthsAs: lengthUnitLabel(),
    left: roundFoot(body.left),
    right: roundFoot(body.right),
    limits,
    note: lengthUnit === "inch"
      ? "Length values are inches. Speak them as inches. setInsoleParam length values must be inches and must stay inside limits."
      : "Length values are millimeters. Speak them as millimeters. setInsoleParam length values must be millimeters and must stay inside limits.",
  };
}

/** Spoken name of a computed parameter, or undefined when it is not one. */
function resolveReadOnlyParam(param: string): string | undefined {
  const key = param.trim().toLowerCase().replace(/[\s_-]/g, "");
  for (const [field, label] of Object.entries(INSOLE_READ_ONLY_PARAMS)) {
    if (key === field.toLowerCase() || key === label.toLowerCase().replace(/\s/g, "")) return label;
  }
  return READ_ONLY_PARAM_ALIASES[key];
}

const READ_ONLY_PARAM_ALIASES: Record<string, string> = {
  partial: "Partial Length",
  partiallength: "Partial Length",
  gedeeltelijkelengte: "Partial Length",
  طولجزئی: "Partial Length",
  طولبخشی: "Partial Length",
  部分长度: "Partial Length",
  instep: "Instep Area",
  insteparea: "Instep Area",
  instepcircumference: "Instep Area",
  wreefgebied: "Instep Area",
  wreef: "Instep Area",
  ناحیهرویپا: "Instep Area",
  رویپا: "Instep Area",
  محیطرویپا: "Instep Area",
  足背围度: "Instep Area",
  足背: "Instep Area",
};

function resolveParam(param: string): keyof typeof INSOLE_PARAM_FIELD_KEYS | undefined {
  const trimmed = param.trim();
  if (trimmed in INSOLE_PARAM_FIELD_KEYS) {
    return trimmed as keyof typeof INSOLE_PARAM_FIELD_KEYS;
  }
  return PARAM_ALIASES[trimmed.toLowerCase().replace(/[\s_-]/g, "")];
}

function resolveFoot(foot: string): VoiceFoot {
  const trimmed = foot.trim();
  return FOOT_ALIASES[trimmed.toLowerCase()] ?? FOOT_ALIASES[trimmed] ?? "both";
}

export const PROPERTY_PANELS = ["automatic", "surface", "additions", "engraving", "project"] as const;
export type PropertyPanelName = (typeof PROPERTY_PANELS)[number];

const PANEL_ALIASES: Record<string, PropertyPanelName | "close"> = {
  automatic: "automatic",
  autodesign: "automatic",
  automaticdesign: "automatic",
  design: "automatic",
  view: "automatic",
  surface: "surface",
  editsurface: "surface",
  edit: "surface",
  additions: "additions",
  addition: "additions",
  hardness: "additions",
  pads: "additions",
  engraving: "engraving",
  engrave: "engraving",
  text: "engraving",
  project: "project",
  settings: "project",
  projectsettings: "project",
  close: "close",
  hide: "close",
  closed: "close",
  dismiss: "close",
  بستن: "close",
  ببند: "close",
  ببندش: "close",
  طراحی: "automatic",
  اتوماتیک: "automatic",
  سطح: "surface",
  ویرایش: "surface",
  افزودنی: "additions",
  اضافات: "additions",
  پد: "additions",
  حکاکی: "engraving",
  پروژه: "project",
  تنظیمات: "project",
  automatisch: "automatic",
  ontwerp: "automatic",
  bewerken: "surface",
  bewerkingspaneel: "surface",
  oppervlak: "surface",
  toevoegingen: "additions",
  graveren: "engraving",
  graveer: "engraving",
  projectinstellingen: "project",
  instellingen: "project",
  sluiten: "close",
  پنلویرایش: "surface",
  پنلسطح: "surface",
  پنلحکاکی: "engraving",
  پنلافزودنی: "additions",
  自动设计: "automatic",
  编辑表面: "surface",
  表面: "surface",
  附加: "additions",
  添加物: "additions",
  雕刻: "engraving",
  项目设置: "project",
  关闭: "close",
};

const PANEL_TO_TAB: Record<PropertyPanelName, ViewerTabsMode> = {
  automatic: ViewerTabsMode.View,
  surface: ViewerTabsMode.Edit,
  additions: ViewerTabsMode.Additions,
  engraving: ViewerTabsMode.Engraving,
  project: ViewerTabsMode.Project,
};

export function openPropertyPanel(panel: string): string {
  const compact = panel.trim().toLowerCase().replace(/[\s_-]/g, "");
  const resolved = PANEL_ALIASES[compact] ?? PANEL_ALIASES[panel.trim()];
  if (!resolved) {
    return voiceText({
      en: `Unknown panel "${panel}". Use one of: ${PROPERTY_PANELS.join(", ")}.`,
      nl: `Onbekend paneel "${panel}". Gebruik: ${PROPERTY_PANELS.join(", ")}.`,
      fa: `پنل «${panel}» ناشناخته است. از این‌ها استفاده کن: ${PROPERTY_PANELS.join(", ")}.`,
      zh: `未知面板「${panel}」。请使用：${PROPERTY_PANELS.join(", ")}。`,
    });
  }
  if (resolved === "close") {
    document.dispatchEvent(new CustomEvent(VOICE_OPEN_PANEL_EVENT, { detail: { close: true } }));
    document.dispatchEvent(new Event("toolbox:close"));
    document.dispatchEvent(new CustomEvent(VOICE_CLOSE_PANELS_EVENT));
    return voiceText({
      en: "Closed the open panel.",
      nl: "Het open paneel is gesloten.",
      fa: "پنل باز بسته شد.",
      zh: "已关闭打开的面板。",
    });
  }
  openAfterClose(() => {
    document.dispatchEvent(new CustomEvent(VOICE_OPEN_PANEL_EVENT, { detail: { tab: PANEL_TO_TAB[resolved] } }));
  });
  const panelName = {
    automatic: voiceText({ en: "Automatic Design", nl: "Automatisch ontwerp", fa: "طراحی خودکار", zh: "自动设计"}),
    surface: voiceText({ en: "Edit Surface", nl: "het bewerkingspaneel", fa: "ویرایش سطح", zh: "编辑表面"}),
    additions: voiceText({ en: "Additions", nl: "Toevoegingen", fa: "افزودنی‌ها", zh: "附加件"}),
    engraving: voiceText({ en: "Engraving", nl: "Graveren", fa: "حکاکی", zh: "雕刻"}),
    project: voiceText({ en: "Project Settings", nl: "Projectinstellingen", fa: "تنظیمات پروژه", zh: "项目设置"}),
  }[resolved];
  return voiceText({
    en: `${panelName} is open.`,
    nl: `${panelName} is open.`,
    fa: `پنل ${panelName} باز شد.`,
    zh: `${panelName}已打开。`,
  });
}

const ENGRAVING_PARAMS: Record<string, keyof Engraving> = {
  engraveText: "engraveText",
  textString: "textString",
  textDepth: "textDepth",
  textHeight: "textHeight",
  textRepeat: "textRepeat",
  textVertical: "textVertical",
  engraveUID: "engraveUID",
  UIDDepth: "UIDDepth",
  UIDHeight: "UIDHeight",
  UIDRepeat: "UIDRepeat",
  UIDVertical: "UIDVertical",
  engraveCE: "engraveCE",
  CEDepth: "CEDepth",
  CEHeight: "CEHeight",
  CEVertical: "CEVertical",
  engraveLogo: "engraveLogo",
  invertLogo: "invertLogo",
  logoDepth: "logoDepth",
  logoWidth: "logoWidth",
};

const ENGRAVING_PARAM_ALIASES: Record<string, keyof Engraving> = {
  text: "textString",
  string: "textString",
  engravingtext: "textString",
  designname: "textString",
  designtitle: "textString",
  design: "textString",
  title: "textString",
  depth: "textDepth",
  height: "textHeight",
  repeat: "textRepeat",
  vertical: "textVertical",
  uid: "engraveUID",
  ce: "engraveCE",
  logo: "engraveLogo",
  متن: "textString",
  اسمطرح: "textString",
  اسمدیزاین: "textString",
  نامطرح: "textString",
  عمق: "textDepth",
  ارتفاع: "textHeight",
  tekst: "textString",
  ontwerpnaam: "textString",
  titel: "textString",
  设计名: "textString",
  设计名称: "textString",
  雕刻文字: "textString",
  文字: "textString",
};

export function dispatchEngravingParam(param: string, value: string): string {
  const field = resolveEngravingParam(param);
  if (!field) {
    return `Unknown engraving param "${param}". Use one of: ${Object.keys(ENGRAVING_PARAMS).join(", ")}.`;
  }

  if (field === "textString") {
    const title = value.trim();
    if (!title) return "Design name cannot be empty.";
    useDesignStore.getState().setDesignName(title);
  }
  const next = Engraving.cloneFrom(useDesignStore.getState().engraving);
  const booleanFields = new Set([
    "engraveText",
    "textVertical",
    "engraveUID",
    "UIDVertical",
    "engraveCE",
    "CEVertical",
    "engraveLogo",
    "invertLogo",
  ]);

  if (field === "textString") {
    next.textString = value.trim();
  } else if (booleanFields.has(field)) {
    const on = parseOnOff(value.trim().toLowerCase(), value.trim());
    if (on === undefined) return `${field} must be on or off. Received: "${value}"`;
    (next as any)[field] = on;
  } else {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return `${field} must be a number. Received: "${value}"`;
    (next as any)[field] = numeric;
  }

  next.updateText();
  next.updateUID();
  next.updateCE();

  useDesignStore.getState().setEngraving(next);
  useViewerStore.getState().setUpdateFromPanel(true);
  if (field === "textString") {
    scheduleAutosave("New design title", { updateDesignInfo: true, now: true });
    document.dispatchEvent(new CustomEvent("voice:engravingUpdated"));
    return `Design name and text string are ${next.textString}.`;
  }
  document.dispatchEvent(new CustomEvent("voice:engravingUpdated"));
  return `Engraving ${field} set to ${String((next as any)[field])}.`;
}

export function requestEngravingState(): string {
  const engraving = useDesignStore.getState().engraving;
  return JSON.stringify({
    engraveText: engraving.engraveText,
    textString: engraving.textString,
    textDepth: engraving.textDepth,
    textHeight: engraving.textHeight,
    textRepeat: engraving.textRepeat,
    textVertical: engraving.textVertical,
    engraveUID: engraving.engraveUID,
    UIDDepth: engraving.UIDDepth,
    UIDHeight: engraving.UIDHeight,
    UIDRepeat: engraving.UIDRepeat,
    UIDVertical: engraving.UIDVertical,
    engraveCE: engraving.engraveCE,
    CEDepth: engraving.CEDepth,
    CEHeight: engraving.CEHeight,
    CEVertical: engraving.CEVertical,
    engraveLogo: engraving.engraveLogo,
  });
}

const PROJECT_VISIBILITY: Record<string, { key: string; label: string }> = {
  insoles: { key: "insolesVisible", label: "insoles" },
  lifted: { key: "liftedInsoleMeshVisible", label: "lifted insole" },
  bones: { key: "bonesMeshVisible", label: "bones" },
  shoe: { key: "shoeDataMeshVisible", label: "shoe" },
  additionsmesh: { key: "additionsViewMeshVisible", label: "addition meshes" },
  additions: { key: "additionsViewMeshVisible", label: "addition meshes" },
  additionvisibility: { key: "additionsViewMeshVisible", label: "addition meshes" },
  padsvisibility: { key: "additionsViewMeshVisible", label: "addition meshes" },
  gcode: { key: "gcodeVisible", label: "gcode" },
  کفی: { key: "insolesVisible", label: "insoles" },
  استخوان: { key: "bonesMeshVisible", label: "bones" },
  کفش: { key: "shoeDataMeshVisible", label: "shoe" },
  zolen: { key: "insolesVisible", label: "insoles" },
  botten: { key: "bonesMeshVisible", label: "bones" },
  schoen: { key: "shoeDataMeshVisible", label: "shoe" },
};

const PROJECT_COLOR_ALIASES: Record<string, "insole" | "top" | "bottom" | "lifted" | "shoe"> = {
  insolecolor: "insole",
  color: "insole",
  colour: "insole",
  topcolor: "top",
  topinsolecolor: "top",
  bottomcolor: "bottom",
  bottominsolecolor: "bottom",
  liftedcolor: "lifted",
  shoecolor: "shoe",
  رنگ: "insole",
  رنگکفی: "insole",
  رنگبالا: "top",
  رنگپایین: "bottom",
  رنگلیفت: "lifted",
  رنگکفش: "shoe",
  kleur: "insole",
  zoolkleur: "insole",
  颜色: "insole",
  鞋垫颜色: "insole",
};

const PROJECT_TRANSPARENCY_ALIASES: Record<string, "insole" | "lifted" | "bones" | "shoe"> = {
  insoletransparency: "insole",
  transparency: "insole",
  transprency: "insole",
  liftedtransparency: "lifted",
  bonestransparency: "bones",
  shoetransparency: "shoe",
  شفافیت: "insole",
  شفافیتکفی: "insole",
  شفافیتلیفت: "lifted",
  شفافیتاستخوان: "bones",
  شفافیتکفش: "shoe",
  transparantie: "insole",
  透明度: "insole",
};

const PROJECT_TOGGLE_ALIASES: Record<string, "animate" | "vertexMarker"> = {
  animate: "animate",
  animatepressure: "animate",
  animation: "animate",
  vertexmarker: "vertexMarker",
  vertex: "vertexMarker",
  marker: "vertexMarker",
  انیمیت: "animate",
  انیمیشن: "animate",
  ورتکس: "vertexMarker",
  نشانگر: "vertexMarker",
  animeren: "animate",
  动画: "animate",
  顶点: "vertexMarker",
};

const NAMED_COLORS: Record<string, string> = {
  red: "ff0000",
  blue: "156289",
  navy: "0b3d66",
  white: "ffffff",
  black: "222222",
  green: "2ecc71",
  yellow: "f1c40f",
  orange: "e67e22",
  pink: "e91e63",
  purple: "8e44ad",
  gray: "7f8c8d",
  grey: "7f8c8d",
  brown: "8b5a2b",
  قرمز: "ff0000",
  آبی: "156289",
  سفید: "ffffff",
  مشکی: "222222",
  سیاه: "222222",
  سبز: "2ecc71",
  زرد: "f1c40f",
  نارنجی: "e67e22",
  صورتی: "e91e63",
  بنفش: "8e44ad",
  خاکستری: "7f8c8d",
  قهوهای: "8b5a2b",
  rood: "ff0000",
  blauw: "156289",
  wit: "ffffff",
  zwart: "222222",
  groen: "2ecc71",
  geel: "f1c40f",
  oranje: "e67e22",
  roze: "e91e63",
  paars: "8e44ad",
  grijs: "7f8c8d",
  bruin: "8b5a2b",
  红: "ff0000",
  蓝: "156289",
  白: "ffffff",
  黑: "222222",
  绿: "2ecc71",
  黄: "f1c40f",
};

const PROJECT_SETTING_NAMES = [
  "insoles",
  "lifted",
  "bones",
  "shoe",
  "additionsMesh",
  "gcode",
  "insoleColor",
  "topColor",
  "bottomColor",
  "liftedColor",
  "shoeColor",
  "insoleTransparency",
  "liftedTransparency",
  "bonesTransparency",
  "shoeTransparency",
  "animate",
  "vertexMarker",
];

function parseHexColor(value: string): string | undefined {
  const raw = value.trim();
  const compact = raw.toLowerCase().replace(/[\s_-]/g, "");
  if (NAMED_COLORS[compact]) return NAMED_COLORS[compact];
  const hex = raw.replace(/^#/, "");
  if (/^[0-9a-fA-F]{6}$/.test(hex)) return hex.toLowerCase();
  if (/^[0-9a-fA-F]{3}$/.test(hex)) return hex.toLowerCase().split("").map((c) => c + c).join("");
  return undefined;
}

function parseTransparency(value: string): number | undefined {
  const raw = value.trim().toLowerCase();
  if (["opaque", "solid", "مات", "on"].includes(raw)) return 255;
  if (["transparent", "شفاف"].includes(raw)) return 80;
  const percent = /%|percent|درصد|procent/.test(raw);
  const match = raw.replace(",", ".").match(/-?\d+(\.\d+)?/);
  if (!match) return undefined;
  const amount = Number(match[0]);
  if (!Number.isFinite(amount)) return undefined;
  if (percent || (amount >= 0 && amount <= 100 && raw.includes("%"))) {
    return Math.max(0, Math.min(255, Math.round((amount / 100) * 255)));
  }
  return Math.max(0, Math.min(255, Math.round(amount)));
}

function ensureInsoleColorVisible() {
  const store = useViewerStore.getState();
  if (!store.coloring) store.setColoring(true);
  if (store.animatePressure) useViewerStore.setState({ animatePressure: false });
}

export function dispatchProjectSetting(setting: string, value: string): string {
  const compact = setting.trim().toLowerCase().replace(/[\s_-]/g, "");
  const store = useViewerStore.getState();

  const visibility = PROJECT_VISIBILITY[compact];
  if (visibility) {
    const on = parseOnOff(value.trim().toLowerCase(), value.trim());
    if (on === undefined) return `${visibility.label} value must be on or off. Received: "${value}"`;
    useViewerStore.setState({ [visibility.key]: on } as any);
    return on
      ? voiceText({
          en: `${visibility.label} are visible.`,
          nl: `${visibility.label} zijn zichtbaar.`,
          fa: `${visibility.label} نمایش داده شد.`,
          zh: `已显示${visibility.label}。`,
        })
      : voiceText({
          en: `${visibility.label} are hidden.`,
          nl: `${visibility.label} zijn verborgen.`,
          fa: `${visibility.label} مخفی شد.`,
          zh: `已隐藏${visibility.label}。`,
        });
  }

  const colorTarget = PROJECT_COLOR_ALIASES[compact];
  if (colorTarget) {
    const hex = parseHexColor(value);
    if (!hex) return `Color value must be a name or hex. Received: "${value}"`;
    ensureInsoleColorVisible();
    if (colorTarget === "insole" || colorTarget === "top") store.setTopInsoleDeafultColor(hex);
    if (colorTarget === "insole" || colorTarget === "bottom") store.setBottomInsoleDeafultColor(hex);
    if (colorTarget === "lifted") store.setLiftedInsoleDefaultColor(hex);
    if (colorTarget === "shoe") store.setShoeDataDefaultColor(hex);
    return voiceText({
      en: `Color is #${hex}.`,
      nl: `Kleur is #${hex}.`,
      fa: `رنگ روی #${hex} تنظیم شد.`,
      zh: `颜色已设为#${hex}。`,
    });
  }

  const transparencyTarget = PROJECT_TRANSPARENCY_ALIASES[compact];
  if (transparencyTarget) {
    const amount = parseTransparency(value);
    if (amount === undefined) return `Transparency must be 0 to 255, or a percent. Received: "${value}"`;
    if (transparencyTarget === "insole") {
      ensureInsoleColorVisible();
      store.setPressureTrancparency(amount);
    }
    if (transparencyTarget === "lifted") store.setLiftedInsoleTransparency(amount);
    if (transparencyTarget === "bones") store.setBonesTransparency(amount);
    if (transparencyTarget === "shoe") store.setShoeDataTransparency(amount);
    return voiceText({
      en: `Transparency is ${amount}.`,
      nl: `Transparantie is ${amount}.`,
      fa: `شفافیت الان ${spokenNumber(amount, 0)} است.`,
      zh: `透明度已设为${spokenNumber(amount, 0)}。`,
    });
  }

  const toggle = PROJECT_TOGGLE_ALIASES[compact];
  if (toggle === "animate") {
    const on = parseOnOff(value.trim().toLowerCase(), value.trim());
    if (on === undefined) return `animate value must be on or off. Received: "${value}"`;
    const exam = useDesignStore.getState().insoleDesign?.pressureData?.ExamType;
    if (on && exam !== "dynamic") {
      return voiceText({
        en: "Animate is only for dynamic pressure data.",
        nl: "Animeren kan alleen bij dynamische drukdata.",
        fa: "انیمیت فقط برای فشار دینامیک است.",
        zh: "动画仅适用于动态压力数据。",
      });
    }
    if (on && store.coloring) store.setColoring(false);
    useViewerStore.setState({ animatePressure: on });
    return on
      ? voiceText({
          en: "Pressure animation is on.",
          nl: "Drukanimatie staat aan.",
          fa: "انیمیت فشار روشن شد.",
          zh: "压力动画已打开。",
        })
      : voiceText({
          en: "Pressure animation is off.",
          nl: "Drukanimatie staat uit.",
          fa: "انیمیت فشار خاموش شد.",
          zh: "压力动画已关闭。",
        });
  }
  if (toggle === "vertexMarker") {
    const on = parseOnOff(value.trim().toLowerCase(), value.trim());
    if (on === undefined) return `vertexMarker value must be on or off. Received: "${value}"`;
    store.setShowMarkerPosition(on);
    return on
      ? voiceText({
          en: "Vertex marker is on.",
          nl: "Vertexmarker staat aan.",
          fa: "ورتکس مارکر روشن شد.",
          zh: "顶点标记已打开。",
        })
      : voiceText({
          en: "Vertex marker is off.",
          nl: "Vertexmarker staat uit.",
          fa: "ورتکس مارکر خاموش شد.",
          zh: "顶点标记已关闭。",
        });
  }

  return `Unknown project setting "${setting}". Use one of: ${PROJECT_SETTING_NAMES.join(", ")}.`;
}

function resolveEngravingParam(param: string): keyof Engraving | undefined {
  const trimmed = param.trim();
  if (trimmed in ENGRAVING_PARAMS) return ENGRAVING_PARAMS[trimmed];
  const compact = trimmed.toLowerCase().replace(/[\s_-]/g, "");
  return ENGRAVING_PARAM_ALIASES[compact] ?? ENGRAVING_PARAMS[compact];
}

const HARDNESS_ALIASES: Record<string, Hardness> = {
  extrahard: Hardness.ExtraHard,
  "extra hard": Hardness.ExtraHard,
  hard: Hardness.Hard,
  medium: Hardness.Medium,
  soft: Hardness.Soft,
  extrasoft: Hardness.ExtraSoft,
  "extra soft": Hardness.ExtraSoft,
  [Hardness.ExtraHard.toLowerCase()]: Hardness.ExtraHard,
  [Hardness.Hard.toLowerCase()]: Hardness.Hard,
  [Hardness.Medium.toLowerCase()]: Hardness.Medium,
  [Hardness.Soft.toLowerCase()]: Hardness.Soft,
  [Hardness.ExtraSoft.toLowerCase()]: Hardness.ExtraSoft,
  خیلیسخت: Hardness.ExtraHard,
  سخت: Hardness.Hard,
  متوسط: Hardness.Medium,
  نرم: Hardness.Soft,
  خیلینرم: Hardness.ExtraSoft,
  extraehard: Hardness.ExtraHard,
  zacht: Hardness.Soft,
  extrazacht: Hardness.ExtraSoft,
};

type CurrentAdditionRef = {
  foot: VoiceFoot;
  footIndex: number;
  index: number;
  name: string;
  height: number;
  hardness: string;
};

function listCurrentAdditions(): CurrentAdditionRef[] {
  const design = useDesignStore.getState().insoleDesign;
  if (!design?.insoles) return [];
  const rows: CurrentAdditionRef[] = [];
  design.insoles.forEach((insole, footIndex) => {
    (insole?.additions || []).forEach((item, index) => {
      if (!item || item.status === AdditionNewStatus.removed) return;
      rows.push({
        foot: footIndex === 0 ? "right" : "left",
        footIndex,
        index,
        name: item.name || item.fileName,
        height: item.height?.value,
        hardness: item.hardness,
      });
    });
  });
  return rows;
}

function catalogNames(): string[] {
  const groups = useSelectedDesignStore.getState().additionListNamesFromServer || [];
  return groups.flatMap((group: { fileNames?: string[] }) => group.fileNames || []);
}

function resolveCatalogName(name: string): { match?: string; error?: string } {
  const names = catalogNames();
  if (names.length === 0) return { error: "Addition catalog is empty. Open a design first." };
  const compact = name.trim().toLowerCase().replace(/[\s_-]/g, "");
  const exact = names.find((item) => item.toLowerCase().replace(/[\s_-]/g, "") === compact);
  if (exact) return { match: exact };
  const partial = names.filter((item) => item.toLowerCase().replace(/[\s_-]/g, "").includes(compact) || compact.includes(item.toLowerCase().replace(/[\s_-]/g, "")));
  if (partial.length === 1) return { match: partial[0] };
  if (partial.length > 1) return { error: `Name "${name}" matches more than one addition: ${partial.join(", ")}.` };
  return { error: `Unknown addition "${name}". Available: ${names.join(", ")}.` };
}

function resolveHardnessValue(value: string): Hardness | undefined {
  const trimmed = value.trim();
  if ((Object.values(Hardness) as string[]).includes(trimmed)) return trimmed as Hardness;
  return HARDNESS_ALIASES[trimmed.toLowerCase()] ?? HARDNESS_ALIASES[trimmed.toLowerCase().replace(/[\s_-]/g, "")];
}

function findCurrentAddition(foot: VoiceFoot, name?: string, index?: number): CurrentAdditionRef | string {
  const rows = listCurrentAdditions().filter((row) => foot === "both" || row.foot === foot);
  if (typeof index === "number" && Number.isFinite(index) && index >= 0) {
    const byIndex = rows.find((row) => row.index === index) ?? listCurrentAdditions().find((row) => row.footIndex === (foot === "left" ? 1 : 0) && row.index === index);
    if (!byIndex) return `No addition at index ${index} on ${foot}.`;
    return byIndex;
  }
  if (!name?.trim()) return "Provide name or index of the addition.";
  const compact = name.trim().toLowerCase().replace(/[\s_-]/g, "");
  const matches = rows.filter((row) => row.name.toLowerCase().replace(/[\s_-]/g, "").includes(compact) || compact.includes(row.name.toLowerCase().replace(/[\s_-]/g, "")));
  if (matches.length === 0) return `No current addition named "${name}" on ${foot}.`;
  if (matches.length > 1) return `Multiple additions match "${name}". Use index from getAdditions: ${matches.map((row) => `${row.foot} index ${row.index} (${row.name})`).join("; ")}.`;
  return matches[0];
}

export function requestAdditionsState(): string {
  const current = listCurrentAdditions();
  const available = catalogNames();
  return JSON.stringify({ current, available });
}

function conditionHasSide(side: ConditionSide | string | undefined, which: "left" | "right"): boolean {
  const value = String(side || "").toLowerCase();
  return value === which || value === "both";
}

function hasPressureOrScan(): boolean {
  try {
    const design = useDesignStore.getState();
    return Boolean(design.insoleDesign?.pressureData)
      || Boolean(design.RondomImageData?.some((img) => img !== undefined))
      || Boolean(useSelectedDesignStore.getState().hasPressure)
      || Boolean(useSelectedDesignStore.getState().hasRondomImage);
  } catch {
    return false;
  }
}

const GUIDANCE_PADS = ["MP Pad 1", "Morton's Extention", "Pad_2", "MB 1-5 1"] as const;

function compactCurrentAdditions() {
  return listCurrentAdditions().map((row) => ({
    foot: row.foot,
    name: row.name,
    index: row.index,
  }));
}

function clipText(value: unknown, max = 240): string | undefined {
  const text = String(value ?? "").trim();
  if (!text) return undefined;
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";

function normalizeVoiceDigits(raw: string): string {
  return raw.replace(/[۰-۹٠-٩]/g, (ch) => {
    const persian = PERSIAN_DIGITS.indexOf(ch);
    if (persian >= 0) return String(persian);
    const arabic = ARABIC_DIGITS.indexOf(ch);
    return arabic >= 0 ? String(arabic) : ch;
  });
}

function parseVoiceNumber(value: string | number): number | undefined {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  const text = normalizeVoiceDigits(String(value)).trim().replace(",", ".");
  const match = text.match(/-?\d+(?:\.\d+)?/);
  if (!match) return undefined;
  const numeric = Number(match[0]);
  return Number.isFinite(numeric) ? numeric : undefined;
}

function isMetricMeasurement(): boolean {
  return useViewerStore.getState().measurementSystem !== MeasurementSystem.imperial;
}

function patientHeightDisplay(heightMm?: number): { value?: number; unit: "cm" | "inch" } {
  const unit = isMetricMeasurement() ? "cm" : "inch";
  if (heightMm == null || !Number.isFinite(heightMm)) return { unit };
  const viewer = convertMmToViewerUnit(heightMm);
  return { value: unit === "cm" ? viewer / 10 : viewer, unit };
}

function patientWeightDisplay(weightKg?: number): { value?: number; unit: "kg" | "lb" } {
  const unit = isMetricMeasurement() ? "kg" : "lb";
  if (weightKg == null || !Number.isFinite(weightKg)) return { unit };
  return { value: convertKgToViewerUnit(weightKg), unit };
}

function patientHeightToMm(value: string | number): number | undefined {
  const numeric = parseVoiceNumber(value);
  if (numeric === undefined) return undefined;
  const text = normalizeVoiceDigits(String(value)).toLowerCase();
  const mentionsCm = /\bcm\b|سانتی/.test(text);
  const mentionsMeter = /\b(m|meter|meters|metres|متر)\b/.test(text) && !mentionsCm;
  if (mentionsMeter) return convertUnitToDefault(numeric * 100, "cm");
  if (/\b(mm|میلی)\b/.test(text)) return numeric;
  if (/\b(in|inch|inches|اینچ)\b/.test(text)) return convertUnitToDefault(numeric, "inch");
  if (/\b(ft|foot|feet|فوت)\b/.test(text)) return convertUnitToDefault(numeric * 12, "inch");
  if (mentionsCm) return convertUnitToDefault(numeric, "cm");
  if (isMetricMeasurement()) {
    if (numeric >= 0.8 && numeric <= 2.8) return convertUnitToDefault(numeric * 100, "cm");
    if (numeric >= 400 && numeric <= 2800) return numeric;
    return convertUnitToDefault(numeric, "cm");
  }
  if (numeric >= 3 && numeric <= 8.5) return convertUnitToDefault(numeric * 12, "inch");
  return convertUnitToDefault(numeric, "inch");
}

function patientWeightToKg(value: string | number): number | undefined {
  const numeric = parseVoiceNumber(value);
  if (numeric === undefined) return undefined;
  const text = normalizeVoiceDigits(String(value)).toLowerCase();
  if (/\b(lb|lbs|pound|pounds|پوند)\b/.test(text)) return convertWeightUnitToDefault(numeric, "lb");
  if (/\b(kg|کیلو)\b/.test(text)) return convertWeightUnitToDefault(numeric, "kg");
  return convertWeightUnitToDefault(numeric, isMetricMeasurement() ? "kg" : "lb");
}

export function requestPatientState(): string {
  try {
    const patient = useDesignStore.getState().patient;
    const auto = useDesignStore.getState().insoleDesign?.autoProperties;
    const pc = patient?.patientConditions;
    const metatarsalgia = auto?.metatarsalgia ?? pc?.metatarsalgia;
    const halluxLimitus = auto?.halluxLimitus ?? pc?.halluxLimitus;
    const sesamoiditis = auto?.sesamoiditis ?? pc?.sesamoiditis;
    const heelSpur = pc?.heelSpur;
    const diabetic = pc?.diabetic;

    const flag = (side?: ConditionSide | string) => ({
      left: conditionHasSide(side, "left"),
      right: conditionHasSide(side, "right"),
    });

    const suggested: { foot: string; name: string; height: number; reason: string }[] = [];
    const pushSuggestion = (side: ConditionSide | string | undefined, name: string, height: number, reason: string) => {
      if (!side || String(side).toLowerCase() === "none") return;
      if (conditionHasSide(side, "left")) suggested.push({ foot: "left", name, height, reason });
      if (conditionHasSide(side, "right")) suggested.push({ foot: "right", name, height, reason });
    };
    pushSuggestion(metatarsalgia, "MP Pad 1", 6, "metatarsalgia");
    pushSuggestion(halluxLimitus, "Morton's Extention", 1, "hallux limitus");
    pushSuggestion(sesamoiditis, "Pad_2", 6, "sesamoiditis");
    if (auto && Number(auto.metatarSalBarLeft) >= 0) suggested.push({ foot: "left", name: "MB 1-5 1", height: 6, reason: "metatarsal bar" });
    if (auto && Number(auto.metatarSalBarRight) >= 0) suggested.push({ foot: "right", name: "MB 1-5 1", height: 6, reason: "metatarsal bar" });

    const pressureData = useDesignStore.getState().insoleDesign?.pressureData;
    const pressure = pressureData ? buildPressureReport(pressureData) : null;
    const hasPatient = isDefinedPatient(patient);
    const noteSuggestedTicks = inferConditionTicksFromNote(patient?.clinicalNote || "");
    const pressureSync = buildPressureSync(pressure, pc);
    const hasApplyable = hasApplyablePatientDesign(pc, auto, suggested);
    const workflow = buildDesignWorkflow({
      hasPatient,
      hasPressure: Boolean(pressure && (pressure as any).available !== false),
      pressureSyncNeeded: Boolean(pressureSync.needed),
      hasApplyable,
    });

    return JSON.stringify({
      hasPatient,
      hasPressure: Boolean(pressureData),
      patient: hasPatient
        ? {
            name: clipText(patient?.name, 80),
            age: patient?.age,
            weight: patientWeightDisplay(patient?.weight).value,
            weightUnit: patientWeightDisplay(patient?.weight).unit,
            height: patientHeightDisplay(patient?.height).value,
            heightUnit: patientHeightDisplay(patient?.height).unit,
            gender: patient?.gender,
            clinicalNote: clipText(patient?.clinicalNote, 240),
          }
        : null,
      conditions: {
        metatarsalgia: flag(metatarsalgia),
        halluxLimitus: flag(halluxLimitus),
        sesamoiditis: flag(sesamoiditis),
        heelSpur: flag(heelSpur),
        diabetic: flag(diabetic),
        footTypeLeft: pc?.insoleTemplateLeft ?? auto?.insoleTemplate,
        footTypeRight: pc?.insoleTemplateRight ?? auto?.insoleTemplate,
        archIndexLeft: pc?.archIndexLeft,
        archIndexRight: pc?.archIndexRight,
      },
      pressure,
      pressureSync,
      noteSuggestedTicks,
      workflow,
      automaticDesign: {
        shoeLastLeft: auto?.profileCurveLeft,
        shoeLastRight: auto?.profileCurveRight,
        offloadingCutout: Boolean(auto?.offLoadingCutout),
        cutoutMetLeft: [1, 2, 3, 4, 5].filter((n) => Number((auto as any)?.[`cutoutMet${n}Left`]) >= 0),
        cutoutMetRight: [1, 2, 3, 4, 5].filter((n) => Number((auto as any)?.[`cutoutMet${n}Right`]) >= 0),
        limbLengthDiscrepancy: Boolean(auto?.limbLengthDiscrepancy),
        shorterLimb: auto?.shorterLimb,
        discrepancyAmount: auto?.limbLengthDiscrepancy ? convertMmToViewerUnit(Number(auto?.DiscrepancyAmount || 0)) : 0,
        metatarsalgia: "ticks Automatic Design Metatarsalgia and adds MP Pad 1 at 6 mm",
        halluxLimitus: "ticks Automatic Design Hallux Limitus and adds Morton's Extention at 1 mm",
        sesamoiditis: "ticks Automatic Design Sesamoiditis and adds Pad_2 at 6 mm",
        footType: "sets Automatic Design Insole Template",
        metatarsalBar: "ticks Automatic Design Metatarsal Bar and adds MB 1-5 1 at 6 mm",
        shoeLast: "Automatic Design Shoe Last dropdown: Generic, Classic, Durea, Sneaker, Men, Women, Darco. Not the scene model (insole/sandal/high heel).",
        offloading: "Automatic Design Offloading Cutout, then Cutout Met 1-5 per foot",
        limbLength: "Automatic Design Limb Length Discrepancy, shorter limb, and amount",
      },
      suggestedAdditions: suggested,
      currentAdditions: compactCurrentAdditions(),
      proposeablePads: [...GUIDANCE_PADS],
    });
  } catch (error) {
    console.error("getPatientState failed", error);
    return JSON.stringify({ error: "Could not read patient state.", proposeablePads: [...GUIDANCE_PADS] });
  }
}

export async function addVoiceAddition(
  api: any,
  name: string,
  foot: string,
  height?: number | string,
  hardness?: string,
  silent?: string | boolean
): Promise<string> {
  void silent;
  const { match, error } = resolveCatalogName(name);
  if (!match) return error || `Unknown addition "${name}".`;
  const resolvedFoot = resolveFoot(foot);
  const feet = resolvedFoot === "left" ? [1] : resolvedFoot === "right" ? [0] : [0, 1];
  const hardnessValue = hardness ? resolveHardnessValue(hardness) : undefined;
  if (hardness && !hardnessValue) {
    return `Unknown hardness "${hardness}". Use Extra Hard, Hard, Medium, Soft, or Extra Soft.`;
  }
  const heightValue = height === undefined || height === "" ? undefined : Number(height);
  if (heightValue !== undefined && !Number.isFinite(heightValue)) {
    return `Height must be a number in millimeters. Received: "${height}"`;
  }

  document.dispatchEvent(new CustomEvent("setLoadingEnable"));
  const added: string[] = [];
  try {
    for (const footIndex of feet) {
      const addition = await addNewAddition(api, match, footIndex);
      if (!addition) return `Failed to add "${match}" to the ${footIndex === 0 ? "right" : "left"} foot.`;
      if (hardnessValue) addition.hardness = hardnessValue;
      if (heightValue !== undefined) addition.SetHeight(heightValue);
      useDesignStore.getState().pushAdditionToDesign(addition, footIndex);
      const index = (useDesignStore.getState().insoleDesign?.insoles[footIndex].additions.length || 1) - 1;
      if (heightValue !== undefined) {
        useDesignStore.setState({ additionHeightUpdated: { side: footIndex, index, value: heightValue } });
      }
      if (hardnessValue) {
        useDesignStore.setState({ additionHardnessUpdated: { side: footIndex, index, value: hardnessValue } });
      }
      added.push(`${footIndex === 0 ? "right" : "left"} index ${index}`);
    }
  } finally {
    document.dispatchEvent(new CustomEvent("setLoadingDisable"));
  }
  return `Added "${match}" on ${resolvedFoot}${heightValue !== undefined ? `, height ${heightValue} mm` : ""}${hardnessValue ? `, hardness ${hardnessValue}` : ""}. ${added.join("; ")}.`;
}

/** How far an addition slides when no distance is given. */
const ADDITION_MOVE_DEFAULT_MM = 3;

/** Parse a move distance into millimetres (viewer and gizmo work in mm on X/Z). */
function parseMoveDistanceMm(amount?: number | string): number {
  if (amount === undefined || amount === "") return ADDITION_MOVE_DEFAULT_MM;
  if (typeof amount === "number" && Number.isFinite(amount) && amount > 0) {
    return Math.max(1, Math.round(amount));
  }
  const s = String(amount).trim();
  const direct = Number(s);
  if (Number.isFinite(direct) && direct > 0) return Math.max(1, Math.round(direct));

  const mmMatch = s.match(/(\d+(?:\.\d+)?)\s*(?:mm|millimet(?:er|re)?s?)/i);
  if (mmMatch) return Math.max(1, Math.round(Number(mmMatch[1])));

  const inchFrac = s.match(/(\d+)\s*\/\s*(\d+)\s*(?:of\s+an?\s+)?in(?:ch(?:es)?)?/i);
  if (inchFrac) {
    const inches = Number(inchFrac[1]) / Number(inchFrac[2]);
    if (Number.isFinite(inches) && inches > 0) {
      return Math.max(1, Math.round(inches * 25.4));
    }
  }

  const inchMatch = s.match(/(\d+(?:\.\d+)?)\s*in(?:ch(?:es)?)?/i);
  if (inchMatch) return Math.max(1, Math.round(Number(inchMatch[1]) * 25.4));

  const anyNum = s.match(/(\d+(?:\.\d+)?)/);
  if (anyNum) return Math.max(1, Math.round(Number(anyNum[1])));

  return Number.NaN;
}

/**
 * Slide an addition across the insole.
 *
 * Goes through the same store signal and the same TryTranslateXZ primitive the
 * drag gizmo uses, so a spoken move and a dragged one behave identically — the
 * viewer refuses both if the addition would leave the insole.
 *
 * NOTE: the medial/lateral signs below are per-foot and have not been confirmed
 * against the rendered model; toe/heel run along Z and are unambiguous.
 */
export function moveVoiceAddition(
  foot: string,
  direction: string,
  amount?: number | string,
  name?: string,
  index?: number
): string {
  const resolvedFoot = resolveFoot(foot);
  if (resolvedFoot === "both") {
    return voiceText({
      en: "Which foot? Moving an addition works on one foot at a time.",
      nl: "Welke voet? Een toevoeging verplaatsen gaat per voet.",
      fa: "کدام پا؟ جابه‌جایی افزودنی هر بار روی یک پا انجام می‌شود.",
      zh: "哪只脚？附加件移动一次只能作用于一只脚。",
    });
  }

  const found = findCurrentAddition(resolvedFoot, name, index);
  if (typeof found === "string") return found;

  const mm = parseMoveDistanceMm(amount);
  if (!Number.isFinite(mm) || mm <= 0) {
    return `Distance must be a positive number of millimetres. Received: "${amount}"`;
  }

  const compact = direction.trim().toLowerCase().replace(/[\s_-]/g, "");
  const toe = ["toe", "toes", "forward", "front", "پنجه", "جلو", "tenen", "voor", "足趾", "向前"];
  const heel = ["heel", "back", "backward", "rear", "پاشنه", "عقب", "hiel", "achter", "足跟", "向后"];
  const medial = ["medial", "inside", "inward", "داخل", "مدیال", "binnen", "mediaal", "内侧"];
  const lateral = ["lateral", "outside", "outward", "خارج", "لترال", "buiten", "外侧"];

  let dx = 0;
  let dz = 0;
  // Towards the toes is positive dz. The signs were the other way round, which
  // made "move it forward" slide the pad back towards the heel and vice versa;
  // the direction was confirmed on the real model.
  if (toe.includes(compact)) dz = mm;
  else if (heel.includes(compact)) dz = -mm;
  else if (medial.includes(compact)) dx = found.footIndex === 0 ? mm : -mm;
  else if (lateral.includes(compact)) dx = found.footIndex === 0 ? -mm : mm;
  else {
    return `Unknown direction "${direction}". Use toe, heel, medial, or lateral.`;
  }

  useDesignStore.setState({
    additionMoved: { side: found.footIndex, index: found.index, dx, dz },
  });

  return voiceText({
    en: `Moved ${found.name} on the ${found.foot} foot ${mm} millimetres ${compact}.`,
    nl: `${found.name} op de ${found.foot} voet ${mm} millimeter verplaatst.`,
    fa: `${found.name} روی پای ${found.foot} به اندازه ${mm} میلی‌متر جابه‌جا شد.`,
    zh: `已将${found.foot}脚的${found.name}移动${mm}毫米。`,
  });
}

export function removeVoiceAddition(foot: string, name?: string, index?: number, silent?: string | boolean): string {
  void silent;
  const resolvedFoot = resolveFoot(foot);
  const feet: VoiceFoot[] = resolvedFoot === "both" ? ["right", "left"] : [resolvedFoot];
  const removed: string[] = [];
  for (const side of feet) {
    const found = findCurrentAddition(side, name, index);
    if (typeof found === "string") return found;
    useDesignStore.setState({ additionRemoved: [{ side: found.footIndex, index: found.index, isAdditionID: false }] });
    removed.push(`${found.foot} ${found.name} (index ${found.index})`);
  }
  return `Removed ${removed.join("; ")}.`;
}

export function setVoiceAdditionParam(
  foot: string,
  param: string,
  value: string | number,
  name?: string,
  index?: number,
  silent?: string | boolean
): string {
  const resolvedFoot = resolveFoot(foot);
  const compact = param.trim().toLowerCase().replace(/[\s_-]/g, "");
  const feet: VoiceFoot[] = resolvedFoot === "both" ? ["right", "left"] : [resolvedFoot];
  void silent;
  const results: string[] = [];
  for (const side of feet) {
    const found = findCurrentAddition(side, name, index);
    if (typeof found === "string") return found;
    if (compact === "height") {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return `Height must be a number in millimeters. Received: "${value}"`;
      useDesignStore.setState({ additionHeightUpdated: { side: found.footIndex, index: found.index, value: numeric } });
      useViewerStore.getState().setSelectedAdditionFromViewer(found.footIndex, found.index);
      results.push(`${found.name} on ${found.foot} height ${numeric} mm`);
    } else if (compact === "hardness") {
      const hardnessValue = resolveHardnessValue(String(value));
      if (!hardnessValue) return `Unknown hardness "${value}". Use Extra Hard, Hard, Medium, Soft, or Extra Soft.`;
      useDesignStore.setState({ additionHardnessUpdated: { side: found.footIndex, index: found.index, value: hardnessValue } });
      results.push(`${found.name} on ${found.foot} hardness ${hardnessValue}`);
    } else {
      return `Unknown addition param "${param}". Use height or hardness.`;
    }
  }
  return `Updated ${results.join("; ")}.`;
}

function showLoadTemplatePanel(): Promise<void> {
  return new Promise((resolve) => {
    closeOpenDialogs("loadTemplate");
    document.dispatchEvent(new CustomEvent(VOICE_OPEN_TEMPLATE_EVENT));
    window.setTimeout(resolve, 50);
  });
}

function showDatabasePanel(): Promise<void> {
  return new Promise((resolve) => {
    closeOpenDialogs("database");
    document.dispatchEvent(new CustomEvent(VOICE_OPEN_DATABASE_EVENT));
    window.setTimeout(resolve, 150);
  });
}

export function openDatabasePanel(): string {
  void showDatabasePanel();
  return voiceText({
    en: "Load Design is open.",
    nl: "Ontwerp laden is open.",
    fa: "پنل لود دیزاین باز شد.",
    zh: "已打开加载设计面板。",
  });
}

function showPatientPanel(): Promise<void> {
  return new Promise((resolve) => {
    closeOpenDialogs("patient");
    document.dispatchEvent(new CustomEvent(VOICE_OPEN_PATIENT_EVENT, { detail: { tab: "info" } }));
    window.setTimeout(resolve, 50);
  });
}

export function openPatientPanel(create?: string | boolean, close?: string | boolean): string {
  if (isTruthyFlag(close) || isTruthyFlag(create)) {
    document.dispatchEvent(new CustomEvent(VOICE_OPEN_PANEL_EVENT, { detail: { close: true } }));
    document.dispatchEvent(new Event("toolbox:close"));
    document.dispatchEvent(new CustomEvent(VOICE_CLOSE_PANELS_EVENT));
    document.dispatchEvent(new CustomEvent(VOICE_OPEN_PATIENT_EVENT, { detail: { close: true, closeCreate: true } }));
    if (isTruthyFlag(create)) {
      return "Do not open Create Patient or Patient Info. Register the patient silently with setPatientParam for name, age, weight, height, and gender as they speak. Height is the number they said in centimeters (metric) or inches (imperial), for example 160. Do not open any patient panel unless they explicitly ask to see Patient Info.";
    }
    return voiceText({
      en: "Patient Info is closed.",
      nl: "Patiëntinfo is gesloten.",
      fa: "اطلاعات بیمار بسته شد.",
      zh: "已关闭患者信息。",
    });
  }
  void showPatientPanel();
  return voiceText({
    en: "Patient Info is open.",
    nl: "Patiëntinfo is open.",
    fa: "اطلاعات بیمار باز شد.",
    zh: "已打开患者信息。",
  });
}

export async function savePatientPanel(api: any): Promise<string> {
  suppressTemplateUntil = Date.now() + 5000;
  const designId = useSelectedDesignStore.getState().designId;
  if (!designId || designId < 1) return "No design is open.";
  const patient = useDesignStore.getState().patient || new Patient();
  const ok = await SavePatientOnServer(api, patient, designId);
  if (!ok) return "Could not save the patient.";
  document.dispatchEvent(new CustomEvent(VOICE_PATIENT_UPDATED_EVENT, { detail: { saved: true } }));
  return "Patient saved. Continue the design workflow. If pressure exists, call setPatientParam param=syncFromPressure when pressureSync.needed is true, then openPanel automatic and SPEAK the proposal. Do not apply the insole yet. After they confirm, call setPatientParam with apply=on. If there is no pressure exam, close Patient Info and only give suggestions that do not need pressure. Do not open Save as Template.";
}

export type VoiceDatabasePage = number | "next" | "prev";

export type VoiceDatabaseQueryResult = {
  search: string;
  page: number;
  totalPages: number;
  totalRecords: number;
  designs: { id: number; name: string }[];
  error?: string;
};

export type VoiceDatabaseQueryDetail = {
  search?: string;
  page?: VoiceDatabasePage;
  resolve?: (result: VoiceDatabaseQueryResult) => void;
};

export type VoiceDatabaseOpenResult = {
  ok: boolean;
  id?: number;
  name?: string;
  error?: string;
};

export type VoiceDatabaseOpenDetail = {
  id?: number;
  name?: string;
  resolve?: (result: VoiceDatabaseOpenResult) => void;
};

const CLEAR_DATABASE_SEARCH = new Set([
  "",
  "*",
  "all",
  "clear",
  "reset",
  "همه",
  "پاک",
  "پاک کن",
  "alles",
  "wissen",
  "leeg",
  "全部",
  "清空",
]);

export function isClearDatabaseSearch(value: string): boolean {
  return CLEAR_DATABASE_SEARCH.has(value.trim().toLowerCase());
}

export function parseDatabasePage(page?: number | string): VoiceDatabasePage | undefined {
  if (page === undefined || page === null || page === "") return undefined;
  const raw = String(page).trim().toLowerCase();
  if (["next", "forward", "بعدی", "بعد", "volgende", "volgend", "下一页", "下页"].includes(raw)) return "next";
  if (["prev", "previous", "back", "قبلی", "قبل", "vorige", "vorig", "上一页", "上页"].includes(raw)) return "prev";
  const n = Number(raw.replace(/[^\d.-]/g, ""));
  if (Number.isFinite(n) && n >= 1) return Math.floor(n);
  return undefined;
}

function mapDesignRows(rows: any[]): { id: number; name: string }[] {
  return rows.map((item: any) => ({
    id: Number(item.designId ?? item.DesignId ?? item.id),
    name: String(item.name ?? item.Name ?? ""),
  })).filter((item) => Number.isFinite(item.id) && item.id > 0);
}

function formatDatabaseList(result: VoiceDatabaseQueryResult): string {
  const page = spokenNumber(result.page, 0);
  const totalPages = spokenNumber(Math.max(1, result.totalPages), 0);
  const total = spokenNumber(result.totalRecords, 0);
  const query = result.search.trim();

  if (result.error && !result.designs.length) {
    if (query) {
      return voiceText({
        en: `No designs match "${query}".`,
        nl: `Geen ontwerpen voor "${query}".`,
        fa: `دیزاینی با «${query}» پیدا نشد.`,
        zh: `没有匹配“${query}”的设计。`,
      });
    }
    return voiceText({
      en: "No designs found.",
      nl: "Geen ontwerpen gevonden.",
      fa: "دیزاینی پیدا نشد.",
      zh: "没有找到设计。",
    });
  }

  if (!query) {
    return voiceText({
      en: `All designs are on screen. Page ${page} of ${totalPages}.`,
      nl: `Alle ontwerpen staan op het scherm. Pagina ${page} van ${totalPages}.`,
      fa: `همه دیزاین‌ها روی صفحه است. صفحه ${page} از ${totalPages}.`,
      zh: `全部设计已显示在屏幕上。第${page}页，共${totalPages}页。`,
    });
  }

  const names = result.designs
    .map((item) => `${spokenNumber(item.id, 0)} ${item.name || ""}`.trim())
    .join(voiceText({ en: "; ", nl: "; ", fa: "؛ ", zh: "；" }));
  const header = voiceText({
    en: `Search "${query}", page ${page} of ${totalPages}. ${total} designs.`,
    nl: `Zoek "${query}", pagina ${page} van ${totalPages}. ${total} ontwerpen.`,
    fa: `جستجوی «${query}»، صفحه ${page} از ${totalPages}. ${total} دیزاین.`,
    zh: `搜索“${query}”，第${page}页，共${totalPages}页。共${total}个设计。`,
  });
  if (!names) return header;
  return `${header} ${names}.`;
}

async function queryDatabasePanel(search?: string, page?: VoiceDatabasePage): Promise<VoiceDatabaseQueryResult> {
  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => {
      resolve({
        search: search?.trim() || "",
        page: typeof page === "number" ? page : 1,
        totalPages: 0,
        totalRecords: 0,
        designs: [],
        error: "panel-timeout",
      });
    }, 2500);
    document.dispatchEvent(
      new CustomEvent<VoiceDatabaseQueryDetail>(VOICE_DATABASE_QUERY_EVENT, {
        detail: {
          search,
          page,
          resolve: (result) => {
            window.clearTimeout(timeout);
            resolve(result);
          },
        },
      })
    );
  });
}

async function fetchDesignPage(api: any, search: string, page: number): Promise<VoiceDatabaseQueryResult> {
  const res = await fetchUserDesigns(api, new PaginatedSearchRequestDto({
    pageNumber: page,
    pageSize: 10,
    searchTerm: search,
  }));
  const rows = (res as any)?.data || (res as any)?.Data || [];
  return {
    search,
    page: Number((res as any)?.pageNumber ?? (res as any)?.PageNumber ?? page) || page,
    totalPages: Number((res as any)?.totalPages ?? (res as any)?.TotalPages ?? 1) || 1,
    totalRecords: Number((res as any)?.totalRecords ?? (res as any)?.TotalRecords ?? rows.length) || 0,
    designs: mapDesignRows(rows),
    error: rows.length ? undefined : "empty",
  };
}

export async function listDesigns(api: any, search?: string, page?: number | string): Promise<string> {
  await showDatabasePanel();
  const parsedPage = parseDatabasePage(page);
  const panelResult = await queryDatabasePanel(search, parsedPage);
  if (panelResult.error !== "panel-timeout") return formatDatabaseList(panelResult);

  const term = search === undefined || search === null
    ? ""
    : isClearDatabaseSearch(search)
      ? ""
      : search.trim();
  const targetPage = typeof parsedPage === "number" ? parsedPage : 1;
  const fallback = await fetchDesignPage(api, term, targetPage);
  return formatDatabaseList(fallback);
}

async function openDatabaseDesign(id?: number, name?: string): Promise<VoiceDatabaseOpenResult> {
  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => {
      resolve({ ok: false, error: "panel-timeout" });
    }, 20000);
    document.dispatchEvent(
      new CustomEvent<VoiceDatabaseOpenDetail>(VOICE_DATABASE_OPEN_EVENT, {
        detail: {
          id,
          name,
          resolve: (result) => {
            window.clearTimeout(timeout);
            resolve(result);
          },
        },
      })
    );
  });
}

function applyDesignOpenFlags(row: { hasPressure?: boolean; HasPressure?: boolean; hasRondom?: boolean; HasRondom?: boolean }) {
  useSelectedDesignStore.setState({
    hasPressure: Boolean(row.hasPressure ?? row.HasPressure),
    hasRondomImage: Boolean(row.hasRondom ?? row.HasRondom),
  });
}

async function resolveDesignRow(api: any, id?: number, name?: string): Promise<{ id: number; name: string; row: any } | string> {
  if (id !== undefined && Number.isFinite(id)) {
    const res = await fetchUserDesigns(api, new PaginatedSearchRequestDto({
      pageNumber: 1,
      pageSize: 10,
      searchTerm: String(id),
    }));
    const rows = ((res as any)?.data || (res as any)?.Data || []) as any[];
    const match = rows.find((item) => Number(item.designId ?? item.DesignId) === id);
    if (!match) return `No design with id ${id}.`;
    return { id, name: String(match.name ?? match.Name ?? id), row: match };
  }
  if (!name?.trim()) return "Provide a design id or name.";
  const res = await fetchUserDesigns(api, new PaginatedSearchRequestDto({
    pageNumber: 1,
    pageSize: 20,
    searchTerm: name.trim(),
  }));
  const rows = ((res as any)?.data || (res as any)?.Data || []) as any[];
  const compact = name.trim().toLowerCase();
  const matches = rows.filter((item) => String(item.name || item.Name || "").toLowerCase().includes(compact));
  if (!matches.length) return `No design named "${name}".`;
  if (matches.length > 1) {
    return `Multiple designs match "${name}": ${matches.map((item) => `${item.name || item.Name} (id ${item.designId ?? item.DesignId})`).join("; ")}. Call loadDesign with id.`;
  }
  return {
    id: Number(matches[0].designId ?? matches[0].DesignId),
    name: String(matches[0].name || matches[0].Name || name),
    row: matches[0],
  };
}

export async function loadDesignByQuery(api: any, id?: number | string, name?: string): Promise<string> {
  await showDatabasePanel();
  const parsedId = id !== undefined && id !== "" && Number.isFinite(Number(id)) ? Number(id) : undefined;
  const panelResult = await openDatabaseDesign(parsedId, name);
  if (panelResult.ok) {
    return `Loading design "${panelResult.name || panelResult.id}" (id ${panelResult.id}) into the scene.`;
  }
  if (panelResult.error && panelResult.error !== "panel-timeout") return panelResult.error;

  const resolved = await resolveDesignRow(api, parsedId, name);
  if (typeof resolved === "string") return resolved;
  applyDesignOpenFlags(resolved.row);
  await scheduleAutosave("Current design", { now: true } as any);
  useSelectedDesignStore.getState().setDesignId(resolved.id);
  localStorage.setItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN, JSON.stringify({
    designId: resolved.id,
    designPageNumber: 1,
  }));
  return `Loading design "${resolved.name}" (id ${resolved.id}) into the scene.`;
}

export async function createNewDesign(api: any, name?: string, openDatabase = true): Promise<string> {
  if (openDatabase) await showDatabasePanel();
  if (useSelectedDesignStore.getState().designId > 0) {
    await scheduleAutosave("Current design", { now: true } as any);
  }
  const templateName = backendInsoleTypeMap[InsoleType.Standard];
  const designId = await getNewDesign(api, templateName, name);
  if (!designId || designId < 1) return "Could not create a new design.";
  useSelectedDesignStore.setState({
    designId,
    designUpdated: true,
    hasPressure: false,
    hasRondomImage: false,
  });
  useViewerStore.setState({ newDesign: true });
  localStorage.setItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN, JSON.stringify({ designId, designPageNumber: 1 }));
  markDesignLoadPendingClose();
  return `Created new design${name?.trim() ? ` "${name.trim()}"` : ""} (id ${designId}). It is loading into the scene.`;
}

let pendingDeleteDesign: { id: number; name: string } | null = null;

async function resolveDesignForDelete(
  api: any,
  id?: number | string,
  name?: string
): Promise<{ id: number; name: string } | string> {
  if (id !== undefined && id !== "" && Number.isFinite(Number(id))) {
    const designId = Number(id);
    const res = await fetchUserDesigns(api, new PaginatedSearchRequestDto({
      pageNumber: 1,
      pageSize: 20,
      searchTerm: String(designId),
    }));
    const rows = ((res as any)?.data || (res as any)?.Data || []) as any[];
    const match = rows.find((item) => Number(item.designId ?? item.DesignId) === designId);
    return { id: designId, name: String(match?.name ?? match?.Name ?? designId) };
  }
  if (!name?.trim()) return "Provide a design id or name.";
  const res = await fetchUserDesigns(api, new PaginatedSearchRequestDto({
    pageNumber: 1,
    pageSize: 20,
    searchTerm: name.trim(),
  }));
  const rows = ((res as any)?.data || (res as any)?.Data || []) as any[];
  const compact = name.trim().toLowerCase();
  const matches = rows.filter((item) => String(item.name || item.Name || "").toLowerCase().includes(compact));
  if (!matches.length) return `No design named "${name}".`;
  if (matches.length > 1) {
    return `Multiple designs match "${name}": ${matches.map((item) => `${item.name || item.Name} (id ${item.designId ?? item.DesignId})`).join("; ")}. Ask the user to pick an id, then call deleteDesign with that id.`;
  }
  return {
    id: Number(matches[0].designId ?? matches[0].DesignId),
    name: String(matches[0].name || matches[0].Name || name),
  };
}

export async function deleteDesignByQuery(
  api: any,
  id?: number | string,
  name?: string,
  confirm?: string | boolean
): Promise<string> {
  await showDatabasePanel();
  const hasTarget = (id !== undefined && id !== "") || Boolean(name?.trim());
  if (hasTarget) {
    const resolved = await resolveDesignForDelete(api, id, name);
    if (typeof resolved === "string") return resolved;
    pendingDeleteDesign = resolved;
  }
  if (!pendingDeleteDesign) return "Provide a design id or name to delete.";
  if (!wantsSave(confirm)) {
    return `Found design "${pendingDeleteDesign.name}" (id ${pendingDeleteDesign.id}). Do not delete yet. Wait for the user to confirm. Then call deleteDesign with confirm=on and this id.`;
  }
  const target = pendingDeleteDesign;
  const ok = await removeDesign(api, target.id);
  if (!ok) return `Could not delete design "${target.name}" (id ${target.id}).`;
  const wasOpen = useSelectedDesignStore.getState().designId === target.id;
  if (wasOpen) {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN);
  }
  document.dispatchEvent(new CustomEvent(VOICE_DESIGN_DELETED_EVENT, { detail: { id: target.id, wasOpen } }));
  pendingDeleteDesign = null;
  return `Deleted design "${target.name}" (id ${target.id}) from the database.`;
}

const PATIENT_DEMO_PARAMS = ["name", "age", "weight", "height", "gender", "clinicalNote"] as const;
const PATIENT_CONDITION_PARAMS = ["metatarsalgia", "halluxLimitus", "sesamoiditis", "heelSpur", "diabetic"] as const;
const FOOT_TYPE_ALIASES: Record<string, InsoleType> = {
  standard: InsoleType.Standard,
  plantarfasciitis: InsoleType.PlantarFasciitis,
  pesplanus: InsoleType.PesPlanusMild,
  pesplanusmild: InsoleType.PesPlanusMild,
  pesplanusmoderate: InsoleType.PesPlanusModerate,
  pesplanussevere: InsoleType.PesPlanusSevere,
  pesplanusverysevere: InsoleType.PesPlanusVerySevere,
  pescavus: InsoleType.PesCavus,
  flat: InsoleType.PesPlanusMild,
  flatfoot: InsoleType.PesPlanusMild,
  higharch: InsoleType.PesCavus,
  استاندارد: InsoleType.Standard,
  فاشئیت: InsoleType.PlantarFasciitis,
  فاشیایکفپا: InsoleType.PlantarFasciitis,
  کفپایصاف: InsoleType.PesPlanusMild,
  کفپایصافخفیف: InsoleType.PesPlanusMild,
  کفپایصافمتوسط: InsoleType.PesPlanusModerate,
  کفپایصافشدید: InsoleType.PesPlanusSevere,
  کفپایگود: InsoleType.PesCavus,
  قوسزیاد: InsoleType.PesCavus,
  platvoet: InsoleType.PesPlanusMild,
  holvoet: InsoleType.PesCavus,
  standaard: InsoleType.Standard,
  peesplaatontsteking: InsoleType.PlantarFasciitis,
  标准: InsoleType.Standard,
  足底筋膜炎: InsoleType.PlantarFasciitis,
  扁平足: InsoleType.PesPlanusMild,
  轻度扁平足: InsoleType.PesPlanusMild,
  中度扁平足: InsoleType.PesPlanusModerate,
  重度扁平足: InsoleType.PesPlanusSevere,
  高弓足: InsoleType.PesCavus,
};

function compactFootToken(value: unknown): string {
  return String(value ?? "").toLowerCase().replace(/[\s_-]/g, "");
}

function sameFootType(a: unknown, b: unknown): boolean {
  const left = compactFootToken(a);
  const right = compactFootToken(b);
  if (!left || !right) return false;
  if (left === right) return true;
  const mappedLeft = compactFootToken(FOOT_TYPE_ALIASES[left] ?? left);
  const mappedRight = compactFootToken(FOOT_TYPE_ALIASES[right] ?? right);
  return mappedLeft === mappedRight;
}

function isUnsetFootType(value: unknown): boolean {
  const compact = compactFootToken(value);
  return !compact || compact === "standard" || compact === "standaard";
}

function isDefinedPatient(patient?: Patient | null): boolean {
  if (!patient) return false;
  if (Number(patient.patientId) > 0) return true;
  return Boolean(String(patient.name || "").trim());
}

function flagIsOn(side?: ConditionSide | string): boolean {
  const value = String(side ?? "").toLowerCase();
  return Boolean(value) && value !== "none" && value !== "false" && value !== "0";
}

function hasApplyablePatientDesign(
  pc: PatientConsditions | undefined,
  auto: any,
  suggested: { foot: string }[]
): boolean {
  if (suggested.length > 0) return true;
  if (!pc && !auto) return false;
  if (!isUnsetFootType(pc?.insoleTemplateLeft) || !isUnsetFootType(pc?.insoleTemplateRight) || !isUnsetFootType(auto?.insoleTemplate)) return true;
  if (Number(pc?.archIndexLeft) >= 0 || Number(pc?.archIndexRight) >= 0) return true;
  return ["metatarsalgia", "halluxLimitus", "sesamoiditis", "heelSpur", "diabetic"].some((key) =>
    flagIsOn((pc as any)?.[key]) || flagIsOn(auto?.[key])
  );
}

function buildPressureSync(pressure: Record<string, unknown> | null, pc?: PatientConsditions) {
  if (!pressure || pressure.available === false) {
    return { needed: false, mismatches: [] as string[], derived: null as null };
  }
  const derived: Record<string, { footType?: string; archIndex?: number; currentFootType?: unknown; currentArchIndex?: unknown }> = {};
  const mismatches: string[] = [];
  for (const side of ["left", "right"] as const) {
    const row = pressure[side] as { footType?: string; archIndex?: number } | null | undefined;
    if (!row) continue;
    const currentFootType = side === "left" ? pc?.insoleTemplateLeft : pc?.insoleTemplateRight;
    const currentArchIndex = side === "left" ? pc?.archIndexLeft : pc?.archIndexRight;
    derived[side] = {
      footType: row.footType,
      archIndex: row.archIndex,
      currentFootType,
      currentArchIndex,
    };
    const typeDiffers = Boolean(row.footType) && (isUnsetFootType(currentFootType) || !sameFootType(currentFootType, row.footType));
    const archDiffers = Number.isFinite(Number(row.archIndex)) && Number(currentArchIndex) !== Number(row.archIndex);
    if (typeDiffers || archDiffers) mismatches.push(side);
  }
  return { needed: mismatches.length > 0, mismatches, derived };
}

function buildDesignWorkflow(args: {
  hasPatient: boolean;
  hasPressure: boolean;
  pressureSyncNeeded: boolean;
  hasApplyable: boolean;
}) {
  if (!args.hasPatient) {
    return {
      next: "createPatient",
      openPatient: false,
      openAutomatic: false,
      askPressure: false,
      instruction: "No patient yet. Do not open Patient Info or Create Patient. Register silently with setPatientParam for name, age, weight, height, and gender as they speak. Height is the spoken number in centimeters (metric) or inches (imperial), for example 160. Then ask about special conditions or a clinical note, tick matching Patient Info options, and save.",
    };
  }
  if (args.hasPressure) {
    return {
      next: args.pressureSyncNeeded ? "syncThenProposeAutomatic" : "proposeAutomatic",
      openPatient: false,
      openAutomatic: true,
      askPressure: false,
      instruction: args.pressureSyncNeeded
        ? "Patient exists and pressure exists. Do not open Patient Info. Sync Patient Info only with setPatientParam param=syncFromPressure if needed. Then openPanel automatic. SPEAK the proposal and ASK if they agree. Do not call footType, metatarsalgia, halluxLimitus, sesamoiditis, or metatarsalBar with apply=on in this turn. After they confirm, call those with apply=on."
        : "Patient exists and pressure exists. Do not open Patient Info. Open Automatic Design, SPEAK the proposal, and ASK if they agree. Do not apply the insole in this turn. After they confirm, call setPatientParam with apply=on.",
    };
  }
  if (args.hasApplyable) {
    return {
      next: "proposeFromPatientOnly",
      openPatient: false,
      openAutomatic: true,
      askPressure: false,
      instruction: "Patient exists but there is no pressure exam. Propose only from patient fields. Open Automatic Design if those fields can be applied. SPEAK the proposal and ASK. Do not apply until they confirm. Then call setPatientParam with apply=on. Say a pressure exam is still needed for a full analysis.",
    };
  }
  return {
    next: "askPressureExam",
    openPatient: false,
    openAutomatic: false,
    askPressure: true,
    instruction: "Patient exists but analysis needs a pressure exam. Tell the user to capture pressure. Do not open Automatic Design.",
  };
}

type InferredNoteTick = { param: "diabetic" | "heelSpur" | "metatarsalgia" | "halluxLimitus" | "sesamoiditis" | "footType"; value: string };

function inferConditionTicksFromNote(note: string): InferredNoteTick[] {
  const text = note.toLowerCase();
  if (!text.trim()) return [];
  const ticks: InferredNoteTick[] = [];
  const has = (...needles: string[]) => needles.some((needle) => text.includes(needle));
  if (has("diabet", "دیابت", "دیابتی", "suikerziekte")) ticks.push({ param: "diabetic", value: "on" });
  if (has("heel spur", "heelspur", "خار پاشنه", "hielspoor", "calcaneal spur")) ticks.push({ param: "heelSpur", value: "on" });
  if (has("metatarsalgia", "متاتارسالژیا", "درد پنجه", "درد کف جلوی پا", "voorvoetpijn")) ticks.push({ param: "metatarsalgia", value: "on" });
  if (has("hallux limitus", "hallux rigidus", "هالوکس", "محدودیت شست", "halluxlimitus")) ticks.push({ param: "halluxLimitus", value: "on" });
  if (has("sesamoid", "سزاموئید", "sesamoiditis")) ticks.push({ param: "sesamoiditis", value: "on" });
  if (has("plantar fasci", "فاشئیت", "فاشیای کف", "peesplaatontsteking", "heel pain", "درد پاشنه", "first-step", "اولین قدم")) {
    ticks.push({ param: "footType", value: InsoleType.PlantarFasciitis });
  } else if (has("pes cavus", "high arch", "کف پای گود", "قوس زیاد", "holvoet", "高弓")) {
    ticks.push({ param: "footType", value: InsoleType.PesCavus });
  } else if (has("pes planus", "flat foot", "flatfoot", "کف پای صاف", "platvoet", "扁平")) {
    ticks.push({ param: "footType", value: InsoleType.PesPlanusMild });
  }
  return ticks;
}

function toBackendInsoleType(value?: string | InsoleType | null): string {
  if (!value) return "Standard";
  if (backendInsoleTypeMap[value as InsoleType]) return backendInsoleTypeMap[value as InsoleType];
  const compact = String(value).toLowerCase().replace(/[\s_-]/g, "");
  const alias = FOOT_TYPE_ALIASES[compact];
  if (alias) return backendInsoleTypeMap[alias];
  const match = Object.entries(InsoleType).find(([key, label]) =>
    key.toLowerCase() === compact || String(label).toLowerCase().replace(/[\s_-]/g, "") === compact
  );
  return match ? match[0] : "Standard";
}

function conditionsPayload(conditions: PatientConsditions) {
  const json = conditions.toJson();
  return {
    ...json,
    insoleTemplateLeft: toBackendInsoleType(json.insoleTemplateLeft),
    insoleTemplateRight: toBackendInsoleType(json.insoleTemplateRight),
  };
}

function mergeConditionSide(current: ConditionSide | string | undefined, foot: VoiceFoot, on: boolean): ConditionSide {
  const leftOn = conditionHasSide(current, "left");
  const rightOn = conditionHasSide(current, "right");
  const nextLeft = foot === "right" ? leftOn : on;
  const nextRight = foot === "left" ? rightOn : on;
  if (nextLeft && nextRight) return ConditionSide.both;
  if (nextLeft) return ConditionSide.left;
  if (nextRight) return ConditionSide.right;
  return ConditionSide.none;
}

function applyAutomaticDesignCondition(
  condition: string,
  foot: VoiceFoot,
  on: boolean,
  value?: string
): Promise<string> {
  return new Promise((resolve) => {
    let settled = false;
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const finish = (payload: string) => {
      if (settled) return;
      settled = true;
      document.removeEventListener(VOICE_AUTOMATIC_CONDITION_DONE_EVENT, onDone as EventListener);
      resolve(payload);
    };
    const onDone = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      if (detail.requestId && detail.requestId !== requestId) return;
      if (detail.ok === false) {
        finish(String(detail.error || "Could not apply Automatic Design."));
        return;
      }
      finish("ok");
    };
    document.addEventListener(VOICE_AUTOMATIC_CONDITION_DONE_EVENT, onDone as EventListener);
    document.dispatchEvent(new CustomEvent(VOICE_AUTOMATIC_CONDITION_EVENT, {
      detail: { condition, foot, on, value, requestId },
    }));
    window.setTimeout(() => finish("ok"), 8000);
  });
}

async function applyInferredNoteTicks(
  api: any,
  designId: number,
  note: string,
  foot: VoiceFoot
): Promise<string[]> {
  void designId;
  const inferred = inferConditionTicksFromNote(note);
  const applied: string[] = [];
  for (const tick of inferred) {
    const current = useDesignStore.getState().patient || new Patient();
    if (tick.param === "footType") {
      const conditions = current.patientConditions;
      const alreadyLeft = !isUnsetFootType(conditions.insoleTemplateLeft);
      const alreadyRight = !isUnsetFootType(conditions.insoleTemplateRight);
      if (alreadyLeft && alreadyRight) continue;
      const result = await setPatientParam(api, "footType", tick.value, foot);
      if (!result.startsWith("Unknown")) applied.push(`footType ${tick.value}`);
      continue;
    }
    if (flagIsOn((current.patientConditions as any)[tick.param])) continue;
    const result = await setPatientParam(api, tick.param, tick.value, foot);
    if (!result.startsWith("Unknown") && !result.startsWith("Use on")) applied.push(tick.param);
  }
  return applied;
}

async function syncPatientFromPressure(api: any, designId: number, foot: VoiceFoot): Promise<string> {
  void designId;
  const pressureData = useDesignStore.getState().insoleDesign?.pressureData;
  const pressure = pressureData ? buildPressureReport(pressureData) : null;
  if (!pressure || pressure.available === false) return "No pressure exam to sync from. Ask the user to capture pressure data.";
  const patient = useDesignStore.getState().patient || new Patient();
  const sync = buildPressureSync(pressure, patient.patientConditions);
  if (!sync.needed) return "Patient foot type and arch index already match the pressure exam.";
  const sides: VoiceFoot[] = foot === "both" ? ["left", "right"] : [foot];
  const changed: string[] = [];
  for (const side of sides) {
    const row = sync.derived?.[side];
    if (!row) continue;
    if (row.footType) {
      await setPatientParam(api, "footType", row.footType, side);
      changed.push(`${side} footType ${row.footType}`);
    }
    if (Number.isFinite(Number(row.archIndex))) {
      await setPatientParam(api, "archIndex", Number(row.archIndex), side);
      changed.push(`${side} archIndex ${row.archIndex}`);
    }
  }
  return changed.length
    ? `Synced Patient Info only: ${changed.join("; ")}. The insole was NOT changed. Open Automatic Design, speak the proposal, and wait. After they confirm, call setPatientParam again with apply=on.`
    : "No pressure-derived foot type or arch index to copy.";
}

export async function setPatientParam(
  api: any,
  param: string,
  value: string | number,
  foot?: string,
  save?: string | boolean,
  _silent?: string | boolean,
  apply?: string | boolean
): Promise<string> {
  const designId = useSelectedDesignStore.getState().designId;
  if (!designId || designId < 1) return "No design is open.";
  const patient = useDesignStore.getState().patient || new Patient();
  const compact = param.trim().toLowerCase().replace(/[\s_-]/g, "");
  const resolvedFoot = resolveFoot(foot || "both");
  const applyToInsole = isTruthyFlag(apply);
  if (isAutomaticDesignControlParam(compact)) {
    return setAutomaticParam(param, value, foot);
  }
  if (compact === "save") {
    return savePatientPanel(api);
  }
  if (compact === "syncfrompressure" || compact === "syncpressure" || compact === "هماهنگفشار") {
    return syncPatientFromPressure(api, designId, resolvedFoot);
  }

  if (compact === "name" || compact === "patientname" || compact === "اسمبیمار" || compact === "نامبیمار" || compact === "naam") {
    const updated = Patient.cloneFrom(patient);
    updated.name = String(value);
    updated.patientConditions = patient.patientConditions;
    const ok = await SavePatientOnServer(api, updated, designId);
    if (!ok) return "Could not save the patient.";
    useDesignStore.setState({ patient: updated });
    document.dispatchEvent(new CustomEvent(VOICE_PATIENT_UPDATED_EVENT));
    return `Patient name is ${updated.name}.`;
  }
  if (compact === "clinicalnote" || compact === "note" || compact === "notes" || compact === "نوت" || compact === "یادداشت" || compact === "klinischenoot" || compact === "noot") {
    const incoming = String(value).trim();
    if (!incoming) return "Provide the clinical note text.";
    const existing = (patient.clinicalNote || "").trim();
    const nextNote = existing && !existing.includes(incoming) ? `${existing}\n${incoming}` : incoming;
    const updated = Patient.cloneFrom(patient);
    updated.clinicalNote = nextNote;
    updated.patientConditions = patient.patientConditions;
    useDesignStore.setState({ patient: updated });
    document.dispatchEvent(new CustomEvent(VOICE_PATIENT_UPDATED_EVENT, { detail: { clinicalNote: nextNote } }));
    const ticked = await applyInferredNoteTicks(api, designId, nextNote, resolvedFoot);
    const tickText = ticked.length
      ? ` Also ticked from the note: ${ticked.join(", ")}.`
      : "";
    if (wantsSave(save)) {
      const saved = await savePatientPanel(api);
      return `${saved}${tickText}`;
    }
    return `Clinical note is filled silently but not saved.${tickText} Ask the user if they want to save it. If they say yes or save, call savePatient. Matching Patient Info ticks from the note are already on.`;
  }
  if (compact === "gender" || compact === "جنسیت" || compact === "geslacht") {
    const rawGender = String(value).toLowerCase();
    const gender = rawGender.startsWith("f") || rawGender === "زن" || rawGender === "خانم" || rawGender === "vrouw" || rawGender === "vrouwelijk"
      ? "Female"
      : rawGender.startsWith("m") || rawGender === "مرد" || rawGender === "آقا" || rawGender === "man" || rawGender === "mannelijk"
        ? "Male"
        : "";
    if (!gender) return "Gender must be Male or Female.";
    const updated = Patient.cloneFrom(patient);
    updated.gender = gender;
    updated.patientConditions = patient.patientConditions;
    const ok = await SavePatientOnServer(api, updated, designId);
    if (!ok) return "Could not save the patient.";
    useDesignStore.setState({ patient: updated });
    document.dispatchEvent(new CustomEvent(VOICE_PATIENT_UPDATED_EVENT));
    return `Gender is ${gender}.`;
  }
  if (compact === "age" || compact === "weight" || compact === "height" || compact === "سن" || compact === "وزن" || compact === "قد" || compact === "leeftijd" || compact === "gewicht") {
    const updated = Patient.cloneFrom(patient);
    if (compact === "age" || compact === "سن" || compact === "leeftijd") {
      const numeric = parseVoiceNumber(value);
      if (numeric === undefined) return `${param} must be a number.`;
      updated.age = numeric;
    } else if (compact === "weight" || compact === "وزن" || compact === "gewicht") {
      const stored = patientWeightToKg(value);
      if (stored === undefined) return `${param} must be a number.`;
      updated.weight = stored;
    } else {
      const stored = patientHeightToMm(value);
      if (stored === undefined) return `${param} must be a number.`;
      updated.height = stored;
    }
    updated.patientConditions = patient.patientConditions;
    const ok = await SavePatientOnServer(api, updated, designId);
    if (!ok) return "Could not save the patient.";
    useDesignStore.setState({ patient: updated });
    document.dispatchEvent(new CustomEvent(VOICE_PATIENT_UPDATED_EVENT));
    if (compact === "height" || compact === "قد") {
      const shown = patientHeightDisplay(updated.height);
      return `Height is ${shown.value} ${shown.unit}.`;
    }
    if (compact === "weight" || compact === "وزن" || compact === "gewicht") {
      const shown = patientWeightDisplay(updated.weight);
      return `Weight is ${shown.value} ${shown.unit}.`;
    }
    return `Age is ${updated.age}.`;
  }
  if (compact === "foottype" || compact === "insoletemplate" || compact === "نوعپا" || compact === "voettype") {
    const compactType = String(value).toLowerCase().replace(/[\s_-]/g, "");
    const type = FOOT_TYPE_ALIASES[compactType]
      ?? ((Object.values(InsoleType) as string[]).includes(String(value)) ? value as InsoleType : undefined);
    if (!type) return `Unknown foot type. Use Standard, Plantar Fasciitis, Pes Planus Mild, Pes Planus Moderate, Pes Planus Severe, Pes Planus Very Severe, or Pes Cavus.`;
    const updated = Patient.cloneFrom(patient);
    const conditions = updated.patientConditions;
    if (resolvedFoot !== "right") conditions.insoleTemplateLeft = type;
    if (resolvedFoot !== "left") conditions.insoleTemplateRight = type;
    useDesignStore.setState({ patient: updated, patientConditionUpdated: true });
    await api.patch(`/api/designs/${designId}`, { autoProperties: conditionsPayload(conditions) });
    document.dispatchEvent(new CustomEvent(VOICE_PATIENT_UPDATED_EVENT));
    if (!applyToInsole) {
      return `Foot type recorded on Patient Info as ${type} on ${resolvedFoot}. The insole was NOT changed. Propose it, ask if they agree, and only after they confirm call setPatientParam again with apply=on.`;
    }
    const design = useDesignStore.getState().insoleDesign;
    if (design?.autoProperties) design.autoProperties.insoleTemplate = type;
    const result = await applyAutomaticDesignCondition("footType", resolvedFoot, true, type);
    if (result !== "ok") return result;
    return `Applied ${type} on ${resolvedFoot} in Automatic Design.`;
  }
  if (compact === "archindex") {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "archIndex must be a number.";
    const updated = Patient.cloneFrom(patient);
    const conditions = updated.patientConditions;
    if (resolvedFoot !== "right") conditions.archIndexLeft = numeric;
    if (resolvedFoot !== "left") conditions.archIndexRight = numeric;
    useDesignStore.setState({ patient: updated, patientConditionUpdated: true });
    await api.patch(`/api/designs/${designId}`, { autoProperties: conditionsPayload(conditions) });
    document.dispatchEvent(new CustomEvent(VOICE_PATIENT_UPDATED_EVENT));
    return `Arch index on ${resolvedFoot} is ${numeric}.`;
  }
  if (compact === "metatarsalbar" || compact === "metbar") {
    const on = parseOnOff(String(value).toLowerCase(), String(value));
    if (on === undefined) return "Use on or off for metatarsalBar.";
    if (!applyToInsole) {
      return `Metatarsal bar on ${resolvedFoot} is proposed ${on ? "on" : "off"}. The insole was NOT changed. After they confirm, call setPatientParam again with apply=on.`;
    }
    const result = await applyAutomaticDesignCondition("metatarsalBar", resolvedFoot, on);
    if (result !== "ok") return result;
    return `Automatic Design metatarsal bar on ${resolvedFoot} is ${on ? "on" : "off"}.`;
  }
  const conditionKey = PATIENT_CONDITION_PARAMS.find((key) => key.toLowerCase() === compact) as typeof PATIENT_CONDITION_PARAMS[number] | undefined;
  if (conditionKey) {
    const on = parseOnOff(String(value).toLowerCase(), String(value));
    if (on === undefined) return `Use on or off for ${conditionKey}.`;
    const updated = Patient.cloneFrom(patient);
    const conditions = updated.patientConditions;
    conditions[conditionKey] = mergeConditionSide(conditions[conditionKey], resolvedFoot, on);
    useDesignStore.setState({ patient: updated, patientConditionUpdated: true });
    await api.patch(`/api/designs/${designId}`, { autoProperties: conditionsPayload(conditions) });
    document.dispatchEvent(new CustomEvent(VOICE_PATIENT_UPDATED_EVENT));
    const mapped = conditionKey === "metatarsalgia" || conditionKey === "halluxLimitus" || conditionKey === "sesamoiditis";
    if (mapped && applyToInsole) {
      const result = await applyAutomaticDesignCondition(conditionKey, resolvedFoot, on);
      if (result !== "ok") return result;
      return `${conditionKey} on ${resolvedFoot} is ${on ? "on" : "off"}. Automatic Design applied.`;
    }
    if (mapped) {
      return `${conditionKey} recorded on Patient Info for ${resolvedFoot}. The insole was NOT changed. After they confirm, call setPatientParam again with apply=on.`;
    }
    return `${conditionKey} on ${resolvedFoot} is ${on ? "on" : "off"}.`;
  }
  return `Unknown patient param "${param}". Use ${[...PATIENT_DEMO_PARAMS, "footType", "archIndex", "metatarsalBar", "syncFromPressure", "shoeLast", "offloadingCutout", "cutoutMet1", "limbLengthDiscrepancy", ...PATIENT_CONDITION_PARAMS].join(", ")}.`;
}

function mapScanType(type?: string): "wbct" | "rondom" | "pressure" {
  const normalized = String(type || "pressure").trim().toLowerCase();
  if (normalized.includes("wbct") || normalized.includes("bone") || normalized.includes("استخوان") || normalized.includes("bot")) return "wbct";
  if (normalized.includes("rondom")) return "rondom";
  return "pressure";
}

function formatPressureScanStatus(detail?: Record<string, unknown> | null): string {
  if (!detail) return "Pressure scan did not respond. Call openScan type=pressure first.";
  if (typeof detail.message === "string" && detail.message.trim()) return detail.message;
  if (detail.noCalibration) return "Pressure is not calibrated. Tell the user they must add a pressure calibration first. Do not continue the pressure test.";
  if (detail.noSerial) return "This browser cannot open the serial port. Tell them to use Chrome or Edge.";
  if (!detail.connected || detail.portPicker) {
    return "The pressure device is not connected. Tell the user to plug in the scanner, then click Connect scanner in the panel and choose the USB port. Voice cannot open that Chrome picker. After they connect, call setPressureScan action=status.";
  }
  const mode = detail.mode === "dynamic" ? "dynamic" : "static";
  const capturing = detail.capturing ? " Scanning is in progress." : "";
  const canSave = detail.canSave ? " There is a capture they can save." : "";
  return `Pressure scanner is connected. Mode is ${mode}.${capturing}${canSave} Ask whether they want a static or a dynamic test if they have not chosen yet.`;
}

function scanHardwareUnsupportedVoiceMessage(): string {
  return voiceText({
    en: "Pressure tests and WBCT are not available on this device. Use a computer.",
    nl: "Druktest en WBCT zijn op dit apparaat niet mogelijk. Gebruik een computer.",
    fa: "تست فشار و WBCT روی این دستگاه امکان‌پذیر نیست. از کامپیوتر استفاده کنید.",
    zh: "此设备无法进行压力测试或 WBCT。请使用电脑。",
  });
}

export async function openScanPanel(api: any, type?: string): Promise<string> {
  const mapped = mapScanType(type);
  if ((mapped === "wbct" || mapped === "pressure") && isScanHardwareUnsupportedDevice()) {
    return scanHardwareUnsupportedVoiceMessage();
  }
  if (mapped === "wbct") return openWbctScan();
  if (mapped === "rondom") {
    closeOpenDialogs("scan");
    window.setTimeout(() => {
      document.dispatchEvent(new CustomEvent(VOICE_OPEN_SCAN_EVENT, { detail: { type: "rondom" } }));
    }, 50);
    return "Rondom scan opened. The user must finish the import in that panel.";
  }
  return startPressureScan(api);
}

async function startPressureScan(api: any): Promise<string> {
  if (isScanHardwareUnsupportedDevice()) return scanHardwareUnsupportedVoiceMessage();
  const calibrated = await getPressureCalib(api);
  if (calibrated==null) {
    return "Pressure is not calibrated. Tell the user the pressure device is not calibrated and they must add a calibration first. Do not open the scan and do not continue.";
  }

  const existingWait = waitForDomEvent(VOICE_PRESSURE_SCAN_DONE_EVENT, 400);
  document.dispatchEvent(new CustomEvent(VOICE_PRESSURE_SCAN_EVENT, { detail: { action: "status" } }));
  const existing = await existingWait;
  if (existing?.detail) return formatPressureScanStatus(existing.detail as Record<string, unknown>);

  const readyWait = waitForDomEvent(VOICE_PRESSURE_SCAN_READY_EVENT, 20000);
  closeOpenDialogs("scan");
  window.setTimeout(() => {
    document.dispatchEvent(new CustomEvent(VOICE_OPEN_SCAN_EVENT, { detail: { type: "pressure" } }));
  }, 50);
  const ready = await readyWait;
  if (ready?.detail) return formatPressureScanStatus(ready.detail as Record<string, unknown>);
  return setPressureScan("status");
}

export async function setPressureScan(action?: string, mode?: string): Promise<string> {
  if (isScanHardwareUnsupportedDevice()) return scanHardwareUnsupportedVoiceMessage();
  const mappedAction = String(action || "status").trim().toLowerCase();
  const mappedMode = String(mode || "").trim().toLowerCase();
  if (!["status", "mode", "capture", "start", "stop", "save"].includes(mappedAction)) {
    return "Unknown pressure-scan action. Use status, mode, capture, start, stop, or save.";
  }
  if (mappedAction === "mode" && mappedMode !== "static" && mappedMode !== "dynamic") {
    return "For action=mode, pass mode=static or mode=dynamic.";
  }
  const resultWait = waitForDomEvent(VOICE_PRESSURE_SCAN_DONE_EVENT, 25000);
  document.dispatchEvent(new CustomEvent(VOICE_PRESSURE_SCAN_EVENT, { detail: { action: mappedAction, mode: mappedMode } }));
  const result = await resultWait;
  if (!result) return "Pressure scan did not respond. Call openScan type=pressure first.";
  return formatPressureScanStatus(result.detail as Record<string, unknown>);
}

async function openWbctScan(): Promise<string> {
  if (isScanHardwareUnsupportedDevice()) return scanHardwareUnsupportedVoiceMessage();
  const resultWait = waitForDomEvent(VOICE_WBCT_SCAN_DONE_EVENT, 15000);
  closeOpenDialogs("wbct");
  window.setTimeout(() => {
    document.dispatchEvent(new Event(VOICE_WBCT_SCAN_EVENT));
  }, 50);
  const result = await resultWait;
  const detail = result?.detail as { message?: string; ok?: boolean; installed?: boolean } | undefined;
  if (detail?.message) return detail.message;
  if (!result) {
    return "WBCT did not respond. Tell the user to try Load Scan → WBCT from the toolbar, and install the T-Soles desktop application if it is missing.";
  }
  if (detail?.installed === false || detail?.ok === false) {
    return "The T-Soles desktop application is not installed. Tell the user they must install that software first, then try WBCT again.";
  }
  return "WBCT window is open. Tell the user to load the data manually in that window. Do not say the scan is finished.";
}

async function fetchTemplateItems(api: any): Promise<{ templateId: number; name: string; category?: string }[] | null> {
  const res = await api.get("/api/templates");
  if (!res.ok) return null;
  const body = res.body;
  if (Array.isArray(body)) return body;
  return body?.templates || body?.Templates || [];
}

function openSaveTemplateBecauseEmpty(): string {
  const blocked = blockTemplateAfterPatientSave();
  if (blocked) return blocked;
  openAfterClose(() => {
    document.dispatchEvent(new CustomEvent(VOICE_OPEN_SAVE_TEMPLATE_EVENT));
  });
  return "No templates exist. Save as Template opened so the user can create the first one. Do not open Load Template.";
}

export async function openLoadTemplatePanel(api: any): Promise<string> {
  const blocked = blockTemplateAfterPatientSave();
  if (blocked) return blocked;
  const items = await fetchTemplateItems(api);
  if (items === null) return "Could not load templates.";
  if (!items.length) return openSaveTemplateBecauseEmpty();
  await showLoadTemplatePanel();
  return "Template panel opened. List categories, then ask the user to pick one template.";
}

export async function listTemplates(api: any): Promise<string> {
  const blocked = blockTemplateAfterPatientSave();
  if (blocked) return blocked;
  const items = await fetchTemplateItems(api);
  if (items === null) return "Could not load templates.";
  if (!items.length) return openSaveTemplateBecauseEmpty();
  await showLoadTemplatePanel();
  const categories: Record<string, { templateId: number; name: string }[]> = {};
  for (const item of items) {
    const category = item.category?.trim() || "default";
    if (!categories[category]) categories[category] = [];
    categories[category].push({ templateId: item.templateId, name: item.name });
  }
  return JSON.stringify({ categories, ask: "Ask the user which category and which template to load." });
}

export async function loadTemplateByQuery(api: any, name?: string, templateId?: number | string, category?: string): Promise<string> {
  const blocked = blockTemplateAfterPatientSave();
  if (blocked) return blocked;
  const fetched = await fetchTemplateItems(api);
  if (fetched === null) return "Could not load templates.";
  if (!fetched.length) return openSaveTemplateBecauseEmpty();
  await showLoadTemplatePanel();
  const items = fetched;
  let matches = items;
  if (category?.trim()) {
    const compact = category.trim().toLowerCase();
    matches = matches.filter((item) => (item.category || "default").toLowerCase().includes(compact));
  }
  if (templateId !== undefined && templateId !== "" && Number.isFinite(Number(templateId))) {
    const match = items.find((item) => item.templateId === Number(templateId));
    if (!match) return `No template with id ${templateId}.`;
    const newId = await loadPrivateTemplate(api, match.templateId);
    if (newId <= 0) return `Failed to load template "${match.name}".`;
    return `Loaded template "${match.name}".`;
  }
  if (!name?.trim()) {
    if (matches.length === 0) return category ? `No templates in category "${category}".` : "Provide a template name or id.";
    return `Templates${category ? ` in ${category}` : ""}: ${matches.map((item) => item.name).join(", ")}. Ask the user to select one.`;
  }
  const compact = name.trim().toLowerCase();
  const named = matches.filter((item) => item.name.toLowerCase().includes(compact));
  if (!named.length) return `Template "${name}" not found.`;
  if (named.length > 1) return `Multiple templates match "${name}": ${named.map((item) => `${item.name} (id ${item.templateId})`).join("; ")}.`;
  const newId = await loadPrivateTemplate(api, named[0].templateId);
  if (newId <= 0) return `Failed to load template "${named[0].name}".`;
  return `Loaded template "${named[0].name}".`;
}

export function setAlignMode(value?: string): string {
  if (!hasPressureOrScan()) {
    return voiceText({
      en: "Align is not available. There is no pressure or scan data on this design.",
      nl: "Uitlijnen is niet beschikbaar. Er is geen druk- of scangegevens op dit ontwerp.",
      fa: "هم‌ترازی در دسترس نیست. روی این دیزاین داده فشار یا اسکن نیست.",
      zh: "对齐不可用。这个设计没有压力或扫描数据。",
    });
  }
  if (useViewerStore.getState().coloring) {
    return voiceText({
      en: "Turn off coloring before align.",
      nl: "Zet kleuring uit vóór uitlijnen.",
      fa: "قبل از هم‌ترازی رنگ‌آمیزی را خاموش کن.",
      zh: "对齐前请先关闭着色。",
    });
  }
  const store = useViewerStore.getState();
  const want = value === undefined || value === ""
    ? !store.alignPressure
    : parseOnOff(String(value).toLowerCase(), String(value));
  if (want === undefined) {
    return voiceText({
      en: "Use on or off for align.",
      nl: "Gebruik aan of uit voor uitlijnen.",
      fa: "برای هم‌ترازی روشن یا خاموش بگو.",
      zh: "对齐请说开或关。",
    });
  }
  if (want === store.alignPressure) {
    return want
      ? voiceText({ en: "Align is already on.", nl: "Uitlijnen staat al aan.", fa: "هم‌ترازی از قبل روشن است.", zh: "对齐已经打开。"})
      : voiceText({ en: "Align is already off.", nl: "Uitlijnen staat al uit.", fa: "هم‌ترازی از قبل خاموش است.", zh: "对齐已经关闭。"});
  }
  if (store.alignPressure) {
    store.setLiveMode(store.oldLiveMode);
  } else {
    store.setOldLiveMode(store.liveMode);
    store.setLiveMode(store.alignPressure);
  }
  store.toggleAlignPressure();
  return want
    ? voiceText({
        en: "Align is on. The user can drag the pressure data.",
        nl: "Uitlijnen staat aan. De gebruiker kan de drukdata verslepen.",
        fa: "هم‌ترازی روشن شد. کاربر می‌تواند داده فشار را جابه‌جا کند.",
        zh: "对齐已打开。用户可以拖动压力数据。",
      })
    : voiceText({ en: "Align is off.", nl: "Uitlijnen staat uit.", fa: "هم‌ترازی خاموش شد.", zh: "对齐已关闭。"});
}

export function requestAlignState(): string {
  return JSON.stringify({
    available: hasPressureOrScan() && !useViewerStore.getState().coloring,
    on: Boolean(useViewerStore.getState().alignPressure),
    hasPressure: hasPressureOrScan(),
  });
}

export function openSaveMenu(): string {
  openAfterClose(() => {
    document.dispatchEvent(new CustomEvent(VOICE_OPEN_SAVE_MENU_EVENT));
  });
  const gcodeReady = (useDesignStore.getState().gCodes?.length || 0) > 0;
  return JSON.stringify({
    opened: true,
    exportOptions: [
      { format: "insoles", label: "Export insoles as STL", available: true },
      { format: "gcode", label: "Export G-code file", available: gcodeReady },
    ],
    saveAsTemplate: "Not an export. If the user wants to save as a template, call saveAsTemplate. That opens the Save Template panel, never Load Template.",
    ask: "Ask which export to download, or if they want to save as a template.",
  });
}

export function exportDesign(format: string): string {
  const compact = String(format || "").trim().toLowerCase().replace(/[\s_-]/g, "");
  if (compact === "template" || compact === "savetemplate") {
    return openSaveTemplatePanel();
  }
  const mapped = compact === "stl" || compact === "insole" || compact === "insoles" ? "insoles"
    : compact === "gcode" || compact === "g-code" ? "gcode"
    : compact === "lowpoly" ? "lowpoly"
    : compact === "gcodeobject" || compact === "obj" ? "gcodeObject"
    : "";
  if (!mapped) return "Unknown export format. Use insoles or gcode. To save as a template, call saveAsTemplate.";
  if (mapped === "gcode" && (useDesignStore.getState().gCodes?.length || 0) < 1) {
    return "G-code is not generated yet. Generate G-code with generateGCode first.";
  }
  document.dispatchEvent(new CustomEvent(VOICE_EXPORT_EVENT, { detail: { format: mapped } }));
  return `Downloading ${mapped}.`;
}

export type SaveTemplateVoiceFields = {
  name?: string;
  category?: string;
  insoleColor?: string;
  additionColor?: string;
  save?: boolean;
};

let pendingSaveTemplateFields: SaveTemplateVoiceFields = {};

export function takePendingSaveTemplateFields(): SaveTemplateVoiceFields {
  const next = pendingSaveTemplateFields;
  pendingSaveTemplateFields = {};
  return next;
}

let templateSaveInFlight = false;
let lastVoiceTemplateSave = { name: "", at: 0 };

export function claimVoiceTemplateSave(name?: string): boolean {
  const key = (name || "").trim().toLowerCase();
  const now = Date.now();
  if (templateSaveInFlight) return false;
  if (key && key === lastVoiceTemplateSave.name && now - lastVoiceTemplateSave.at < 5000) return false;
  templateSaveInFlight = true;
  lastVoiceTemplateSave = { name: key, at: now };
  return true;
}

export function releaseVoiceTemplateSave() {
  templateSaveInFlight = false;
}

function wantsSave(save?: string | boolean): boolean {
  if (save === true) return true;
  const value = String(save || "").trim().toLowerCase();
  return value === "on" || value === "true" || value === "yes" || value === "save" || value === "بله" || value === "ذخیره" || value === "ja" || value === "opslaan";
}

function wantsSilent(silent?: string | boolean): boolean {
  if (silent === true) return true;
  const value = String(silent || "").trim().toLowerCase();
  return value === "on" || value === "true" || value === "yes" || value === "silent";
}

export function openSaveTemplatePanel(
  name?: string,
  category?: string,
  insoleColor?: string,
  additionColor?: string,
  save?: string | boolean
): string {
  const blocked = blockTemplateAfterPatientSave();
  if (blocked) return blocked;
  const title = name?.trim() || "";
  const cat = category?.trim() || "";
  const insole = insoleColor?.trim() || "";
  const addition = additionColor?.trim() || "";
  const shouldSave = wantsSave(save);
  pendingSaveTemplateFields = {
    name: title || undefined,
    category: cat || undefined,
    insoleColor: insole || undefined,
    additionColor: addition || undefined,
    save: shouldSave,
  };
  openAfterClose(() => {
    document.dispatchEvent(new CustomEvent(VOICE_OPEN_SAVE_TEMPLATE_EVENT, {
      detail: pendingSaveTemplateFields,
    }));
  });
  const parts = ["Save as Template opened."];
  if (title) parts.push(`Name is "${title}".`);
  if (cat) parts.push(`Category is "${cat}".`);
  if (insole) parts.push(`Insole color is ${insole}.`);
  if (addition) parts.push(`Addition color is ${addition}.`);
  if (shouldSave) parts.push(title ? "Saving the template now." : "Cannot save until a name is set in the panel.");
  else parts.push("Fields are filled in the panel. Call saveAsTemplate again with save=on to save.");
  return parts.join(" ");
}

function isAutomaticDesignControlParam(compact: string): boolean {
  return Boolean(resolveAutomaticDesignControl(compact));
}

function resolveAutomaticDesignControl(param: string): string | undefined {
  const compact = String(param || "").trim().toLowerCase().replace(/[\s_-]/g, "");
  const aliases: Record<string, string> = {
    shoelast: "shoeLast",
    profilecurve: "shoeLast",
    شولست: "shoeLast",
    لست: "shoeLast",
    لستکفش: "shoeLast",
    قالبکفش: "shoeLast",
    offloadingcutout: "offloadingCutout",
    offloadcutout: "offloadingCutout",
    offloading: "offloadingCutout",
    کاتاوت: "offloadingCutout",
    cutoutmet1: "cutoutMet1",
    cutoutmet2: "cutoutMet2",
    cutoutmet3: "cutoutMet3",
    cutoutmet4: "cutoutMet4",
    cutoutmet5: "cutoutMet5",
    met1: "cutoutMet1",
    met2: "cutoutMet2",
    met3: "cutoutMet3",
    met4: "cutoutMet4",
    met5: "cutoutMet5",
    limblengthdiscrepancy: "limbLengthDiscrepancy",
    limblength: "limbLengthDiscrepancy",
    lld: "limbLengthDiscrepancy",
    discrepancy: "limbLengthDiscrepancy",
    اختلافاعضا: "limbLengthDiscrepancy",
    اختلافضول: "limbLengthDiscrepancy",
    shorterlimb: "shorterLimb",
    shorter: "shorterLimb",
    پایکوتاهتر: "shorterLimb",
    discrepancyamount: "discrepancyAmount",
    limblengthamount: "discrepancyAmount",
    thirdquarter: "thirdQuarter",
    "3/4": "thirdQuarter",
    "34insole": "thirdQuarter",
    "3/4insole": "thirdQuarter",
    threequarter: "thirdQuarter",
    threequarterinsole: "thirdQuarter",
    partialinsole: "thirdQuarter",
    driekwart: "thirdQuarter",
    driekwartzool: "thirdQuarter",
    کفیسهچهارم: "thirdQuarter",
    سهچهارم: "thirdQuarter",
    کفیجزئی: "thirdQuarter",
    四分之三: "thirdQuarter",
    四分之三鞋垫: "thirdQuarter",
    "3/4鞋垫": "thirdQuarter",
    // The full insole is the same tick asked for from the other side — off
    // rather than on. The value is worked out from these words below.
    fullinsole: "thirdQuarter",
    full: "thirdQuarter",
    wholeinsole: "thirdQuarter",
    completeinsole: "thirdQuarter",
    volledigezool: "thirdQuarter",
    helezool: "thirdQuarter",
    کفیکامل: "thirdQuarter",
    کامل: "thirdQuarter",
    تمامکفی: "thirdQuarter",
    完整鞋垫: "thirdQuarter",
    全长鞋垫: "thirdQuarter",
  };
  return aliases[compact];
}

export async function setAutomaticParam(
  param: string,
  value?: string | number,
  foot?: string
): Promise<string> {
  const mapped = resolveAutomaticDesignControl(param);
  if (!mapped) {
    return "Unknown Automatic Design param. Use shoeLast, thirdQuarter, offloadingCutout, cutoutMet1-5, limbLengthDiscrepancy, shorterLimb, or discrepancyAmount.";
  }
  // Handled before the panel is opened, unlike every other control here. The
  // tick changes the shape of the insole itself, so the result is on screen in
  // the viewer and getInsoleState reports it — opening Automatic Design over
  // the model to show a checkbox only hides what the user asked to see.
  if (mapped === "thirdQuarter") {
    // "Show me the full insole" is a request to switch this tick OFF, and
    // "3/4 insole" to switch it ON. Both arrive here as the same param, so when
    // the call carries no on/off the words decide. Defaulting to on, as the
    // other controls here do, turned a request for the full insole into its
    // opposite — which is the one mistake this control must not make.
    const spokenAs = String(param || "").trim().toLowerCase().replace(/[\s_-]/g, "");
    const stated = parseOnOff(String(value ?? "").toLowerCase(), String(value ?? ""));
    const asksForFull = /full|whole|complete|volledige|hele|کامل|تمام|完整|全长/.test(spokenAs);
    const asksForPartial = /3\/4|34|quarter|partial|driekwart|سهچهارم|جزئی|四分之三/.test(spokenAs);
    const on = stated !== undefined
      ? stated
      : asksForFull ? false
        : asksForPartial ? true
          : undefined;
    if (on === undefined) return "Ask whether they want the 3/4 insole or the full insole.";
    // Not available on the sandal: the panel hides the tick there, so there is
    // nothing to switch and Partial Length would never appear.
    // Compared as a string, the way setModelCategory below does, rather than
    // pulling the enum in for one check.
    if (useDesignStore.getState().insoleDesign?.category === "Sandal") {
      return "The 3/4 insole is not part of the sandal. Say the model has to be an insole or a high heel for it, and offer to switch the model.";
    }
    // Same two writes the tick itself makes: the flag, and the G-code it
    // invalidates when a finalised design changes shape.
    if (useDesignStore.getState().GcodeReady) useDesignStore.setState({ GcodeReady: false });
    useDesignStore.setState({ thirdQuarter: on });
    // Spoken as returned, so it says only what the user would say: no tool
    // name, no mention of how the app reads a value back.
    return on ? "The insole is 3/4 now." : "The insole is full length now.";
  }

  const resolvedFoot = resolveFoot(foot || "both");

  // Driven straight from here, the way the surface parameters are. Automatic
  // Design used to be opened first so the user could watch the control move,
  // but a panel nobody asked for covers the model they are actually watching,
  // and the tick it shows is not the point — the change to the insole is. The
  // panel opens on request, or at the end of a design proposal, and nowhere
  // else. The panel's own checkboxes catch up from autoParams the next time it
  // is opened, so nothing here depends on it being mounted.

  // One control is not visible from wherever the camera happens to be: the
  // limb length lift lands under a single heel. The rear view is the one that
  // shows it, both insoles side by side with one heel standing higher than the
  // other, so turn the camera there and show both feet, exactly as a voice
  // parameter change does.
  //
  // Framed on the visible meshes rather than asked for as a named view. The
  // named view frames on the whole scene, counting the shoe and the skeleton
  // whether or not they are drawn, which left the camera far enough back that
  // the shallow rear angle read as looking down at the insoles from above. This
  // is one animated move onto what is actually on screen, at the same closeness
  // the Heel Height parameter uses, and it is awaited so the camera has arrived
  // before the heel starts to rise — the angle settling and the shape changing
  // at the same time is what looked like the camera moving twice.
  if (mapped === "limbLengthDiscrepancy" || mapped === "shorterLimb" || mapped === "discrepancyAmount") {
    await ensureBothFeetVisibleForParamChange();
    await fitCameraToVisible({ view: "heel", durationMs: 700, fill: 0.62 });
  }

  if (mapped === "shoeLast") {
    const last = resolveProfileCurveName(String(value ?? ""));
    if (!last) return "Unknown shoe last. Use Generic, Classic, Durea, Sneaker, Men, Women, or Darco. This is not the scene model; insole/sandal/high heel uses setModelCategory.";
    const result = await applyAutomaticDesignCondition("shoeLast", resolvedFoot, true, last);
    if (result !== "ok") return result;
    return `Shoe Last is ${last} on ${resolvedFoot}. This is the Automatic Design last, not the scene model.`;
  }

  if (mapped === "offloadingCutout") {
    const on = parseOnOff(String(value ?? "on").toLowerCase(), String(value ?? "on"));
    if (on === undefined) return "Use on or off for offloadingCutout.";
    const result = await applyAutomaticDesignCondition("offloadingCutout", resolvedFoot, on);
    if (result !== "ok") return result;
    return `Offloading Cutout is ${on ? "on" : "off"}.`;
  }

  if (mapped.startsWith("cutoutMet")) {
    const on = parseOnOff(String(value ?? "on").toLowerCase(), String(value ?? "on"));
    if (on === undefined) return `Use on or off for ${mapped}.`;
    const result = await applyAutomaticDesignCondition(mapped, resolvedFoot, on);
    if (result !== "ok") return result;
    return `${mapped.replace("cutoutMet", "Cutout Met ")} on ${resolvedFoot} is ${on ? "on" : "off"}.`;
  }

  if (mapped === "shorterLimb") {
    const shorter = resolvedFoot === "both"
      ? (String(value || "").toLowerCase().startsWith("l") ? "left" : "right")
      : resolvedFoot;
    const result = await applyAutomaticDesignCondition("shorterLimb", shorter as VoiceFoot, true, shorter);
    if (result !== "ok") return result;
    return `Shorter limb is ${shorter}.`;
  }

  if (mapped === "discrepancyAmount") {
    const numeric = parseVoiceNumber(value ?? "");
    if (numeric === undefined) return "discrepancyAmount must be a number in the current length unit.";
    const result = await applyAutomaticDesignCondition("discrepancyAmount", resolvedFoot, true, String(numeric));
    if (result !== "ok") return result;
    return `Limb length discrepancy amount is ${numeric}.`;
  }

  const on = parseOnOff(String(value ?? "on").toLowerCase(), String(value ?? "on"));
  const numeric = parseVoiceNumber(value ?? "");
  const result = await applyAutomaticDesignCondition(
    "limbLengthDiscrepancy",
    resolvedFoot,
    numeric !== undefined ? true : Boolean(on),
    numeric !== undefined ? String(numeric) : String(value ?? "")
  );
  if (result !== "ok") return result;
  if (numeric !== undefined) return `Limb length discrepancy is on, amount ${numeric}, shorter limb ${resolvedFoot === "both" ? "unchanged" : resolvedFoot}.`;
  return `Limb length discrepancy is ${on ? "on" : "off"}.`;
}

export function setModelCategory(category: string): string {
  const compact = String(category || "").trim().toLowerCase().replace(/[\s_-]/g, "");
  const state = compact === "insole" || compact === "کفی" || compact === "zool" ? "Insole"
    : compact === "sandal" || compact === "صندل" || compact === "sandaal" ? "Sandal"
    : compact === "heel" || compact === "highheel" || compact === "پاشنهبلند" || compact === "hogehak" ? "HighHeel"
    : "";
  if (!state) return "Unknown model. Use insole, sandal, or heel.";
  const label = state;
  document.dispatchEvent(new CustomEvent("updateCategoryState", { detail: { state } }));
  document.dispatchEvent(new CustomEvent(VOICE_SET_CATEGORY_LABEL_EVENT, { detail: { label } }));
  return `Scene model is ${label}.`;
}

export function getDefaultPrinters(): string {
  const { leftPrinterObject, rightPrinterObject } = restoreDefaultPrinters();
  const left = leftPrinterObject?.ip ? (leftPrinterObject.settingName || leftPrinterObject.ip) : null;
  const right = rightPrinterObject?.ip ? (rightPrinterObject.settingName || rightPrinterObject.ip) : null;
  return JSON.stringify({
    left,
    right,
    configured: Boolean(left || right),
    note: left || right
      ? "Default printer is set. G-code can be generated."
      : "No default printer. Open settings so the user can add one.",
  });
}

export function openSettingsPanel(): string {
  closeOpenDialogs("settings");
  document.dispatchEvent(new CustomEvent(VOICE_OPEN_SETTINGS_EVENT));
  return voiceText({
    en: "Settings is open.",
    nl: "Instellingen is open.",
    fa: "تنظیمات باز شد.",
    zh: "已打开设置。",
  });
}

function helperVoiceLabel(helper: { hasRamp?: boolean; cutFront?: boolean } | null | undefined): string {
  if (!helper) return "none";
  if (helper.hasRamp) return "ramp";
  if (helper.cutFront) return "frontTrim";
  return "none";
}

function settingsStateFromStorage() {
  const { leftPrinterObject, rightPrinterObject } = restoreDefaultPrinters();
  const helpers = restorePrintHelpers() || [];
  const leftHelper = helpers.find((h: { side?: string } | null) => h?.side?.toLowerCase() === "left") || helpers[1];
  const rightHelper = helpers.find((h: { side?: string } | null) => h?.side?.toLowerCase() === "right") || helpers[0];
  const printers = [leftPrinterObject, rightPrinterObject].filter(Boolean).map((p) => ({
    name: p?.name,
    ip: p?.ip,
    setting: p?.setting?.name,
  }));
  return {
    printers,
    printerSettings: [],
    defaultLeft: leftPrinterObject?.name || null,
    defaultRight: rightPrinterObject?.name || null,
    helperLeft: helperVoiceLabel(leftHelper),
    helperRight: helperVoiceLabel(rightHelper),
  };
}

function waitForDomEvent(name: string, timeoutMs: number): Promise<CustomEvent | null> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (event: CustomEvent | null) => {
      if (settled) return;
      settled = true;
      document.removeEventListener(name, onEvent as EventListener);
      resolve(event);
    };
    const onEvent = (event: Event) => finish(event as CustomEvent);
    document.addEventListener(name, onEvent as EventListener);
    window.setTimeout(() => finish(null), timeoutMs);
  });
}

async function applySettingsDetail(detail: Record<string, unknown>, silent?: string | boolean, save?: string | boolean): Promise<string> {
  const readyWait = waitForDomEvent(VOICE_SETTINGS_READY_EVENT, 8000);
  if (!wantsSilent(silent)) openSettingsPanel();
  document.dispatchEvent(new Event(VOICE_PING_SETTINGS_EVENT));
  await readyWait;

  const resultWait = waitForDomEvent(VOICE_SETTINGS_RESULT_EVENT, 8000);
  document.dispatchEvent(new CustomEvent(VOICE_SET_SETTINGS_EVENT, { detail }));
  const resultEvent = await resultWait;
  const result = resultEvent?.detail as { ok?: boolean; message?: string } | undefined;
  if (!resultEvent) {
    return "Settings did not confirm the change. Call openSettings, wait, then try addPrinter again with the same name and ip.";
  }
  if (result?.ok === false) return result.message || "Could not apply that settings change.";
  if (wantsSave(save)) {
    await new Promise((resolve) => window.setTimeout(resolve, 200));
    return saveSettingsPanel();
  }
  return result?.message || "Settings updated. Ask if they want to save. If they say yes, call saveSettings.";
}

function asSettingsArgs(raw: unknown): Record<string, unknown> {
  if (raw == null) return {};
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return asSettingsArgs(parsed);
    } catch {
      return { name: raw };
    }
    return { name: raw };
  }
  if (typeof raw !== "object") return { name: String(raw) };
  const bag = raw as Record<string, unknown>;
  const nested = bag.parameters ?? bag.params ?? bag.arguments ?? bag.args;
  return { ...bag, ...(nested && typeof nested === "object" ? nested as Record<string, unknown> : {}) };
}

function pickSettingsArg(bag: Record<string, unknown>, aliases: string[]): string {
  for (const [key, value] of Object.entries(bag)) {
    const compact = key.toLowerCase().replace(/[\s_-]/g, "");
    if (!aliases.includes(compact)) continue;
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
}

function normalizePrinterAddress(value: string): string {
  const original = String(value || "").trim();
  if (!original) return "";
  const dotted = original.replace(/\s+(dot|point|punt|نقطه)\s+/gi, ".");
  const ipv4 = dotted.replace(/\s/g, "").match(/\d{1,3}(?:\.\d{1,3}){3}/);
  if (ipv4) return ipv4[0];
  return original;
}

function splitPrinterNameAndAddress(name: string, address: string): { name: string; ip: string } {
  let title = String(name || "").trim();
  let ip = normalizePrinterAddress(address);
  if (!ip) {
    const fromName = title.match(/\d{1,3}(?:\.\d{1,3}){3}/);
    if (fromName) {
      ip = fromName[0];
      title = title.replace(fromName[0], "").replace(/\s+(at|@|ip|address|آدرس|adres)\s+/i, " ").trim();
    }
  }
  if (!title && ip) title = ip;
  return { name: title, ip };
}

export async function getSettingsState(): Promise<string> {
  return new Promise((resolve) => {
    let settled = false;
    let panelOpen = false;
    const finish = (payload: unknown) => {
      if (settled) return;
      settled = true;
      document.removeEventListener(VOICE_SETTINGS_STATE_EVENT, onState as EventListener);
      document.removeEventListener(VOICE_SETTINGS_STATUS_EVENT, onStatus as EventListener);
      resolve(JSON.stringify(payload));
    };
    const onState = (event: Event) => finish((event as CustomEvent).detail);
    const onStatus = (event: Event) => {
      if ((event as CustomEvent).detail?.open) panelOpen = true;
    };
    document.addEventListener(VOICE_SETTINGS_STATE_EVENT, onState as EventListener);
    document.addEventListener(VOICE_SETTINGS_STATUS_EVENT, onStatus as EventListener);
    document.dispatchEvent(new Event(VOICE_GET_SETTINGS_EVENT));
    document.dispatchEvent(new Event(VOICE_PING_SETTINGS_EVENT));
    window.setTimeout(() => {
      if (!panelOpen) finish(settingsStateFromStorage());
    }, 250);
    window.setTimeout(() => finish(settingsStateFromStorage()), 5000);
  });
}

type SettingsParam =
  | "defaultPrinter"
  | "helper"
  | "name"
  | "ip"
  | "setting"
  | "remove"
  | "checkConnection"
  | "closeConnection"
  | "addPrinter"
  | "save";

const SETTINGS_PARAM_ALIASES: Record<string, SettingsParam> = {
  defaultprinter: "defaultPrinter",
  default: "defaultPrinter",
  printer: "defaultPrinter",
  printerdefault: "defaultPrinter",
  چاپگر: "defaultPrinter",
  پرینتر: "defaultPrinter",
  standaardprinter: "defaultPrinter",
  helper: "helper",
  support: "helper",
  ramp: "helper",
  کمک: "helper",
  هلپر: "helper",
  ondersteuning: "helper",
  name: "name",
  title: "name",
  printertitle: "name",
  printername: "name",
  عنوان: "name",
  naam: "name",
  ip: "ip",
  address: "ip",
  آدرس: "ip",
  adres: "ip",
  setting: "setting",
  printersetting: "setting",
  type: "setting",
  مدل: "setting",
  instelling: "setting",
  remove: "remove",
  delete: "remove",
  حذف: "remove",
  verwijderen: "remove",
  check: "checkConnection",
  checkconnection: "checkConnection",
  connection: "checkConnection",
  اتصال: "checkConnection",
  verbinding: "checkConnection",
  closeconnection: "closeConnection",
  closeconnections: "closeConnection",
  closedialog: "closeConnection",
  dismissconnection: "closeConnection",
  dismiss: "closeConnection",
  بستناتصال: "closeConnection",
  بستنپنجره: "closeConnection",
  sluiten: "closeConnection",
  add: "addPrinter",
  addprinter: "addPrinter",
  newprinter: "addPrinter",
  اضافه: "addPrinter",
  toevoegen: "addPrinter",
  save: "save",
  ok: "save",
  ذخیره: "save",
  opslaan: "save",
  默认打印机: "defaultPrinter",
  打印机: "defaultPrinter",
  辅助: "helper",
  名称: "name",
  地址: "ip",
  型号: "setting",
  删除: "remove",
  连接: "checkConnection",
  关闭连接: "closeConnection",
  添加打印机: "addPrinter",
  保存: "save",
};

const HELPER_ALIASES: Record<string, "none" | "ramp" | "frontTrim"> = {
  none: "none",
  nosupport: "none",
  off: "none",
  بدون: "none",
  geen: "none",
  ramp: "ramp",
  hasramp: "ramp",
  rampsupport: "ramp",
  رمپ: "ramp",
  helling: "ramp",
  fronttrim: "frontTrim",
  cutfront: "frontTrim",
  trim: "frontTrim",
  cut: "frontTrim",
  برش: "frontTrim",
  voorkant: "frontTrim",
  无: "none",
  斜坡: "ramp",
  前修: "frontTrim",
};

export async function addPrinter(
  raw?: unknown,
  ipArg?: string,
  settingArg?: string,
  saveArg?: string | boolean,
  silentArg?: string | boolean
): Promise<string> {
  const bag = typeof raw === "object" && raw != null ? asSettingsArgs(raw) : asSettingsArgs({
    name: raw,
    ip: ipArg,
    setting: settingArg,
    save: saveArg,
    silent: silentArg,
  });
  const parsed = splitPrinterNameAndAddress(
    (() => {
      const title = pickSettingsArg(bag, ["name", "title", "printername", "printertitle", "printer", "عنوان", "naam"]);
      return title.toLowerCase().replace(/[\s_-]/g, "") === "addprinter" ? "" : title;
    })(),
    pickSettingsArg(bag, ["ip", "address", "ipaddress", "printerip", "printeraddress", "host", "url", "آدرس", "adres"]) || String(ipArg || "")
  );
  if (!parsed.name) return "Provide a printer title.";
  if (!parsed.ip) return "Provide the printer address / IP.";
  const setting = pickSettingsArg(bag, ["setting", "printersetting", "type", "model", "مدل", "instelling"]) || String(settingArg || "").trim();
  const save = bag.save != null ? bag.save as string | boolean : saveArg;
  const silent = bag.silent != null ? bag.silent as string | boolean : silentArg;
  return applySettingsDetail({
    addPrinter: { name: parsed.name, ip: parsed.ip, setting: setting || undefined },
  }, silent, save);
}

export async function removePrinter(
  raw?: unknown,
  saveArg?: string | boolean,
  silentArg?: string | boolean
): Promise<string> {
  const bag = typeof raw === "object" && raw != null ? asSettingsArgs(raw) : asSettingsArgs({
    name: raw,
    save: saveArg,
    silent: silentArg,
  });
  let title = pickSettingsArg(bag, ["name", "title", "printer", "printername", "printertitle", "value", "عنوان", "naam"]);
  if (title.toLowerCase().replace(/[\s_-]/g, "") === "removeprinter") title = "";
  const save = bag.save != null ? bag.save as string | boolean : saveArg;
  const silent = bag.silent != null ? bag.silent as string | boolean : silentArg;
  return applySettingsDetail({
    removePrinter: title || true,
  }, silent, save);
}

export async function setSettingsParam(
  param: string,
  value?: string,
  foot?: string,
  save?: string | boolean,
  silent?: string | boolean,
  printer?: string,
  name?: string,
  ip?: string,
  setting?: string
): Promise<string> {
  const compact = String(param || "").trim().toLowerCase().replace(/[\s_-]/g, "");
  const mapped = SETTINGS_PARAM_ALIASES[compact] || SETTINGS_PARAM_ALIASES[param.trim().toLowerCase()];
  if (!mapped) {
    return `Unknown settings field "${param}". Use addPrinter, name, ip, setting, defaultPrinter, helper, remove, or checkConnection.`;
  }
  if (mapped === "save") return saveSettingsPanel();
  if (mapped === "addPrinter") {
    return addPrinter(name || value, ip, setting, save, silent);
  }
  if (mapped === "remove") {
    return removePrinter({ name: value || printer || name, save, silent });
  }
  if (mapped === "closeConnection") {
    return applySettingsDetail({ closeConnection: true }, silent, save);
  }

  const row = String(printer || "").trim();
  const resolvedFoot = resolveFoot(foot || "both");
  const detail: Record<string, unknown> = {};

  if (mapped === "defaultPrinter") {
    const printerName = String(value || name || "").trim();
    if (!printerName) return "Provide a printer name.";
    if (resolvedFoot === "left" || resolvedFoot === "both") detail.defaultLeft = printerName;
    if (resolvedFoot === "right" || resolvedFoot === "both") detail.defaultRight = printerName;
  } else if (mapped === "helper") {
    const helper = HELPER_ALIASES[String(value || "").trim().toLowerCase().replace(/[\s_-]/g, "")];
    if (!helper) return "Helper must be none, ramp, or frontTrim.";
    if (resolvedFoot === "left") {
      detail.helperLeft = helper;
      detail.helperRight = "none";
    } else if (resolvedFoot === "right") {
      detail.helperRight = helper;
      detail.helperLeft = "none";
    } else {
      detail.helperLeft = helper;
      detail.helperRight = helper;
    }
  } else if (mapped === "name") {
    const next = String(value || name || "").trim();
    if (!next) return "Provide the new printer title.";
    detail.printer = row;
    detail.name = next;
  } else if (mapped === "ip") {
    const next = String(value || ip || "").trim();
    if (!next) return "Provide the printer address / IP.";
    detail.printer = row;
    detail.ip = next;
  } else if (mapped === "setting") {
    const next = String(value || setting || "").trim();
    if (!next) return "Provide the printer setting name.";
    detail.printer = row;
    detail.setting = next;
  } else if (mapped === "checkConnection") {
    const target = String(value || row || name || "").trim();
    const compact = target.toLowerCase().replace(/[\s_-]/g, "");
    if (["close", "ok", "okay", "dismiss", "done", "cancel", "بستن", "بسته", "oké", "sluiten"].includes(compact)) {
      return applySettingsDetail({ closeConnection: true }, silent, save);
    }
    if (!target) return "Provide the printer title to check.";
    detail.checkConnection = target;
  }

  return applySettingsDetail(detail, silent, save);
}

export function saveSettingsPanel(): string {
  document.dispatchEvent(new Event(VOICE_SAVE_SETTINGS_EVENT));
  return "Clicked OK. If the connection result dialog was open it is closed. Otherwise settings were saved.";
}

export async function generateGCode(): Promise<string> {
  const resultWait = waitForDomEvent(VOICE_GCODE_RESULT_EVENT, 15000);
  const { leftPrinterObject, rightPrinterObject } = restoreDefaultPrinters();
  if (!leftPrinterObject?.ip && !rightPrinterObject?.ip) {
    openSettingsPanel();
    return "No default printer is defined. Settings opened. Ask the user to add a printer and set left and right defaults, then call generateGCode again. Do not say G-code is generating.";
  }
  openAfterClose(() => {
    document.dispatchEvent(new CustomEvent(VOICE_GENERATE_GCODE_EVENT));
  });
  const resultEvent = await resultWait;
  const result = resultEvent?.detail as { ok?: boolean; started?: boolean; message?: string; openSettings?: boolean } | undefined;
  if (result?.openSettings) openSettingsPanel();
  if (!resultEvent) {
    return "G-code did not start. There is likely a Settings problem (missing default printer or hardness). Call getDefaultPrinters and getSettingsState, open Settings if needed, then generateGCode again. Do not say G-code is generating.";
  }
  return result?.message || (result?.started
    ? "G-code generation started. When it finishes, the Print confirmation panel opens."
    : "G-code did not start. Fix Settings, then call generateGCode again. Do not say G-code is generating.");
}

export function confirmPrint(action?: string, foot?: string): string {
  const compact = String(action || "open").trim().toLowerCase().replace(/[\s_-]/g, "");
  if (["cancel", "close", "no", "dismiss", "لغو", "بستن", "annuleren", "afbreken", "取消"].includes(compact)) {
    document.dispatchEvent(new CustomEvent(VOICE_PRINT_CONFIRM_EVENT, { detail: { action: "cancel" } }));
    return voiceText({
      en: "Print confirmation cancelled.",
      nl: "Printbevestiging geannuleerd.",
      fa: "چاپ لغو شد.",
      zh: "打印已取消。",
    });
  }
  if (["accept", "send", "confirm", "yes", "print", "بفرست", "تایید", "تأیید", "accepteren", "bevestigen", "发送", "确认"].includes(compact)) {
    closeOpenDialogs("print");
    document.dispatchEvent(new CustomEvent(VOICE_PRINT_CONFIRM_EVENT, { detail: { action: "open" } }));
    return voiceText({
      en: "I cannot send the print by voice. Check the consent box and tap Send Print on the panel.",
      nl: "Ik kan de print niet met spraak versturen. Vink het vakje aan en tik op Send Print.",
      fa: "چاپ را با صدا نمی‌فرستم. تیک مسئولیت را بزن و روی Send Print بزن.",
      zh: "无法用语音发送打印。请勾选同意并点击 Send Print。",
    });
  }
  closeOpenDialogs("print");
  document.dispatchEvent(new CustomEvent(VOICE_PRINT_CONFIRM_EVENT, { detail: { action: "open" } }));
  return voiceText({
    en: "Print confirmation is open. Check the box and tap Send Print yourself.",
    nl: "De printbevestiging is open. Vink het vakje aan en tik zelf op Send Print.",
    fa: "پنل تأیید چاپ باز شد. تیک را بزن و خودت Send Print را بزن.",
    zh: "打印确认已打开。请勾选并自行点击 Send Print。",
  });
}

const METRIC_ALIASES: Record<string, MeasurementSystem> = {
  metric: MeasurementSystem.metric,
  metrics: MeasurementSystem.metric,
  mm: MeasurementSystem.metric,
  millimeter: MeasurementSystem.metric,
  millimeters: MeasurementSystem.metric,
  si: MeasurementSystem.metric,
  متریک: MeasurementSystem.metric,
  میلیمتر: MeasurementSystem.metric,
  میلیمترها: MeasurementSystem.metric,
  metrisch: MeasurementSystem.metric,
  公制: MeasurementSystem.metric,
  毫米: MeasurementSystem.metric,
  imperial: MeasurementSystem.imperial,
  inch: MeasurementSystem.imperial,
  inches: MeasurementSystem.imperial,
  us: MeasurementSystem.imperial,
  english: MeasurementSystem.imperial,
  امپریال: MeasurementSystem.imperial,
  اینچ: MeasurementSystem.imperial,
  اینچی: MeasurementSystem.imperial,
  imperiaal: MeasurementSystem.imperial,
  duim: MeasurementSystem.imperial,
  英制: MeasurementSystem.imperial,
  英寸: MeasurementSystem.imperial,
};

export function setAppMeasurement(value?: string): string {
  const store = useViewerStore.getState();
  const compact = String(value || "").trim().toLowerCase().replace(/[\s_-]/g, "");
  let next: MeasurementSystem | undefined;
  if (!compact || compact === "toggle" || compact === "switch") {
    next = store.measurementSystem === MeasurementSystem.imperial
      ? MeasurementSystem.metric
      : MeasurementSystem.imperial;
  } else {
    next = METRIC_ALIASES[compact];
  }
  if (!next) {
    return voiceText({
      en: "Unknown units. Use metric or imperial.",
      nl: "Onbekende eenheid. Gebruik metrisch of imperiaal.",
      fa: "واحد ناشناخته است. متریک یا اینچ بگو.",
      zh: "未知单位。请说公制或英制。",
    });
  }
  store.setMeasurementSystem(next);
  return next === MeasurementSystem.metric
    ? voiceText({
        en: "Units are metric. Speak every insole length in millimeters. setInsoleParam length values are millimeters.",
        nl: "Eenheden zijn metrisch. Spreek elke zoolmaat in millimeter. setInsoleParam-lengtes zijn millimeter.",
        fa: "واحد متریک است. هر طول کفی را به میلی‌متر بگو. مقدارهای طول در setInsoleParam میلی‌متر هستند.",
        zh: "单位是公制。每项鞋垫长度请用毫米说。setInsoleParam 的长度值是毫米。",
      })
    : voiceText({
        en: "Units are imperial. Speak every insole length in inches. setInsoleParam length values are inches.",
        nl: "Eenheden zijn imperiaal. Spreek elke zoolmaat in inch. setInsoleParam-lengtes zijn inch.",
        fa: "واحد اینچ است. هر طول کفی را به اینچ بگو. مقدارهای طول در setInsoleParam اینچ هستند.",
        zh: "单位是英制。每项鞋垫长度请用英寸说。setInsoleParam 的长度值是英寸。",
      });
}

export function getAppMeasurement(): string {
  const system = useViewerStore.getState().measurementSystem;
  return JSON.stringify({
    measurementSystem: system,
    label: system === MeasurementSystem.imperial ? "Imperial" : "Metric",
  });
}

export function setAppTheme(value?: string): string {
  const compact = String(value || "").trim().toLowerCase().replace(/[\s_-]/g, "");
  const isDark = document.body.classList.contains("dark-mode");
  let next: "dark" | "light" | "toggle" = "toggle";
  if (!compact || compact === "toggle" || compact === "switch") next = "toggle";
  else if (["dark", "night", "black", "تاریک", "donker", "深色", "暗色"].includes(compact)) next = "dark";
  else if (["light", "day", "white", "روشن", "licht", "浅色", "亮色"].includes(compact)) next = "light";
  else return voiceText({
    en: "Unknown theme. Use dark or light.",
    nl: "Onbekend thema. Gebruik donker of licht.",
    fa: "تم ناشناخته است. تاریک یا روشن بگو.",
    zh: "未知主题。请说深色或浅色。",
  });
  document.dispatchEvent(new CustomEvent(VOICE_SET_THEME_EVENT, { detail: { value: next } }));
  const result = next === "toggle" ? (isDark ? "light" : "dark") : next;
  return result === "dark"
    ? voiceText({ en: "Theme is dark.", nl: "Thema is donker.", fa: "تم تاریک شد.", zh: "已切换为深色主题。"})
    : voiceText({ en: "Theme is light.", nl: "Thema is licht.", fa: "تم روشن شد.", zh: "已切换为浅色主题。"});
}

export function getAppTheme(): string {
  const isDark = document.body.classList.contains("dark-mode");
  return JSON.stringify({ theme: isDark ? "dark" : "light" });
}

export type TicketVoiceFields = { title?: string; description?: string; send?: boolean };

let pendingTicketFields: TicketVoiceFields = {};

export function peekPendingTicketFields(): TicketVoiceFields {
  return { ...pendingTicketFields };
}

export function clearPendingTicketFields() {
  pendingTicketFields = {};
}

function ticketText(...values: unknown[]): string {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
}

function asTicketArgs(raw: unknown): Record<string, unknown> {
  if (raw == null) return {};
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return asTicketArgs(parsed);
    } catch {
      return {};
    }
  }
  if (typeof raw !== "object") return {};
  const bag = raw as Record<string, unknown>;
  const nested = bag.parameters ?? bag.params ?? bag.arguments;
  return { ...bag, ...(nested && typeof nested === "object" ? nested as Record<string, unknown> : {}) };
}

function pickTicketField(bag: Record<string, unknown>, aliases: string[]): string {
  const param = ticketText(bag.param).toLowerCase().replace(/[\s_-]/g, "");
  if (aliases.includes(param)) return ticketText(bag.value);
  for (const [key, value] of Object.entries(bag)) {
    const compact = key.toLowerCase().replace(/[\s_-]/g, "");
    if (aliases.includes(compact)) {
      const text = ticketText(value);
      if (text) return text;
    }
  }
  return "";
}

function showTicketsPanel(): Promise<void> {
  return new Promise((resolve) => {
    closeOpenDialogs();
    window.setTimeout(() => {
      document.dispatchEvent(new CustomEvent(VOICE_OPEN_TICKETS_EVENT, {
        detail: peekPendingTicketFields(),
      }));
      window.setTimeout(resolve, 100);
    }, 50);
  });
}

function keepTicketsPanel(): Promise<void> {
  return new Promise((resolve) => {
    document.dispatchEvent(new CustomEvent(VOICE_OPEN_TICKETS_EVENT, {
      detail: peekPendingTicketFields(),
    }));
    window.setTimeout(resolve, 40);
  });
}

export function openTicketsPanel(): string {
  void showTicketsPanel();
  return "Tickets dialog is open. If they already gave a title or description, call sendTicket with those fields. Do not tell the user to say send. Do not mention the word send.";
}

export async function sendSupportTicket(raw?: unknown): Promise<string> {
  const bag = asTicketArgs(raw);
  const t = pickTicketField(bag, ["title", "subject", "عنوان"]);
  const d = pickTicketField(bag, ["description", "message", "body", "text", "توضیح", "توضیحات"]);
  const hasNewText = Boolean(t || d);
  if (t) pendingTicketFields.title = t;
  if (d) pendingTicketFields.description = d;

  if (hasNewText) {
    await keepTicketsPanel();
    document.dispatchEvent(new CustomEvent(VOICE_TICKET_UPDATED_EVENT, {
      detail: { title: pendingTicketFields.title || "", description: pendingTicketFields.description || "" },
    }));
    if (pendingTicketFields.title && pendingTicketFields.description) {
      return "Title and description are written in the ticket dialog. Speak only a short confirmation of the text. Do not ask them to say send. Do not mention send or ارسال. If they later ask to send, then call sendTicket with send=on only.";
    }
    if (pendingTicketFields.title) return "Title is written in the ticket dialog. If they have not given a description yet, ask only for the description. Do not mention send.";
    return "Description is written in the ticket dialog. If they have not given a title yet, ask only for the title. Do not mention send.";
  }

  await showTicketsPanel();

  if (wantsSave(bag.send as string | boolean | undefined)) {
    const designId = useSelectedDesignStore.getState().designId;
    if (!designId || designId < 1) return "No design is open. A ticket needs a design.";
    if (!pendingTicketFields.title || !pendingTicketFields.description) {
      return "Ticket dialog is open. Title or description is still empty. Ask only for the missing field. Do not mention send.";
    }
    document.dispatchEvent(new CustomEvent(VOICE_SEND_TICKET_EVENT));
    return "Ticket sent to admin. The dialog will close.";
  }

  return "Tickets dialog is open. If they gave a title or description, call sendTicket to write it. Do not mention send.";
}

export async function undoDesign(): Promise<string> {
  if (!useDesignStore.getState().insoleDesign) {
    return voiceText({ en: "No design is open.", nl: "Er is geen ontwerp open.", fa: "هیچ دیزاینی باز نیست.", zh: "当前没有打开的设计。"});
  }
  if (localDesignStack.length <= 1) {
    return voiceText({ en: "Nothing to undo.", nl: "Niets om ongedaan te maken.", fa: "چیزی برای برگرداندن نیست.", zh: "没有可撤销的步骤。"});
  }
  await undoLastAutosave();
  return voiceText({ en: "Undone.", nl: "Ongedaan gemaakt.", fa: "برگردانده شد.", zh: "已撤销。"});
}

export async function mirrorInsole(side: string): Promise<string> {
  const design = useDesignStore.getState().insoleDesign;
  if (!design) {
    return voiceText({ en: "No design is open.", nl: "Er is geen ontwerp open.", fa: "هیچ دیزاینی باز نیست.", zh: "当前没有打开的设计。"});
  }
  const left = design.insoles.find((insole) => insole.side === "left");
  const right = design.insoles.find((insole) => insole.side === "right");
  if (!left || !right) {
    return voiceText({
      en: "Both feet must be loaded to mirror.",
      nl: "Beide voeten moeten geladen zijn om te spiegelen.",
      fa: "برای آینه کردن هر دو پا باید بار شده باشند.",
      zh: "镜像需要左右脚都已加载。",
    });
  }

  const resolved = resolveFoot(side);
  if (resolved === "both") {
    return voiceText({
      en: "Say left or right. Mirror copies one foot onto the other.",
      nl: "Zeg links of rechts. Spiegelen kopieert de ene voet op de andere.",
      fa: "بگو چپ یا راست. آینه یک پا را روی پای دیگر کپی می‌کند.",
      zh: "请说左或右。镜像会把一只脚复制到另一只。",
    });
  }
  if (resolved === "left") {
    await mirrorToLeft();
    return voiceText({
      en: "Mirrored the right foot onto the left.",
      nl: "De rechter voet is op de linker gespiegeld.",
      fa: "کفی راست روی کفی چپ آینه شد.",
      zh: "已把右脚镜像到左脚。",
    });
  }
  await mirrorToRight();
  return voiceText({
    en: "Mirrored the left foot onto the right.",
    nl: "De linker voet is op de rechter gespiegeld.",
    fa: "کفی چپ روی کفی راست آینه شد.",
    zh: "已把左脚镜像到右脚。",
  });
}

/**
 * Start the interface walkthrough, the same one the Tutorial button runs.
 *
 * Expressed as a request on the tutorial store rather than a direct call, so the
 * voice route and the button end up in exactly one place: Chatbox watches that
 * store and owns starting the narration, including opening a voice session first
 * when there is not one. Calling the walkthrough from here instead would give it
 * a second, subtly different entry point.
 *
 * The walkthrough speaks for itself, sentence by sentence, through the same
 * SPEAK_EXACTLY path the exhibition demonstration uses. So the one thing the
 * agent must not do after this returns is describe what is about to happen: that
 * talks over the walkthrough's own first line.
 */
export function startInterfaceTutorial(): string {
  // The exhibition demonstration and the walkthrough are two different things,
  // and starting either one cancels whatever narration is already playing. So a
  // request that arrives mid-demonstration is refused rather than granted: the
  // visitor asked to be taught the interface, not to have the presentation they
  // are watching cut off and replaced.
  const narration = useTutorialStore.getState().narration;
  if (narration === "tour") {
    return (
      "The guided tour is playing right now, and this is not the tour. Do not start " +
      "a walkthrough over it. Tell the visitor you can walk them through the interface " +
      "once the tour has finished. To control the tour itself, use tourControl."
    );
  }
  if (narration === "tutorial") {
    return "The walkthrough is already running. Say nothing and let it continue.";
  }
  requestTutorial();
  return (
    "The interface walkthrough is starting now and narrates itself. " +
    "Say nothing further and do not describe it."
  );
}
